// PettoBook data access layer — thin wrappers over Supabase queries.
// All functions throw on error. RLS enforces tenant isolation server-side.

import { appFetch, usesNativeHttp } from "./appFetch.js";
import { supabase } from "./supabaseClient";
import { getLocale } from "./i18n.js";

// ── shapes ─────────────────────────────────────────────────────────────────
// All mappers translate snake_case DB rows ↔ in-app camelCase shape used by App.jsx.

export const mapTenant = (r) => r && ({
  id: r.id,
  name: r.name,
  plan: r.plan,
  trialEndsAt: r.trial_ends_at || null,
  trialEndedAt: r.trial_ended_at || null,
  theme: r.theme,
  ownerId: r.owner_id,
  bookingSlug: r.booking_slug,
  address: r.address || "",
  phone: r.phone || "",
  country: r.country || "HK",
  currency: r.currency || "HKD",
  language: r.language || "zh-HK",
  disclaimerSenior: r.disclaimer_senior || "",
  seniorAgeYears: r.senior_age_years != null ? Number(r.senior_age_years) : null,
  termsBooking: r.terms_booking || "",
  termsCat: r.terms_cat || "",
  dailyLimit: r.daily_limit,
  holidays: r.holidays || [],
  businessHours: r.business_hours || {},
  depositPolicy: r.deposit_policy || {},
  cancellationPolicy: r.cancellation_policy || {},
  paymentMethods: r.payment_methods || {},
  quickCollect: r.quick_collect || null,
  // POS: the switch is set by the platform (plan / add-on); settings by the shop.
  posEnabled: !!r.pos_enabled,
  posSettings: (r.pos_settings && typeof r.pos_settings === "object") ? r.pos_settings : {},
  depositMethods: Array.isArray(r.deposit_methods) ? r.deposit_methods : [],
  messagingConfig: r.messaging_config || {},
  subscription: r.subscription || null,
  specialBreeds: r.special_breeds || [],
  logoUrl: r.logo_url || null,
  appIcon: r.app_icon || null,
  bannerUrl: r.banner_url || null,
  about: r.about || "",
  tagline: r.tagline || "",
  website: r.website || "",
  instagram: r.instagram || "",
  facebook: r.facebook || "",
  shopGallery: Array.isArray(r.shop_gallery) ? r.shop_gallery : [],
  staffOrder: Array.isArray(r.staff_order) ? r.staff_order : [],
  pointRedeemables: Array.isArray(r.point_redeemables) ? r.point_redeemables : [],
  stampCard: r.stamp_card || { enabled: false, totalSlots: 10, milestones: [], resetOnFull: true },
  // Per-shop customizable health-report fields. Null = fall back to the
  // built-in REPORT_CHIPS defaults so shops that never touched settings
  // keep the same experience. Descriptor shape:
  //   { key: "skin", label: "皮膚 🩺", type: "chip"|"text",
  //     options: ["正常","乾燥","發炎"], enabled: true, order: 0 }
  reportTemplate: Array.isArray(r.report_template) ? r.report_template : null,
  priceAdjustments: Array.isArray(r.price_adjustments) ? r.price_adjustments : [],
  eventTemplates: Array.isArray(r.event_templates) ? r.event_templates : [],
  reviewsEnabled: r.reviews_enabled !== false,
  healthReportEnabled: r.health_report_enabled !== false,
  hideBranding: !!r.hide_branding,
  walletEnabled: r.wallet_enabled !== false,
  loyaltyEnabled: !!r.loyalty_enabled,
  loyaltyEarnPer: Number(r.loyalty_earn_per) || 100,
  loyaltyRedeemValue: Number(r.loyalty_redeem_value) || 1,
  // Where redeemed vouchers can be applied at checkout: grooming (default),
  // products (retail-heavy shops), or both. Booking + POS gate on this.
  redeemScope: r.redeem_scope || "grooming",
  // WooCommerce integration. The consumer secret is only ever read by
  // the server-side proxy edge fn — never surfaced to the iOS bundle.
  // Owner tab reads these to render the settings form; sensitive fields
  // arrive masked from the DB via RLS-scoped view (defense in depth).
  wcEnabled: !!r.wc_enabled,
  wcStoreUrl: r.wc_store_url || "",
  wcConsumerKey: r.wc_consumer_key || "",
  wcConsumerSecret: r.wc_consumer_secret || "",
  wcCurrency: r.wc_currency || "",
  wcHideOutOfStock: !!r.wc_hide_out_of_stock,
  wcVisibleCategories: Array.isArray(r.wc_visible_categories) ? r.wc_visible_categories.map(Number).filter(Boolean) : [],
  wcHiddenCategories:  Array.isArray(r.wc_hidden_categories)  ? r.wc_hidden_categories.map(Number).filter(Boolean)  : [],
  // Who earns points/stamps: "online" = customer's own online bookings only
  // (default); "all" = every completed visit incl. walk-ins.
  loyaltyEarnScope: r.loyalty_earn_scope || "online",
  // On owner-approved profile merge, also carry over stamp progress + coupons
  // (default false — only wallet + points merge).
  loyaltyMergeRewards: !!r.loyalty_merge_rewards,
  maxPetWeightKg: r.max_pet_weight_kg != null ? Number(r.max_pet_weight_kg) : null,
  blockedBreedIds: Array.isArray(r.blocked_breed_ids) ? r.blocked_breed_ids : [],
  taxConfig: r.tax_config || { enabled: false, rate: 0, label: "稅", mode: "exclusive" },
  stripeConfig: r.stripe_config || { enabled: false, publishable_key: "", account_id: "" },
  // Directory + geo (Phase 1 of the shop-directory plan). region is one
  // of HK_REGIONS ids; latitude/longitude are server-geocoded from the
  // shop's address; directoryListed is the owner's opt-in; boostUntil is
  // set by the Stripe Boost flow (null when not boosted).
  region: r.region || null,
  latitude: r.latitude != null ? Number(r.latitude) : null,
  longitude: r.longitude != null ? Number(r.longitude) : null,
  directoryListed: r.directory_listed === true,
  directoryBooking: r.directory_booking !== false, // default true (online booking on)
  directoryTags: Array.isArray(r.directory_tags) ? r.directory_tags : [],
  // 'in_shop' (fixed location) | 'mobile' (上門 only) | 'both'
  serviceMode: r.service_mode || "in_shop",
  onboarded: r.onboarded === true, // welcome wizard dismissed (persisted, not localStorage)
  // Shop-level pricing taxonomy (size tiers + coatSplit + hugeExtra) defined
  // once and shared by every service. { tiers:[{id,label,minW,maxW}], coatSplit, hugeExtra:{} }
  pricingConfig: r.pricing_config && typeof r.pricing_config === "object" ? r.pricing_config : {},
  boostUntil: r.boost_until || null,
  geocodedAt: r.geocoded_at || null,
  // populated by loader:
  staffIds: [], customerIds: [], services: [], addons: [], inventory: [], cancelPolicy: r.cancellation_policy || {},
  createdAt: r.created_at,
});

export const mapProfile = (r) => r && ({
  id: r.id,
  tenantId: r.tenant_id,
  role: r.role,
  name: r.name,
  phone: r.phone || "",
  countryCode: r.country_code || "+852",
  phone2: r.phone2 || "",
  phone2CountryCode: r.phone2_country_code || "+852",
  address: r.address || "",
  email: r.email || "",
  avatar: r.avatar,
  enabled: r.enabled !== false,
  // Separate "show on customer booking page" flag. enabled=true +
  // customerVisible=false means the owner can still assign this staffer
  // internally but customers won't see them in the groomer picker.
  customerVisible: r.customer_visible !== false,
  jobType: r.job_type,
  settleMode: r.settle_mode || "daily",
  mpfEnabled: r.mpf_enabled === null || r.mpf_enabled === undefined ? null : !!r.mpf_enabled,
  baseSalary: r.base_salary || 0,
  hourlyRate: r.hourly_rate || 0,
  commissionMode: r.commission_mode,
  commissionRate: r.commission_rate || 0,
  commissionEnabled: r.commission_enabled !== false,
  packageCommissionRate: Number(r.package_commission_rate) || 0,
  packageRedemptionCommissionRate: Number(r.package_redemption_commission_rate) || 0,
  googleSyncEnabled: !!r.google_sync_enabled,
  googleEmail: r.google_email || null,
  googleCalendarId: r.google_calendar_id || null,
  commissionTiers: Array.isArray(r.commission_tiers) ? r.commission_tiers : [],
  serviceCommissionRates: (r.service_commission_rates && typeof r.service_commission_rates === "object") ? r.service_commission_rates : {},
  rateChangeLog: Array.isArray(r.rate_change_log) ? r.rate_change_log : [],
  permissions: r.permissions || {},
  marketingOptOut: !!r.marketing_opt_out,
  subscriptionTier: r.subscription_tier || "free",
  subscriptionPeriod: r.subscription_period || null,
  subscriptionExpiresAt: r.subscription_expires_at || null,
  subscriptionSource: r.subscription_source || null,
  subscriptionWillRenew: r.subscription_will_renew !== false,
  joinDate: r.join_date,
  leaveDate: r.leave_date,
  schedulePreference: r.schedule_preference || "no_preference",
  workDays: r.work_days || {},
  // Per-weekday working hours override. Empty / missing keys inherit shop
  // business_hours. Shape: { "0": { open:"10:00", close:"19:00", enabled:true } }.
  staffHours: (r.staff_hours && typeof r.staff_hours === "object") ? r.staff_hours : {},
  // Daily wage log for part-timers — used by payroll alongside hourly rate.
  wageLog: Array.isArray(r.wage_log) ? r.wage_log : [],
  // Payout history — each confirmed salary payment {paidAt, amount, method, from, to, note, at}.
  payoutLog: Array.isArray(r.payout_log) ? r.payout_log : [],
  // Services this staff can provide (empty = can do all, for back-compat).
  canServiceIds: Array.isArray(r.can_service_ids) ? r.can_service_ids : [],
  // Time off / holidays — array of { from, to, note }.
  timeOff: Array.isArray(r.time_off) ? r.time_off : [],
  rating: r.rating,
  reviewCount: r.review_count || 0,
  clockRecords: r.clock_records || [],
  wallet: r.wallet || 0,
  points: r.points || 0,
  walletTx: r.wallet_tx || [],
  tags: r.tags || [],
  referredBy: r.referred_by,
  notes: r.notes || "",
  isSuper: !!r.is_super,
  mustChangePassword: !!r.must_change_password,
  icalToken: r.ical_token || "",
  pets: [],         // populated by loader
  recurringApts: [], // legacy, populated by loader if used
});

export const mapPet = (r) => r && ({
  id: r.id,
  tenantId: r.tenant_id,
  ownerId: r.owner_id,
  name: r.name,
  breed: r.breed || "",
  weight: r.weight,
  weightHistory: r.weight_history || [],
  birthday: r.birthday,
  type: r.type,
  species: r.species || null,
  coatLength: r.coat_length,
  sizeOverride: r.size_override,
  allergies: r.allergies || "無",
  vaccineDate: r.vaccine_date,
  vetNote: r.vet_note || "",
  staffNote: r.staff_note || "",
  behavior: r.behavior || {},
  groomingStyle: r.grooming_style || {},
  medicalFlags: r.medical_flags || {},
  photos: r.photos || [],
  logs: r.logs || [],
  specialPrices: r.special_prices || {},   // { [serviceId]: price } — per-dog legacy pricing
  blockedServiceIds: Array.isArray(r.blocked_service_ids) ? r.blocked_service_ids : [],  // services N.A. for this pet
});

export const mapService = (r) => r && ({
  id: r.id,
  tenantId: r.tenant_id,
  name: r.name,
  kind: r.kind || "service",                    // 'service' (main pick) | 'spa' (optional upsell)
  description: r.description || "",
  basePrice: Number(r.base_price) || 0,
  estimatedMinutes: r.estimated_minutes,
  bufferMinutes: r.buffer_minutes,
  mobileAvailable: r.mobile_available === true,
  mobilePrice: r.mobile_price != null ? Number(r.mobile_price) : null, // static-service 上門 price
  pricingMode: r.pricing_mode,
  matrixPricing: r.matrix_pricing,
  smartPricing: r.smart_pricing || { enabled: false, rules: [] },
  enabled: r.enabled !== false,
  displayOrder: r.display_order || 0,
  customerVisible: r.customer_visible !== false,
  // Each service targets exactly ONE pet type so dog / cat / small-animal
  // prices are independent. Legacy rows store a multi-value pet_types[];
  // we read the first element as the canonical type, falling back to "dog".
  petType: (Array.isArray(r.pet_types) && r.pet_types.length ? r.pet_types[0] : null) || "dog",
  // Multi-pet-type support — primarily for SPA services that apply to
  // BOTH dogs and cats. Regular services stay single-type (the SPA
  // editor is the only UI that exposes multi-select). Read as array;
  // fall back to a one-element array of the legacy single column for
  // pre-migration rows.
  petTypes: Array.isArray(r.pet_types) && r.pet_types.length
    ? r.pet_types
    : [r.pet_type || "dog"],
  // Add-ons that are INCLUDED (free) with this main service — "basic care"
  // baked into the price (e.g. 沖涼全套 includes 剪甲). Used to avoid double-
  // charging when the customer requests an included add-on.
  includedAddonIds: Array.isArray(r.included_addon_ids) ? r.included_addon_ids : [],
});

export const mapAddon = (r) => r && ({
  id: r.id,
  tenantId: r.tenant_id,
  name: r.name,
  price: Number(r.price) || 0,
  pricingMode: r.pricing_mode || "flat",
  variants: Array.isArray(r.variants) ? r.variants : [],
  enabled: r.enabled !== false,
  // Addons can apply to multiple pet types (e.g. "剪指甲" for both
  // dog + cat). Read the new `pet_types` array; fall back to the
  // legacy single `pet_type` column for rows that haven't been
  // migrated yet.
  petTypes: Array.isArray(r.pet_types) && r.pet_types.length
    ? r.pet_types
    : [r.pet_type || "dog"],
  // Extra duration this add-on contributes to a booking's slot block.
  // Null in DB → 0 (legacy "addons don't extend the visit" behaviour).
  estimatedMinutes: r.estimated_minutes == null ? 0 : Number(r.estimated_minutes) || 0,
});

export const mapInventory = (r) => r && ({
  id: r.id,
  tenantId: r.tenant_id,
  name: r.name,
  qty: Number(r.qty) || 0,
  lowAlert: Number(r.low_alert) || 0,
  unit: r.unit || "",
  costPerUnit: Number(r.cost_per_unit) || 0,
});

export const mapAppointment = (r) => r && ({
  id: r.id,
  tenantId: r.tenant_id,
  kind: r.kind || "grooming",
  customerId: r.customer_id,
  petId: r.pet_id,
  staffId: r.staff_id,
  svcId: r.service_id,
  service: r.service_name || "",
  addons: r.addons || [],
  spaIds: Array.isArray(r.spa_ids) ? r.spa_ids : [],
  date: r.date,
  time: r.time,
  status: r.status,
  basePrice: Number(r.base_price) || 0,
  addonPrice: Number(r.addon_price) || 0,
  deposit: Number(r.deposit) || 0,
  depositPaidWallet: Number(r.deposit_paid_wallet) || 0,
  depositPaidPoints: Number(r.deposit_paid_points) || 0,
  depositVoucherId: r.deposit_voucher_id || null,
  depositPaidVoucher: Number(r.deposit_paid_voucher) || 0,
  // Full voucher discount applied to the booking total — written by
  // customer_settle_booking RPC, read by aptRevenue + the owner-side
  // breakdown so the till reflects the voucher savings.
  voucherDiscount: Number(r.voucher_discount) || 0,
  depositClaimedAt: r.deposit_claimed_at || null,
  refundedAt: r.refunded_at || null,
  refundAmount: Number(r.refund_amount) || 0,
  surcharges: r.surcharges || [],
  discount: r.discount || null,
  remainingBalance: r.remaining_balance,
  paid: !!r.paid,
  paymentLog: Array.isArray(r.payment_log) ? r.payment_log : [],
  packageRedeemError: r.package_redeem_error || null,
  source: r.source,
  checkedInAt: r.checked_in_at,
  startedAt: r.started_at,
  completedAt: r.completed_at,
  report: r.report || null,
  signature: r.signature || null,
  rating: r.rating || null,
  ratingComment: r.rating_comment || null,
  cancelReason: r.cancel_reason || null,
  staffNote: r.staff_note || "",
  groomingRequest: r.grooming_request || "",
  priceAdjustLog: r.price_adjust_log || [],
  customDuration: r.custom_duration_minutes,
  customerPackageId: r.customer_package_id || null,
  approvalStatus: r.approval_status || null,
  autoCompletedAt: r.auto_completed_at || null,
  tipAmount: Number(r.tip_amount) || 0,
  groupId: r.group_id || null,
  depositReceiptUrl: r.deposit_receipt_url || null,
  // Before/after photo album with a public share link (separate from
  // report.photos which depends on the health-report feature toggle).
  publicPhotos: Array.isArray(r.public_photos) ? r.public_photos : [],
  publicPhotoToken: r.public_photo_token || null,
  depositConfirmedAt: r.deposit_confirmed_at || null,
  expiresAt: r.expires_at || null,
  label: r.label || null,
  color: r.color || null,
  leaveType: r.leave_type || null,
  isMobile: r.is_mobile === true,
  serviceAddress: r.service_address || "",
  serviceNotes: r.service_notes || "",
  createdBy: r.created_by,
  createdAt: r.created_at,
});

export const mapReview = (r) => r && ({
  id: r.id,
  tenantId: r.tenant_id,
  aptId: r.appointment_id,
  customerId: r.customer_id,
  staffId: r.staff_id,
  rating: r.rating,
  comment: r.comment,
  date: r.created_at?.slice(0, 10),
});

export const mapNotification = (r) => r && ({
  id: r.id,
  tenantId: r.tenant_id,
  userId: r.user_id,
  type: r.type,
  title: r.title,
  body: r.body,
  read: r.read,
  aptId: r.appointment_id,
  // Format created_at in the viewer's local timezone (the raw slice gave UTC,
  // so a HK customer saw "07:09" for a 15:09 booking). Falls back to the
  // raw slice if Date parsing fails.
  time: (() => {
    if (!r.created_at) return "";
    const d = new Date(r.created_at);
    if (isNaN(d.getTime())) return r.created_at.slice(11, 16);
    return String(d.getHours()).padStart(2, "0") + ":" + String(d.getMinutes()).padStart(2, "0");
  })(),
  createdAt: r.created_at,
});

export const mapAnnouncement = (r) => r && ({
  id: r.id, title: r.title, body: r.body, active: r.active, byUser: r.by_user, date: r.created_at?.slice(0, 10),
  audience: r.audience || "both", // 'both' | 'shop' | 'customer'
});

const mapPlatformConfig = (r) => r && ({ config: r.config || {}, autoNotifications: r.auto_notifications || [], updatedAt: r.updated_at });

// ── reads ──────────────────────────────────────────────────────────────────

// Public/tenant reads below take the raw path on native: supabase-js .from()
// never settles on Android once a session has been restored, which left a
// logged-in customer reopening the app stuck on 「載入美容院資料…」.
export async function fetchTenantBySlug(slug) {
  if (_isNative()) return mapTenant((await rawRead(`tenants?booking_slug=eq.${encodeURIComponent(slug)}&select=*&limit=1`))[0] || null);
  const { data, error } = await supabase.from("tenants").select("*").eq("booking_slug", slug).maybeSingle();
  if (error) throw error;
  return mapTenant(data);
}

export async function fetchTenant(tenantId) {
  if (_isNative()) return mapTenant((await rawRead(`tenants?id=eq.${encodeURIComponent(tenantId)}&select=*&limit=1`))[0] || null);
  const { data, error } = await supabase.from("tenants").select("*").eq("id", tenantId).maybeSingle();
  if (error) throw error;
  return mapTenant(data);
}

// Batch variant — used when a customer is a member of multiple shops and
// dataLoader needs all of them at once. Replaces N separate fetchTenant
// calls with a single round-trip. Returns tenants in the same order as the
// input ids (missing tenants are filtered out).
export async function fetchTenantsByIds(ids) {
  const uniq = Array.from(new Set((ids || []).filter(Boolean)));
  if (uniq.length === 0) return [];
  let data;
  if (_isNative()) data = await rawRead(`tenants?id=in.(${uniq.map(encodeURIComponent).join(",")})&select=*`);
  else { const r = await supabase.from("tenants").select("*").in("id", uniq); if (r.error) throw r.error; data = r.data; }
  const mapped = (data || []).map(mapTenant);
  const byId = new Map(mapped.map(t => [t.id, t]));
  return uniq.map(id => byId.get(id)).filter(Boolean);
}

export async function fetchAllTenants() {
  const { data, error } = await supabase.from("tenants").select("*");
  if (error) throw error;
  return (data || []).map(mapTenant);
}

export async function fetchProfile(userId, accessToken) {
  // accessToken is optional but strongly preferred. supabase-js's
  // getSession() / token-refresh can wedge on iOS Capacitor right after
  // ASWebAuth completes (the new refresh_token races with PostgREST
  // validation through the same blocked WKWebView session). The caller
  // already has session.access_token, so the bypass path skips both
  // supabase-js entirely and Capacitor Preferences I/O.
  const url = (import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "");
  const apikey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";
  const bearer = accessToken || apikey;
  // eslint-disable-next-line no-console
  console.log("[PettoBook] fetchProfile via fetch, hasToken=", !!accessToken);
  const resp = await appFetch(`${url}/rest/v1/profiles?id=eq.${encodeURIComponent(userId)}&select=*`, {
    headers: {
      apikey,
      Authorization: `Bearer ${bearer}`,
      Accept: "application/vnd.pgrst.object+json",
    },
  });
  // eslint-disable-next-line no-console
  console.log("[PettoBook] fetchProfile resp status=", resp.status);
  if (resp.status === 406 || resp.status === 404) return null;
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`fetchProfile ${resp.status}: ${text.slice(0, 200)}`);
  }
  const row = await resp.json();
  return mapProfile(row);
}

// Returns every profile that belongs to this tenant, including multi-shop
// customers whose primary profile.tenant_id points at a different shop but
// who hold a customer_memberships row for THIS tenant. Without the second
// query, the owner's CRM is blind to customers who originally registered
// at another shop and later booked here.
export async function fetchTenantProfiles(tenantId) {
  // One read, two transports: raw fetch on native (supabase-js `.from()` never
  // resolves there — see fetchTenantAppointments), supabase-js on web. The
  // merge logic below is shared, so the two platforms can't drift apart.
  const getRows = async (path, build) => {
    if (_isNative()) return (await rawRead(path)) || [];
    const { data, error } = await build();
    if (error) throw error;
    return data || [];
  };
  const tid = encodeURIComponent(tenantId);
  const memberCols = "user_id,display_name,phone,wallet,points,wallet_tx,tags,joined_at";
  const [pRows, mRows] = await Promise.all([
    // 1. Direct primary-tenant profiles (staff, owner, legacy customers).
    getRows(`profiles?tenant_id=eq.${tid}&select=*`,
      () => supabase.from("profiles").select("*").eq("tenant_id", tenantId)),
    // 2. Membership-linked customers (their profile.tenant_id may be elsewhere).
    getRows(`customer_memberships?tenant_id=eq.${tid}&select=${memberCols}`,
      () => supabase.from("customer_memberships").select(memberCols.replace(/,/g, ", ")).eq("tenant_id", tenantId)),
  ]);
  const out = (pRows || []).map(mapProfile);
  const memByUser = Object.fromEntries((mRows || []).map(r => [r.user_id, r]));
  // Wallet + points are PER-SHOP (customer_memberships) and are the SOURCE OF
  // TRUTH — profiles.wallet/points are deprecated/zeroed. Overlay the membership
  // values onto every primary-tenant customer too; without this they'd show a
  // stale/zero balance (this is the read-split bug that made the wallet + points
  // disagree between views).
  for (const mp of out) {
    const m = memByUser[mp.id];
    if (m) {
      mp.wallet = Number(m.wallet) || 0;
      mp.points = Number(m.points) || 0;
      mp.walletTx = Array.isArray(m.wallet_tx) ? m.wallet_tx : [];
      if (Array.isArray(m.tags)) mp.tags = m.tags;
    }
  }
  const known = new Set(out.map(p => p.id));
  const extraIds = (mRows || []).map(r => r.user_id).filter(id => !known.has(id));
  if (extraIds.length > 0) {
    // Fetch the underlying profile rows in one query. RLS now allows it
    // (profiles_select_strict's "staff via memberships" branch).
    const extraRows = await getRows(
      `profiles?id=in.(${encodeURIComponent(extraIds.join(","))})&select=*`,
      () => supabase.from("profiles").select("*").in("id", extraIds));
    // Overlay membership-scoped fields (wallet/points/tags/phone) so the CRM
    // shows THIS tenant's relationship, not the customer's primary-shop one.
    for (const row of extraRows || []) {
      const mp = mapProfile(row);
      const m = memByUser[row.id];
      if (m) {
        mp.tenantId = tenantId; // scope the in-memory record to THIS shop
        // In THIS shop they're a customer, whatever they are at home (an owner
        // of another shop booking here). Keeping role "owner"/"staff" made
        // them a bookable staff column on this shop's calendar.
        if (mp.role !== "customer") { mp.homeRole = mp.role; mp.role = "customer"; }
        mp.name = m.display_name || mp.name;
        mp.phone = m.phone || mp.phone;
        mp.wallet = Number(m.wallet) || 0;
        mp.points = Number(m.points) || 0;
        mp.walletTx = Array.isArray(m.wallet_tx) ? m.wallet_tx : [];
        mp.tags = Array.isArray(m.tags) ? m.tags : [];
      }
      out.push(mp);
    }
  }
  // Overlay "has the native customer app installed" flag so the CRM can
  // show a small badge next to customers who're on the app. RLS policy
  // "shop can read customer installs" lets the shop staff see this.
  try {
    const ids = out.map(p => p.id);
    if (ids.length) {
      const installs = await getRows(
        `app_installs?bundle_id=eq.com.pettobook.customer&user_id=in.(${encodeURIComponent(ids.join(","))})&select=user_id,last_seen_at`,
        () => supabase.from("app_installs").select("user_id, last_seen_at")
          .eq("bundle_id", "com.pettobook.customer").in("user_id", ids));
      const byUser = Object.fromEntries((installs || []).map(r => [r.user_id, r]));
      for (const p of out) {
        const rec = byUser[p.id];
        if (rec) { p.appInstalled = true; p.appLastSeen = rec.last_seen_at; }
      }
    }
  } catch { /* non-fatal */ }
  return out;
}

export async function fetchTenantServices(tenantId) {
  if (_isNative()) return (await rawRead(`services?tenant_id=eq.${encodeURIComponent(tenantId)}&select=*&order=display_order.asc`)).map(mapService);
  const { data, error } = await supabase.from("services").select("*").eq("tenant_id", tenantId).order("display_order", { ascending: true });
  if (error) throw error;
  return (data || []).map(mapService);
}

export async function fetchTenantAddons(tenantId) {
  if (_isNative()) return (await rawRead(`addons?tenant_id=eq.${encodeURIComponent(tenantId)}&select=*&order=display_order.asc`)).map(mapAddon);
  const { data, error } = await supabase.from("addons").select("*").eq("tenant_id", tenantId).order("display_order", { ascending: true });
  if (error) throw error;
  return (data || []).map(mapAddon);
}

export async function fetchTenantInventory(tenantId) {
  const { data, error } = await supabase.from("inventory").select("*").eq("tenant_id", tenantId);
  if (error) throw error;
  return (data || []).map(mapInventory);
}

export async function fetchTenantPets(tenantId) {
  // Native raw path — same reason as fetchTenantAppointments below.
  if (_isNative()) {
    const rows = await rawRead(`pets?tenant_id=eq.${encodeURIComponent(tenantId)}&deleted_at=is.null&select=*`);
    return (rows || []).map(mapPet);
  }
  const { data, error } = await supabase.from("pets").select("*").eq("tenant_id", tenantId).is("deleted_at", null);
  if (error) throw error;
  return (data || []).map(mapPet);
}

export async function fetchCustomerPets(ownerId) {
  if (_isNative()) {
    const rows = await rawRead(`pets?owner_id=eq.${encodeURIComponent(ownerId)}&deleted_at=is.null&select=*`);
    return (rows || []).map(mapPet);
  }
  const { data, error } = await supabase.from("pets").select("*").eq("owner_id", ownerId).is("deleted_at", null);
  if (error) throw error;
  return (data || []).map(mapPet);
}

export async function fetchTenantAppointments(tenantId, { from, to } = {}) {
  // NATIVE reads go through the hardened raw fetch. supabase-js `.from()` does
  // not resolve on Android — measured on a device, every call hung — and THIS
  // function is what the resume refetch and the 45s poll are built on, so on
  // native both silently never completed and the app stayed stale after a
  // reopen until something else forced a full reload.
  if (_isNative()) {
    const parts = [`tenant_id=eq.${encodeURIComponent(tenantId)}`, "deleted_at=is.null", "select=*", "order=date.desc,time.asc"];
    if (from) parts.push(`date=gte.${encodeURIComponent(from)}`);
    if (to) parts.push(`date=lte.${encodeURIComponent(to)}`);
    const rows = await rawRead("appointments?" + parts.join("&"));
    return (rows || []).map(mapAppointment);
  }
  let q = supabase.from("appointments").select("*").eq("tenant_id", tenantId).is("deleted_at", null);
  if (from) q = q.gte("date", from);
  if (to) q = q.lte("date", to);
  const { data, error } = await q.order("date", { ascending: false }).order("time");
  if (error) throw error;
  return (data || []).map(mapAppointment);
}

export async function fetchCustomerAppointments(customerId, { from } = {}) {
  // Native raw path — same reason as fetchTenantAppointments above.
  if (_isNative()) {
    const parts = [`customer_id=eq.${encodeURIComponent(customerId)}`, "deleted_at=is.null", "select=*", "order=date.desc", "limit=100"];
    if (from) parts.push(`date=gte.${encodeURIComponent(from)}`);
    const rows = await rawRead("appointments?" + parts.join("&"));
    return (rows || []).map(mapAppointment);
  }
  let q = supabase.from("appointments").select("*").eq("customer_id", customerId).is("deleted_at", null);
  if (from) q = q.gte("date", from);
  const { data, error } = await q.order("date", { ascending: false }).limit(100);
  if (error) throw error;
  return (data || []).map(mapAppointment);
}

export async function fetchTenantReviews(tenantId) {
  const { data, error } = await supabase.from("reviews").select("*").eq("tenant_id", tenantId);
  if (error) throw error;
  return (data || []).map(mapReview);
}

// Owner grants a voucher to one or many customers. RPC scrubs auth (must
// be owner/staff of the tenant), appends to customer_memberships.vouchers,
// AND drops a notifications row so the customer sees it in the app + push.
// Voucher shape: {type, label, amount, refId?, expiresAt?, note?}
export async function ownerGrantVoucher({ userIds, tenantId, voucher }) {
  const { data, error } = await supabase.rpc("owner_grant_voucher", {
    p_user_ids: userIds,
    p_tenant_id: tenantId,
    p_voucher: voucher,
  });
  if (error) throw error;
  return Number(data) || 0;
}

// Owner broadcasts a notification to one or many customers. Optional
// voucher payload is attached (customer app can then redeem in one tap).
export async function ownerBroadcastNotification({ userIds, tenantId, title, body, type, aptId }) {
  const { data, error } = await supabase.rpc("owner_broadcast_notification", {
    p_user_ids: userIds,
    p_tenant_id: tenantId,
    p_title: title || "",
    p_body: body || "",
    p_type: type || "announcement",
    p_apt_id: aptId || null,
  });
  if (error) throw error;
  return Number(data) || 0;
}

// Shop news CRUD — owner-only via RLS.
export async function fetchShopNews(tenantId) {
  const { data, error } = await supabase.from("shop_news")
    .select("*").eq("tenant_id", tenantId).order("published_at", { ascending: false });
  if (error) throw error;
  return data || [];
}
export async function upsertShopNews(row) {
  const payload = {
    id: row.id, tenant_id: row.tenantId, title: row.title, body: row.body ?? null,
    image_url: row.imageUrl ?? null, cta_label: row.ctaLabel ?? null, cta_route: row.ctaRoute ?? null,
    voucher_ref: row.voucherRef ?? null, pinned: !!row.pinned, active: row.active !== false,
    expires_at: row.expiresAt ?? null,
  };
  if (!payload.id) delete payload.id;
  const { data, error } = await supabase.from("shop_news").upsert(payload).select().maybeSingle();
  if (error) throw error;
  return data;
}
export async function deleteShopNews(id) {
  const { error } = await supabase.from("shop_news").delete().eq("id", id);
  if (error) throw error;
}

export async function fetchMyNotifications(userId) {
  const { data, error } = await supabase.from("notifications").select("*").eq("user_id", userId).order("created_at", { ascending: false }).limit(50);
  if (error) throw error;
  return (data || []).map(mapNotification);
}

export async function fetchAnnouncements() {
  const { data, error } = await supabase.from("announcements").select("*").eq("active", true).order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(mapAnnouncement);
}

export async function fetchPlatformConfig() {
  const { data, error } = await supabase.from("platform_config").select("*").eq("id", "singleton").maybeSingle();
  if (error) throw error;
  return mapPlatformConfig(data);
}

export async function insertAnnouncement(ann) {
  const row = stripUndef({
    id: ann.id,
    title: ann.title,
    body: ann.body,
    active: ann.active !== false,
    by_user: ann.byUser,
    audience: ann.audience || "both",
  });
  // Native: see pushNotification — supabase-js hangs on Android, and the
  // admin portal shell posts announcements from a WebView too.
  if (_isNative()) return await rawUpsert("announcements", row);
  const { data, error } = await supabase.from("announcements").insert(row).select("*").maybeSingle();
  if (error) throw error;
  return data;
}

export async function deleteAnnouncement(id) {
  if (_isNative()) {
    await rawWrite("DELETE", `announcements?id=eq.${encodeURIComponent(id)}`, null);
    return;
  }
  const { error } = await supabase.from("announcements").delete().eq("id", id);
  if (error) throw error;
}

export async function upsertPlatformConfig({ config, autoNotifications }) {
  const row = {
    id: "singleton",
    config: config || {},
    auto_notifications: autoNotifications || [],
    updated_at: new Date().toISOString(),
  };
  // Use the reliable raw-fetch write path (same as appointments/services).
  // supabase-js .upsert() can wedge/abort after the tab wakes from background,
  // which silently dropped every platform-config save (settings never
  // persisted). rawUpsert posts with the live access token + merge-duplicates.
  const saved = await rawUpsert("platform_config", row);
  return mapPlatformConfig(saved);
}

export async function createStaff(payload) {
  // Bound the call to 15s so a stuck network / dead edge function surfaces a
  // clear error to the owner instead of a forever-spinning "處理中" button.
  const TIMEOUT_MS = 15000;
  const invocation = supabase.functions.invoke("create-staff", { body: payload });
  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error("請求逾時，請稍後再試（15 秒無回應）")), TIMEOUT_MS)
  );
  const { data, error } = await Promise.race([invocation, timeout]);
  if (error) {
    // Surface the function's own error body if Supabase wrapped one in.
    let detail = "";
    try {
      detail = await error.context?.json?.().then((j) => {
        if (j?.code === "staff_limit") {
          const zh = (getLocale() || "zh-HK").startsWith("zh");
          return zh ? `已達方案員工上限（${j.max} 位，包括店主）。請升級方案，或先停用一位員工。`
                    : `Your plan allows ${j.max} staff (including the owner). Upgrade, or disable a staff member first.`;
        }
        return j?.error || j?.detail || "";
      }) || "";
    } catch {}
    throw new Error(detail || error.message || "create-staff failed");
  }
  if (data?.error) throw new Error(data.error);
  if (!data?.userId) throw new Error("create-staff 沒有回傳 userId");
  return data;
}

// ── writes ─────────────────────────────────────────────────────────────────

const stripUndef = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined));

// Native raw-fetch helpers — bypass the post-OAuth supabase-js wedge.
// We grab the access token from the supabase auth state synchronously
// (it's kept in memory after setSession so no async storage hop on the
// hot write path).
// Capacitor native only. NOTE: this gate routes the ENTIRE data path (incl.
// the initial dataLoader sync) onto raw-fetch. It was briefly widened to iOS
// standalone PWA to fix silent write failures there, but that pushed PWA sync
// reads through raw-fetch and caused a 25s sync timeout — reverted. Any future
// PWA write-reliability fix must be scoped to WRITES only, never the read/sync
// path. (See the reschedule false-success probe fix in App.jsx, which is the
// safe, platform-agnostic half of that effort.)
const _isNative = () => typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
const _restBase = () => (import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "") + "/rest/v1";
const _apikey = () => import.meta.env.VITE_SUPABASE_ANON_KEY || "";
// In-memory bearer cache — supabase.auth.getSession() itself can wedge
// on native after OAuth, so we read from window.__petosAccessToken
// (populated by auth.js + the boot path) instead of asking supabase-js.
// Falls back to anon key if not set.
function _bearer() {
  try { return window.__petosAccessToken || _apikey(); } catch { return _apikey(); }
}
// Phase 2.5 — if the in-memory token is missing but we DO have a session
// in storage (e.g. cross-tab login, boot race, OAuth redirect just
// happened), populate the cache before letting the raw write fall back
// to the anon key. Without this guard, RLS silently rejects the write
// and the user sees a generic "permission denied" error instead of
// understanding it was just a stale-token boot ordering issue. Cheap
// best-effort — never throws.
let _bearerHydrating = null;
async function _ensureBearer() {
  try {
    if (typeof window !== "undefined" && window.__petosAccessToken) return;
    if (_bearerHydrating) { await _bearerHydrating; return; }
    _bearerHydrating = (async () => {
      try {
        // HARD TIMEOUT: supabase-js getSession() can HANG forever on an aged
        // iOS WebView after the app is reopened. Without this cap every native
        // read/write awaiting a bearer hung too — the inbox stopped refreshing
        // and only a force-quit recovered it. Cap it and fall through; the
        // caller still has the cached token / will get a 401 → refresh path.
        const withCap = Promise.race([
          supabase.auth.getSession(),
          new Promise((resolve) => setTimeout(() => resolve({ data: null, _timeout: true }), 4000)),
        ]);
        const { data } = await withCap;
        if (data?.session?.access_token) {
          try { window.__petosAccessToken = data.session.access_token; } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
    })();
    await _bearerHydrating;
    _bearerHydrating = null;
  } catch { _bearerHydrating = null; }
}
// Detect a server response that indicates the access token is no longer
// valid (expired / revoked / signed-by-different-key) and notify the app
// so it can drop back to the login screen. Without this, the UI sat on
// the stale profile and every action silently 401'd.
function _maybeFireSessionExpired(status, bodyText) {
  if (status !== 401 && status !== 403) return;
  const msg = String(bodyText || "");
  // PostgREST surfaces these for invalid JWT; we only fire on token-related
  // codes (not generic RLS denials, which are also 401/403 but legitimate).
  if (!/JWT|jwt|token|expired|InvalidGrant|PGRST301|PGRST302/.test(msg)) return;
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("petos:session-expired", { detail: { reason: status + ":" + msg.slice(0,80) } }));
    }
  } catch { /* ignore */ }
}
// ── "The socket is probably dead" window ───────────────────────────────────
//
// Right after the app comes back from the background, the first request out
// often goes onto a socket the OS kept in the pool but that is actually dead.
// It COMMITS server-side and the response never returns. Normally we can't
// know that until a 12.5s timeout expires — which is why the owner's first
// save after reopening took >10s even though other devices already showed the
// row saved.
//
// On resume we mark the connection suspect for a minute. During that window a
// stalled request is given ~3.5s instead of 12.5s before we warm a fresh
// socket and retry, which turns that >10s first save into ~5s. The FIRST
// successful raw call clears the suspicion, so only the genuinely-first
// request after a reopen ever pays the short timeout.
//
// RPCs are excluded: a retry of an upsert is idempotent (same id, same row),
// but a retry of, say, record_payment_atomic is a second charge. Those keep
// the long, patient timeout.
let _socketSuspectUntil = 0;
export function markSocketSuspect(ms = 60000) { _socketSuspectUntil = Date.now() + ms; }
const _firstAttemptTimeout = (path) => {
  // Android sends through its native HTTP stack (appFetch), which opens a clean
  // connection per request — there is no dead pooled socket to cut short, and
  // a 3.5s cutoff on a slow cold link would just send the write twice.
  if (usesNativeHttp()) return 12500;
  if (Date.now() >= _socketSuspectUntil) return 12500;
  if (String(path || "").startsWith("rpc/")) return 12500;
  return 3500;
};
// Deliberately NO "clear on first success". The failure this exists for is a
// WRITE hanging on a dead socket right after reopen even though reads on the
// same connection just succeeded — the resume refetch would otherwise clear
// the suspicion a second before the owner's save needs it. The window simply
// expires on time. Cost of being wrong: an idempotent upsert is retried once.

async function rawWrite(method, path, body, prefer, _retried, _netRetried) {
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), 12000);
  try {
    // Phase 2.5 — guard against the stale-token boot race. If the in-
    // memory bearer cache is empty, pull from storage before sending.
    await _ensureBearer();
    const headers = {
      apikey: _apikey(),
      Authorization: `Bearer ${_bearer()}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    if (prefer) headers.Prefer = prefer;
    // ⚠️ RACE the fetch against a real timer — abort() alone is FICTION on a
    // half-dead WKWebView socket (documented repeatedly in this codebase): the
    // aborted fetch never rejects, so the 12s "timeout" above never fired and a
    // pets/appointments write could hang FOREVER, stalling every write queued
    // behind it in syncStateDiff. 2026-08-31 owner repro: walk-in with a new
    // customer → customer landed (rawInvoke) but the pet POST hung → the
    // appointment POST never sent → the optimistic row was wiped by the next
    // refetch ("appointment created and disappeared, pet not created").
    // On the FIRST timeout we warm a fresh socket (auth/v1/health, no-store)
    // and retry ONCE — the warm-up forces a new TCP/TLS setup, which is what
    // revives a black-holed HTTP/2 pool after reopen. NOTE: this deliberately
    // re-applies ONLY the raced-fetch part of the reverted 2026-08-10 work,
    // not the _withTimeout wrappers or the watchdog timing change.
    let resp;
    try {
      resp = await Promise.race([
        appFetch(`${_restBase()}/${path}`, {
          method,
          signal: controller.signal,
          headers,
          body: body ? JSON.stringify(body) : undefined,
          timeoutMs: _netRetried ? 12500 : _firstAttemptTimeout(path),
        }),
        new Promise((_, rej) => setTimeout(() => rej(Object.assign(new Error("rawWrite net-timeout: " + path.slice(0, 60)), { code: "net-timeout" })), _netRetried ? 12500 : _firstAttemptTimeout(path))),
      ]);
    } catch (e) {
      if (e?.code === "net-timeout" && !_netRetried) {
        try { controller.abort(); } catch { /* best effort */ }
        // Cheap unauthenticated ping that opens a FRESH connection.
        try {
          await Promise.race([
            appFetch(`${(import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "")}/auth/v1/health`, { cache: "no-store", headers: { apikey: _apikey() }, timeoutMs: 3000 }),
            new Promise((res) => setTimeout(res, 3000)),
          ]);
        } catch { /* ignore — retry anyway */ }
        return await rawWrite(method, path, body, prefer, _retried, true);
      }
      throw e;
    }
    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      // Token expired/invalid → the cached in-memory bearer went stale (native
      // sessions age out ~1h). Refresh ONCE and retry before giving up, so a
      // write can't silently 401 while edge-function calls (which auto-refresh)
      // still succeed — the cause of "customer saved but appointment vanished".
      if (!_retried && (resp.status === 401 || resp.status === 403) &&
          /JWT|jwt|token|expired|PGRST301|PGRST302|InvalidGrant/.test(text)) {
        let refreshed = false;
        try {
          const mod = await import("./auth.js");
          const r = await mod.ensureFreshSession();
          refreshed = !!(r && r.ok && (typeof window !== "undefined" && window.__petosAccessToken));
        } catch { /* fall through to session-expired below */ }
        if (refreshed) return await rawWrite(method, path, body, prefer, true);
      }
      _maybeFireSessionExpired(resp.status, text);
      const err = new Error(`rawWrite ${resp.status}: ${text.slice(0, 200)}`);
      err.status = resp.status;
      throw err;
    }
    const ct = resp.headers.get("content-type") || "";
    if (!ct.includes("application/json")) return null;
    const text = await resp.text();
    if (!text) return null;
    return JSON.parse(text);
  } finally {
    clearTimeout(tid);
  }
}
async function rawUpsert(table, row) {
  const arr = await rawWrite("POST", table, [row], "resolution=merge-duplicates,return=representation");
  return (arr && arr[0]) || null;
}
// Hardened edge-function call for NATIVE. supabase-js's functions.invoke() wedges
// /hangs on an aged iOS WebView session (same failure that pushed reads onto
// rawRead and writes onto rawWrite) — so a WhatsApp send would roll back and the
// user had to force-quit. This POSTs straight to /functions/v1/<fn> with a 20s
// abort + refresh-once-on-401, so an aged session self-heals. Returns parsed JSON.
async function rawInvoke(fn, body, _retried) {
  const base = (import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "");
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), 20000);
  try {
    await _ensureBearer();
    const resp = await appFetch(`${base}/functions/v1/${fn}`, {
      method: "POST",
      signal: controller.signal,
      headers: { apikey: _apikey(), Authorization: `Bearer ${_bearer()}`, "Content-Type": "application/json" },
      body: JSON.stringify(body || {}),
      timeoutMs: 20000,
    });
    const text = await resp.text().catch(() => "");
    if (!resp.ok) {
      // A 401 from an authenticated edge fn is ALWAYS a token problem — the
      // only credential we send is the bearer. Refresh + retry once regardless
      // of the body text: send-whatsapp returns a bare {"error":"unauthorized"}
      // (no "JWT"/"token" keyword) when getUser() rejects an expired token on
      // resume, so the old keyword-gated trigger never fired and the 訊息 send
      // failed right after reopening the app. Retry is safe — the fn rejects on
      // auth BEFORE sending, so it can't double-send. 403 stays keyword-gated
      // (genuine "forbidden" won't be fixed by a refresh).
      const authRetry = resp.status === 401 ||
        (resp.status === 403 && /JWT|jwt|token|expired|PGRST301|PGRST302|InvalidGrant/.test(text));
      if (!_retried && authRetry) {
        let refreshed = false;
        try { const mod = await import("./auth.js"); const r = await mod.ensureFreshSession(); refreshed = !!(r && r.ok && (typeof window !== "undefined" && window.__petosAccessToken)); } catch { /* fall through */ }
        if (refreshed) return await rawInvoke(fn, body, true);
      }
      _maybeFireSessionExpired(resp.status, text);
      let j; try { j = JSON.parse(text); } catch { /* not json */ }
      const err = new Error((j && j.error) || `invoke ${fn} ${resp.status}: ${text.slice(0, 160)}`);
      err.status = resp.status;
      throw err;
    }
    try { return text ? JSON.parse(text) : null; } catch { return null; }
  } finally {
    clearTimeout(tid);
  }
}
async function rawUpdate(table, filterCol, filterVal, changes) {
  const arr = await rawWrite("PATCH", `${table}?${filterCol}=eq.${encodeURIComponent(filterVal)}`, changes, "return=representation");
  return (arr && arr[0]) || null;
}
// Authenticated PostgREST GET over the hardened raw-fetch path. supabase-js's
// own `.select()` wedges/hangs on aged iOS WebView sessions after resume (the
// same failure that pushed every write onto rawWrite) — a hung read never
// resolves OR rejects, so a view's spinner spins forever until the app is
// force-killed. `path` is the full PostgREST query string after /rest/v1/, e.g.
// `wa_conversations?tenant_id=eq.<id>&select=*&order=...`. Returns the parsed
// array (never null). Refreshes the bearer once on a token-related 401/403.
async function rawRead(path, _retried, _netRetried) {
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), 12000);
  try {
    await _ensureBearer();
    // Same raced-timeout rationale as rawWrite: abort() may never reject on a
    // dead socket, and a hanging rawRead silently breaks the save-watchdog's
    // verify + the outbox replay's existence check. A clean reject lets those
    // callers treat it as "unverified" instead of stalling forever.
    let resp;
    try {
      resp = await Promise.race([
        appFetch(`${_restBase()}/${path}`, {
          method: "GET",
          timeoutMs: _netRetried ? 12500 : _firstAttemptTimeout(""),
          signal: controller.signal,
          headers: {
            apikey: _apikey(),
            Authorization: `Bearer ${_bearer()}`,
            Accept: "application/json",
          },
        }),
        // Reads are idempotent, so they always get the short post-resume window.
        new Promise((_, rej) => setTimeout(() => rej(Object.assign(new Error("rawRead net-timeout: " + path.slice(0, 60)), { code: "net-timeout" })), _netRetried ? 12500 : _firstAttemptTimeout(""))),
      ]);
    } catch (e) {
      // Same recovery writes get: a stalled read means the socket is dead, so
      // open a fresh one and ask again. Without this the shortened post-resume
      // window would just fail reads faster instead of healing them.
      if (e?.code === "net-timeout" && !_netRetried) {
        try { controller.abort(); } catch { /* best effort */ }
        try {
          await Promise.race([
            appFetch(`${(import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "")}/auth/v1/health`, { cache: "no-store", headers: { apikey: _apikey() }, timeoutMs: 3000 }),
            new Promise((res) => setTimeout(res, 3000)),
          ]);
        } catch { /* retry regardless */ }
        clearTimeout(tid);
        return await rawRead(path, _retried, true);
      }
      throw e;
    }
    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      if (!_retried && (resp.status === 401 || resp.status === 403) &&
          /JWT|jwt|token|expired|PGRST301|PGRST302|InvalidGrant/.test(text)) {
        let refreshed = false;
        try {
          const mod = await import("./auth.js");
          const r = await mod.ensureFreshSession();
          refreshed = !!(r && r.ok && (typeof window !== "undefined" && window.__petosAccessToken));
        } catch { /* fall through */ }
        if (refreshed) return await rawRead(path, true);
      }
      _maybeFireSessionExpired(resp.status, text);
      const err = new Error(`rawRead ${resp.status}: ${text.slice(0, 200)}`);
      err.status = resp.status;
      throw err;
    }
    const text = await resp.text();
    if (!text) return [];
    try { return JSON.parse(text); } catch { return []; }
  } finally {
    clearTimeout(tid);
  }
}

// Verify that rows with the given ids EXIST in a table — the read-back used by
// the native save-watchdog to distinguish "response dropped after commit"
// (row exists → genuinely saved) from "request never sent on a zombie socket"
// (row missing → the write was LOST; caller stashes it in the outbox for
// replay). Raw fetch (fresh request, no supabase-js queue), bounded by
// rawRead's own 12s abort; callers should race it tighter.
// Returns a Set of the ids that were found. Throws on network failure —
// callers must treat a throw as "unverified", NOT as "missing".
export async function verifyRowsExist(table, ids) {
  const list = (ids || []).filter(Boolean);
  if (!list.length) return new Set();
  const inList = list.map((id) => '"' + String(id).replace(/"/g, "") + '"').join(",");
  const rows = await rawRead(`${table}?id=in.(${encodeURIComponent(inList)})&select=id`);
  return new Set((rows || []).map((r) => r.id));
}
// Liveness probe for NATIVE, over the hardened raw fetch.
//
// The heartbeat used to probe with supabase-js `.from()`. On Android that call
// NEVER RESOLVES — measured on a device: every single ping hit its 5s timeout
// while rawRead queries on the same connection answered in ~600ms. supabase-js
// wedging on native is the whole reason rawRead exists; the probe was the one
// place still asking the broken client whether the app was alive.
//
// The cost was not cosmetic: a false "dead" made the heartbeat reconnect
// realtime AND run a full 11-query state reload on EVERY tick, forever, on
// every Android device. Constant traffic, constant battery, and a liveness
// signal that could never go green.
//
// Probes the AUTHENTICATED path (the same one saves use), so a wedge that only
// kills authed traffic is still caught — a public-table read stays green on a
// wedged session and false-alives. Any response, 401 included, means the
// connection is alive (a stale token is ensureFreshSession's job). Only a hang
// or a network throw is "dead".
export async function pingRest(userId) {
  const path = userId
    ? `profiles?id=eq.${encodeURIComponent(userId)}&select=id&limit=1`
    : "platform_config?select=id&limit=1";
  try {
    await Promise.race([
      rawRead(path),
      new Promise((_, rej) => setTimeout(() => rej(new Error("ping-timeout")), 5000)),
    ]);
    return true;
  } catch (e) {
    // A 401/403 came BACK — that is a live connection with a tired token.
    if (/\b(401|403)\b/.test(String(e?.message || e))) return true;
    return false;
  }
}

// Read appointments straight back off the server, mapped to app-state shape.
//
// Used only by the native save verifier: after a write times out we need to
// ask Postgres what it actually holds, and compare it field-by-field against
// what we meant to write. Deliberately goes through rawRead (the hardened
// fetch) rather than supabase-js, because the whole reason we are here is that
// supabase-js's connection just black-holed a request.
export async function fetchAppointmentsByIds(ids) {
  const list = (ids || []).filter(Boolean);
  if (!list.length) return [];
  const inList = list.map((id) => '"' + String(id).replace(/"/g, "") + '"').join(",");
  const rows = await rawRead(`appointments?id=in.(${encodeURIComponent(inList)})&select=*`);
  return (rows || []).map(mapAppointment).filter(Boolean);
}

// Did these rows actually get written since `sinceIso`?
//
// The companion to verifyRowsExist, for EDITS. Existence proves an insert
// landed but says nothing about an update, so a timed-out edit used to be
// unprovable and was reported as 儲存失敗 even when the row had in fact
// committed (the response just never came back over a dead socket). Tables
// with a set_updated_at trigger — appointments, tenants — give us a real
// server-side receipt: if updated_at moved past the moment the save started,
// the write reached Postgres. Returns the subset of ids that can be proven.
export async function verifyRowsTouchedSince(table, ids, sinceIso) {
  const list = (ids || []).filter(Boolean);
  if (!list.length) return new Set();
  const inList = list.map((id) => '"' + String(id).replace(/"/g, "") + '"').join(",");
  const rows = await rawRead(`${table}?id=in.(${encodeURIComponent(inList)})&select=id,updated_at`);
  const since = new Date(sinceIso).getTime();
  return new Set(
    (rows || [])
      .filter((r) => r.updated_at && new Date(r.updated_at).getTime() >= since)
      .map((r) => r.id)
  );
}

// PostgREST RPC over the hardened raw-fetch path on native. supabase-js's own
// .rpc() wedges/hangs on aged iOS WebView sessions — the same failure that
// pushed every write onto rawWrite. On native we POST straight to
// /rest/v1/rpc/<fn>; web keeps supabase-js untouched. Shaped exactly like
// supabase.rpc so call sites keep their { data, error } handling verbatim.
// AUTHENTICATED RPCs only — rawWrite assumes a logged-in bearer (fine for
// owner/staff/customer actions; NOT for anon/public reads).
// Who is signed in — WITHOUT a network round-trip on native.
//
// supabase.auth.getUser() is a supabase-js auth call, and on the Android app
// after a reopen it never settles: measured 2026-10-05 in the emulator, the
// 訊息 AI switch flipped to OFF on screen but no request ever left the device
// (edge logs: no /auth/v1/user, no POST) and the DB stayed ON. Every save that
// began with it stalled the same way. The access token we already hold carries
// the user id in its `sub` claim, so read it from there; the web keeps the
// server-verified getUser(), bounded so it can't hang a save either.
function _userIdFromToken() {
  try {
    const t = typeof window !== "undefined" ? window.__petosAccessToken : null;
    if (!t) return null;
    const part = t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(part + "===".slice((part.length + 3) % 4))).sub || null;
  } catch { return null; }
}
async function _currentUserId() {
  if (_isNative()) {
    const fromToken = _userIdFromToken();
    if (fromToken) return fromToken;
  }
  try {
    const res = await Promise.race([
      supabase.auth.getUser(),
      new Promise((r) => setTimeout(() => r({ data: null }), 4000)),
    ]);
    return res?.data?.user?.id || _userIdFromToken();
  } catch { return _userIdFromToken(); }
}

async function callRpc(fn, args) {
  if (_isNative()) {
    try {
      const data = await rawWrite("POST", `rpc/${fn}`, args ?? {});
      return { data, error: null };
    } catch (e) {
      return { data: null, error: e };
    }
  }
  return await supabase.rpc(fn, args);
}

// Postgres date/timestamp/time columns reject "" — coerce to null. Apply to
// any field whose name suggests it carries a date/time payload.
const nullIfEmpty = (v) => (v === "" ? null : v);
const sanitizeDates = (row) => {
  const out = { ...row };
  for (const k of Object.keys(out)) {
    if (/(_at|_date|date|birthday|time)$/i.test(k) && out[k] === "") out[k] = null;
  }
  return out;
};

export async function upsertAppointment(apt) {
  // Postgres uuid columns reject "" outright (22P02 invalid input syntax for
  // type uuid), and PostgREST turns that into a bare 400 — so a customer who
  // booked WITHOUT choosing a groomer ("— 不指定 —" is value="", and bk.staffId
  // starts as "") got 預約失敗 on every attempt, with nothing wrong in the form.
  // Normalise here rather than at each call site: every screen that builds an
  // appointment from a <select> is one empty option away from the same 400.
  const uuidOrNull = v => (v === "" || v === undefined ? (v === "" ? null : undefined) : v);
  const row = stripUndef({
    id: apt.id,
    tenant_id: apt.tenantId,
    kind: apt.kind || undefined,
    customer_id: uuidOrNull(apt.customerId),
    pet_id: uuidOrNull(apt.petId),
    staff_id: uuidOrNull(apt.staffId),
    service_id: uuidOrNull(apt.svcId),
    service_name: apt.service,
    addons: apt.addons,
    spa_ids: apt.spaIds || [],
    date: apt.date,
    time: apt.time,
    status: apt.status,
    base_price: apt.basePrice,
    addon_price: apt.addonPrice,
    deposit: apt.deposit,
    surcharges: apt.surcharges,
    discount: apt.discount,
    remaining_balance: apt.remainingBalance,
    paid: apt.paid,
    payment_log: Array.isArray(apt.paymentLog) ? apt.paymentLog : undefined,
    package_redeem_error: apt.packageRedeemError === undefined ? undefined : (apt.packageRedeemError || null),
    source: apt.source,
    checked_in_at: apt.checkedInAt,
    started_at: apt.startedAt,
    completed_at: apt.completedAt,
    report: apt.report,
    signature: apt.signature,
    rating: apt.rating,
    rating_comment: apt.ratingComment,
    cancel_reason: apt.cancelReason,
    staff_note: apt.staffNote,
    grooming_request: apt.groomingRequest,
    price_adjust_log: apt.priceAdjustLog,
    custom_duration_minutes: apt.customDuration,
    customer_package_id: apt.customerPackageId || null,
    approval_status: apt.approvalStatus || null,
    tip_amount: apt.tipAmount || 0,
    group_id: apt.groupId || null,
    deposit_receipt_url: apt.depositReceiptUrl || null,
    deposit_confirmed_at: apt.depositConfirmedAt || null,
    deposit_paid_wallet: Number(apt.depositPaidWallet) || 0,
    deposit_paid_points: Number(apt.depositPaidPoints) || 0,
    deposit_voucher_id:  apt.depositVoucherId || null,
    deposit_claimed_at:  apt.depositClaimedAt || null,
    refunded_at:         apt.refundedAt || null,
    refund_amount:       Number(apt.refundAmount) || 0,
    expires_at: apt.expiresAt || null,
    label: apt.label || null,
    color: apt.color || null,
    leave_type: apt.leaveType === undefined ? undefined : (apt.leaveType || null),
    is_mobile: apt.isMobile === undefined ? undefined : !!apt.isMobile,
    service_address: apt.serviceAddress === undefined ? undefined : (apt.serviceAddress || null),
    service_notes: apt.serviceNotes === undefined ? undefined : (apt.serviceNotes || null),
    voucher_discount: apt.voucherDiscount === undefined ? undefined : (Number(apt.voucherDiscount) || 0),
    intended_use_wallet: apt.intendedUseWallet === undefined ? undefined : (Math.max(0, Math.floor(Number(apt.intendedUseWallet) || 0))),
    intended_use_points: apt.intendedUsePoints === undefined ? undefined : (Math.max(0, Math.floor(Number(apt.intendedUsePoints) || 0))),
    intended_voucher_id: apt.intendedVoucherId === undefined ? undefined : (apt.intendedVoucherId || null),
    public_photos: apt.publicPhotos === undefined ? undefined : (Array.isArray(apt.publicPhotos) ? apt.publicPhotos : []),
    public_photo_token: apt.publicPhotoToken === undefined ? undefined : (apt.publicPhotoToken || null),
  });
  if (_isNative()) {
    try {
      const saved = await rawUpsert("appointments", sanitizeDates(row));
      return mapAppointment(saved);
    } catch (e) {
      const msg = (e.message || "").toLowerCase();
      if (msg.includes("customer_blocked")) {
        const blockErr = new Error("此客戶已被封鎖，無法建立預約");
        blockErr.code = "CUSTOMER_BLOCKED";
        throw blockErr;
      }
      if (row.customer_package_id && (msg.includes("customer_package_id") || msg.includes("23503"))) {
        const retryRow = { ...row, customer_package_id: null };
        const saved2 = await rawUpsert("appointments", sanitizeDates(retryRow));
        return mapAppointment(saved2);
      }
      throw e;
    }
  }
  const { data, error } = await supabase.from("appointments").upsert(sanitizeDates(row)).select("*").maybeSingle();
  if (error) {
    // Salvage path: if the customer_package_id FK is invalid (cp was
    // deleted / refunded since the booking sheet rendered), retry
    // without it so the appointment still saves. The shop can re-link
    // the package at done-time using FIFO. Without this fallback the
    // entire booking insert fails with a cryptic FK error and the
    // customer's request disappears.
    //
    // C2.5 hardening: log loudly + surface to telemetry. Previously
    // the only signal was a soft console.warn — owners had no way to
    // know a package linkage was silently dropped. We bump to console.
    // error and ping the global error sink so Sentry / a future banner
    // can act on it.
    const msg = (error.message || "").toLowerCase();
    if (msg.includes("customer_blocked")) {
      const blockErr = new Error("此客戶已被封鎖，無法建立預約");
      blockErr.code = "CUSTOMER_BLOCKED";
      throw blockErr;
    }
    if (row.customer_package_id && (msg.includes("customer_package_id") || msg.includes("23503") || error.code === "23503")) {
      const droppedCpId = row.customer_package_id;
      // eslint-disable-next-line no-console
      console.error("[PettoBook] apt upsert hit a stale customer_package_id FK; retrying without it. apt=" + apt.id + " cp=" + droppedCpId);
      try {
        if (typeof window !== "undefined" && typeof window.__pettobookOnSalvage === "function") {
          window.__pettobookOnSalvage({ type: "apt_cp_dropped", aptId: apt.id, cpId: droppedCpId });
        }
      } catch { /* listener errors must not break the salvage */ }
      const retryRow = { ...row, customer_package_id: null };
      const { data: data2, error: err2 } = await supabase.from("appointments").upsert(sanitizeDates(retryRow)).select("*").maybeSingle();
      if (err2) throw err2;
      return mapAppointment(data2);
    }
    throw error;
  }
  return mapAppointment(data);
}

// Soft-delete an appointment — sets deleted_at instead of removing the row.
// Preserves audit trail, allows restore, and keeps FKs intact. Pass
// `{ hard: true }` if you really need to wipe (e.g. mistake within seconds).
export async function deleteAppointment(id, opts = {}) {
  if (opts.hard) {
    if (_isNative()) {
      await rawWrite("DELETE", `appointments?id=eq.${encodeURIComponent(id)}`, null);
      return;
    }
    const { error } = await supabase.from("appointments").delete().eq("id", id);
    if (error) throw error;
    return;
  }
  // Native uses raw fetch — supabase-js's UPDATE wedges the post-OAuth
  // request queue on iPad (see ipad-google-login-architecture memory).
  if (_isNative()) {
    await rawUpdate("appointments", "id", id, { deleted_at: new Date().toISOString() });
    return;
  }
  const { error } = await supabase
    .from("appointments")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function upsertPet(pet) {
  const row = stripUndef({
    id: pet.id,
    tenant_id: pet.tenantId,
    owner_id: pet.ownerId,
    name: pet.name,
    breed: pet.breed,
    weight: pet.weight,
    weight_history: pet.weightHistory,
    birthday: pet.birthday,
    type: pet.type,
    species: pet.species || null,
    coat_length: pet.coatLength,
    size_override: pet.sizeOverride,
    allergies: pet.allergies,
    vaccine_date: pet.vaccineDate,
    vet_note: pet.vetNote,
    staff_note: pet.staffNote,
    behavior: pet.behavior,
    grooming_style: pet.groomingStyle,
    medical_flags: pet.medicalFlags,
    photos: pet.photos,
    logs: pet.logs,
    special_prices: pet.specialPrices,
    blocked_service_ids: pet.blockedServiceIds,
  });
  if (_isNative()) {
    const saved = await rawUpsert("pets", sanitizeDates(row));
    return mapPet(saved);
  }
  const { data, error } = await supabase.from("pets").upsert(sanitizeDates(row)).select("*").maybeSingle();
  if (error) throw error;
  return mapPet(data);
}

// Soft-delete a pet — same rationale as deleteAppointment.
export async function deletePet(id, opts = {}) {
  if (opts.hard) {
    if (_isNative()) {
      await rawWrite("DELETE", `pets?id=eq.${encodeURIComponent(id)}`, null);
      return;
    }
    const { error } = await supabase.from("pets").delete().eq("id", id);
    if (error) throw error;
    return;
  }
  if (_isNative()) {
    await rawUpdate("pets", "id", id, { deleted_at: new Date().toISOString() });
    return;
  }
  const { error } = await supabase
    .from("pets")
    .update({ deleted_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function upsertService(svc) {
  const basePrice = Number(svc.basePrice);
  const row = stripUndef({
    id: svc.id,
    tenant_id: svc.tenantId,
    name: svc.name,
    kind: svc.kind || "service",
    description: svc.description,
    base_price: Number.isFinite(basePrice) ? basePrice : 0,
    estimated_minutes: svc.estimatedMinutes,
    buffer_minutes: svc.bufferMinutes,
    mobile_available: svc.mobileAvailable === undefined ? undefined : !!svc.mobileAvailable,
    mobile_price: svc.mobilePrice === undefined ? undefined : (svc.mobilePrice === null || svc.mobilePrice === "" ? null : Number(svc.mobilePrice)),
    pricing_mode: svc.pricingMode,
    matrix_pricing: svc.matrixPricing,
    smart_pricing: svc.smartPricing,
    enabled: svc.enabled,
    display_order: svc.displayOrder,
    customer_visible: svc.customerVisible !== false,
    // Persist the single petType as a one-element array since the underlying
    // column is pet_types text[].
    // Write petTypes as the source of truth when multi-select is in use
    // (SPA dog+cat). Fall back to the legacy single petType column so
    // older clients still see a sensible default. Always at least one
    // entry; defaults to ["dog"] when both are missing.
    pet_types: (Array.isArray(svc.petTypes) && svc.petTypes.length)
      ? svc.petTypes
      : [svc.petType || "dog"],
    included_addon_ids: svc.includedAddonIds === undefined ? undefined : (Array.isArray(svc.includedAddonIds) ? svc.includedAddonIds : []),
  });
  if (_isNative()) {
    const saved = await rawUpsert("services", row);
    return mapService(saved);
  }
  const { data, error } = await supabase.from("services").upsert(row).select("*").maybeSingle();
  if (error) throw error;
  return mapService(data);
}

export async function deleteService(id) {
  if (_isNative()) {
    await rawWrite("DELETE", `services?id=eq.${encodeURIComponent(id)}`, null);
    return;
  }
  const { error } = await supabase.from("services").delete().eq("id", id);
  if (error) throw error;
}

export async function upsertAddon(addon) {
  const row = stripUndef({
    id: addon.id,
    tenant_id: addon.tenantId,
    name: addon.name,
    price: addon.price,
    pricing_mode: addon.pricingMode || "flat",
    variants: addon.variants || [],
    enabled: addon.enabled,
    pet_types: Array.isArray(addon.petTypes) && addon.petTypes.length ? addon.petTypes : ["dog"],
    estimated_minutes: addon.estimatedMinutes === undefined ? undefined : (Number(addon.estimatedMinutes) || 0),
  });
  if (_isNative()) {
    const saved = await rawUpsert("addons", row);
    return mapAddon(saved);
  }
  const { data, error } = await supabase.from("addons").upsert(row).select("*").maybeSingle();
  if (error) throw error;
  return mapAddon(data);
}

export async function deleteAddon(id) {
  if (_isNative()) {
    await rawWrite("DELETE", `addons?id=eq.${encodeURIComponent(id)}`, null);
    return;
  }
  const { error } = await supabase.from("addons").delete().eq("id", id);
  if (error) throw error;
}

export async function upsertInventory(item) {
  const row = stripUndef({
    id: item.id,
    tenant_id: item.tenantId,
    name: item.name,
    qty: item.qty,
    low_alert: item.lowAlert,
    unit: item.unit,
    cost_per_unit: item.costPerUnit,
  });
  if (_isNative()) {
    const saved = await rawUpsert("inventory", row);
    return mapInventory(saved);
  }
  const { data, error } = await supabase.from("inventory").upsert(row).select("*").maybeSingle();
  if (error) throw error;
  return mapInventory(data);
}

export async function deleteInventory(id) {
  if (_isNative()) {
    await rawWrite("DELETE", `inventory?id=eq.${encodeURIComponent(id)}`, null);
    return;
  }
  const { error } = await supabase.from("inventory").delete().eq("id", id);
  if (error) throw error;
}

export async function patchProfile(id, updates) {
  const row = stripUndef({
    name: updates.name,
    phone: updates.phone,
    country_code: updates.countryCode,
    phone2: updates.phone2,
    phone2_country_code: updates.phone2CountryCode,
    address: updates.address,
    avatar: updates.avatar,
    enabled: updates.enabled,
    customer_visible: updates.customerVisible === undefined ? undefined : !!updates.customerVisible,
    job_type: updates.jobType,
    settle_mode: updates.settleMode,
    mpf_enabled: updates.mpfEnabled === undefined ? undefined : updates.mpfEnabled,
    base_salary: updates.baseSalary,
    hourly_rate: updates.hourlyRate,
    commission_mode: updates.commissionMode,
    commission_rate: updates.commissionRate,
    commission_enabled: updates.commissionEnabled,
    package_commission_rate: updates.packageCommissionRate,
    package_redemption_commission_rate: updates.packageRedemptionCommissionRate,
    commission_tiers: updates.commissionTiers,
    service_commission_rates: updates.serviceCommissionRates,
    rate_change_log: updates.rateChangeLog,
    permissions: updates.permissions,
    join_date: updates.joinDate,
    leave_date: updates.leaveDate,
    schedule_preference: updates.schedulePreference,
    work_days: updates.workDays,
    staff_hours: updates.staffHours,
    wage_log: updates.wageLog,
    payout_log: updates.payoutLog,
    can_service_ids: updates.canServiceIds,
    time_off: updates.timeOff,
    rating: updates.rating,
    review_count: updates.reviewCount,
    clock_records: updates.clockRecords,
    wallet: updates.wallet,
    points: updates.points,
    wallet_tx: updates.walletTx,
    marketing_opt_out: updates.marketingOptOut === undefined ? undefined : !!updates.marketingOptOut,
    tags: updates.tags,
    referred_by: updates.referredBy,
    notes: updates.notes,
    tenant_id: updates.tenantId,
    role: updates.role,
    must_change_password: updates.mustChangePassword,
    // ical_token has a UNIQUE index. Multiple staff get patched in
    // parallel during schedule saves; if the diff carries `icalToken: ""`
    // (the mapper's default for "no token"), every patch writes the empty
    // string and the second one trips the unique constraint. Treat empty
    // strings as NULL so unset tokens stay unique-friendly.
    ical_token: (updates.icalToken === "" ? null : updates.icalToken),
  });
  if (_isNative()) {
    const saved = await rawUpdate("profiles", "id", id, row);
    return mapProfile(saved);
  }
  const { data, error } = await supabase.from("profiles").update(row).eq("id", id).select("*").maybeSingle();
  if (error) throw error;
  // `.maybeSingle()` returns data=null with NO error when zero rows were
  // updated — this happens when RLS rejects the write or a BEFORE-trigger
  // returns NULL. Without this check the caller falsely thinks the patch
  // succeeded; on next page reload the unchanged DB row replaces the
  // optimistic local edit, which looks like "my changes reverted".
  if (!data) {
    // eslint-disable-next-line no-console
    console.error("[PettoBook] patchProfile affected 0 rows for", id, "fields:", Object.keys(row));
    throw new Error("儲存失敗：權限不足或資料未變更");
  }
  return mapProfile(data);
}

// Owner edits a customer's profile via a SECURITY DEFINER RPC. This (a) works
// even for legacy customers with no membership row (the old patchProfile path
// hit RLS and silently updated 0 rows → "權限不足或資料未變更"), and (b)
// auto-links the customer to the shop. A duplicate-phone collision still
// raises 23505, which the caller maps to a friendly message.
export async function ownerUpdateCustomer({ userId, tenantId, name, phone, countryCode, phone2, phone2CountryCode, address }) {
  const { data, error } = await supabase.rpc("owner_update_customer", {
    p_user_id: userId, p_tenant_id: tenantId,
    p_name: name ?? null, p_phone: phone ?? null, p_country_code: countryCode ?? null,
    p_phone2: phone2 ?? null, p_phone2_country_code: phone2CountryCode ?? null, p_address: address ?? null,
  });
  if (error) throw error;
  return data;
}

// ── Customer claim (owner-approved merge of walk-in records) ──────────────
// A customer whose phone matches a shop walk-in files a claim; the owner
// approves it to merge the walk-in's pets/appointments/wallet/points into the
// customer's account (and apply the phone). Safe: only the owner can approve.
export async function requestCustomerClaim({ tenantId, phone }) {
  const { data, error } = await callRpc("request_customer_claim", { p_tenant_id: tenantId, p_phone: phone });
  if (error) throw error;
  return data || { matched: 0 };
}
export async function fetchPendingClaims(tenantId) {
  const { data, error } = await callRpc("owner_pending_claims", { p_tenant_id: tenantId });
  if (error) throw error;
  return (data || []).map(r => ({
    id: r.id,
    requesterId: r.requester_id,
    requesterName: r.requester_name || "",
    phone: r.phone || "",
    walkinCount: Number(r.walkin_count) || 0,
    petCount: Number(r.pet_count) || 0,
    // Merge preview — names + pets from the target walk-in records so
    // the owner can visually confirm WHO gets merged into the incoming
    // account. Empty arrays when the RPC hasn't been upgraded yet.
    targetNames: Array.isArray(r.target_names) ? r.target_names : [],
    petNames: Array.isArray(r.pet_names) ? r.pet_names : [],
    createdAt: r.created_at,
  }));
}
export async function approveCustomerClaim(claimId) {
  const { data, error } = await callRpc("approve_customer_claim", { p_claim_id: claimId });
  if (error) throw error;
  return data;
}
export async function rejectCustomerClaim(claimId) {
  const { data, error } = await callRpc("reject_customer_claim", { p_claim_id: claimId });
  if (error) throw error;
  return data;
}

export async function insertTenant(t) {
  const row = stripUndef({
    name: t.name,
    plan: t.plan || "free",
    theme: t.theme || "peach",
    owner_id: t.ownerId,
    booking_slug: t.bookingSlug,
    address: t.address,
    phone: t.phone,
    country: t.country || "HK",
    currency: t.currency || "HKD",
    language: t.language || "zh-HK",
    disclaimer_senior: t.disclaimerSenior,
    terms_booking: t.termsBooking,
    terms_cat: t.termsCat,
    daily_limit: t.dailyLimit || 8,
    holidays: t.holidays || [],
    business_hours: t.businessHours || {},
    deposit_policy: t.depositPolicy || {},
    cancellation_policy: t.cancellationPolicy || {},
    payment_methods: t.paymentMethods || ["cash"],
    messaging_config: t.messagingConfig || {},
    special_breeds: t.specialBreeds || [],
  });
  const { data, error } = await supabase.from("tenants").insert(row).select("*").maybeSingle();
  if (error) throw error;
  return mapTenant(data);
}

export async function patchTenant(id, updates) {
  const row = stripUndef({
    name: updates.name,
    plan: updates.plan,
    theme: updates.theme,
    booking_slug: updates.bookingSlug,
    address: updates.address,
    phone: updates.phone,
    country: updates.country,
    currency: updates.currency,
    language: updates.language,
    disclaimer_senior: updates.disclaimerSenior,
    senior_age_years: updates.seniorAgeYears === undefined ? undefined : (updates.seniorAgeYears == null ? null : Math.max(1, Math.min(30, Math.round(Number(updates.seniorAgeYears) || 10)))),
    terms_booking: updates.termsBooking,
    terms_cat: updates.termsCat,
    daily_limit: updates.dailyLimit,
    holidays: updates.holidays,
    business_hours: updates.businessHours,
    deposit_policy: updates.depositPolicy,
    cancellation_policy: updates.cancellationPolicy,
    payment_methods: updates.paymentMethods,
    quick_collect: updates.quickCollect,
    deposit_methods: updates.depositMethods,
    messaging_config: updates.messagingConfig,
    subscription: updates.subscription,
    special_breeds: updates.specialBreeds,
    logo_url: updates.logoUrl,
    app_icon: updates.appIcon,
    banner_url: updates.bannerUrl,
    about: updates.about,
    tagline: updates.tagline,
    website: updates.website,
    instagram: updates.instagram,
    facebook: updates.facebook,
    shop_gallery: updates.shopGallery,
    staff_order: updates.staffOrder,
    point_redeemables: updates.pointRedeemables,
    stamp_card: updates.stampCard,
    report_template: updates.reportTemplate,
    price_adjustments: updates.priceAdjustments,
    event_templates: Array.isArray(updates.eventTemplates) ? updates.eventTemplates : undefined,
    reviews_enabled: updates.reviewsEnabled,
    health_report_enabled: updates.healthReportEnabled,
    hide_branding: updates.hideBranding === undefined ? undefined : !!updates.hideBranding,
    wallet_enabled: updates.walletEnabled === undefined ? undefined : !!updates.walletEnabled,
    loyalty_enabled: updates.loyaltyEnabled === undefined ? undefined : !!updates.loyaltyEnabled,
    loyalty_earn_per: updates.loyaltyEarnPer === undefined ? undefined : Math.max(1, Number(updates.loyaltyEarnPer) || 100),
    loyalty_redeem_value: updates.loyaltyRedeemValue === undefined ? undefined : Math.max(0.01, Number(updates.loyaltyRedeemValue) || 1),
    redeem_scope: updates.redeemScope === undefined ? undefined : (["grooming","products","both"].includes(updates.redeemScope) ? updates.redeemScope : "grooming"),
    wc_enabled: updates.wcEnabled === undefined ? undefined : !!updates.wcEnabled,
    wc_store_url: updates.wcStoreUrl,
    wc_consumer_key: updates.wcConsumerKey,
    wc_consumer_secret: updates.wcConsumerSecret,
    wc_currency: updates.wcCurrency,
    wc_hide_out_of_stock: updates.wcHideOutOfStock === undefined ? undefined : !!updates.wcHideOutOfStock,
    wc_visible_categories: Array.isArray(updates.wcVisibleCategories)
      ? updates.wcVisibleCategories.map(Number).filter(Boolean)
      : undefined,
    wc_hidden_categories: Array.isArray(updates.wcHiddenCategories)
      ? updates.wcHiddenCategories.map(Number).filter(Boolean)
      : undefined,
    loyalty_earn_scope: updates.loyaltyEarnScope === undefined ? undefined : (updates.loyaltyEarnScope === "all" ? "all" : "online"),
    loyalty_merge_rewards: updates.loyaltyMergeRewards === undefined ? undefined : !!updates.loyaltyMergeRewards,
    pos_settings: updates.posSettings && typeof updates.posSettings === "object" ? updates.posSettings : undefined,
    max_pet_weight_kg: updates.maxPetWeightKg !== undefined ? updates.maxPetWeightKg : undefined,
    blocked_breed_ids: Array.isArray(updates.blockedBreedIds) ? updates.blockedBreedIds : undefined,
    tax_config: updates.taxConfig,
    stripe_config: updates.stripeConfig,
    region: updates.region,
    latitude: updates.latitude,
    longitude: updates.longitude,
    directory_listed: updates.directoryListed === undefined ? undefined : !!updates.directoryListed,
    directory_tags: updates.directoryTags === undefined ? undefined : (Array.isArray(updates.directoryTags) ? updates.directoryTags : []),
    directory_booking: updates.directoryBooking === undefined ? undefined : !!updates.directoryBooking,
    service_mode: updates.serviceMode,
    onboarded: updates.onboarded === undefined ? undefined : !!updates.onboarded,
    pricing_config: updates.pricingConfig === undefined ? undefined : (updates.pricingConfig || {}),
    boost_until: updates.boostUntil,
    geocoded_at: updates.geocodedAt,
  });
  if (_isNative()) {
    const saved = await rawUpdate("tenants", "id", id, row);
    return mapTenant(saved);
  }
  const { data, error } = await supabase.from("tenants").update(row).eq("id", id).select("*").maybeSingle();
  if (error) throw error;
  return mapTenant(data);
}

export async function pushNotification(notif) {
  const row = stripUndef({
    id: notif.id,
    tenant_id: notif.tenantId,
    user_id: notif.userId,
    type: notif.type,
    title: notif.title,
    body: notif.body,
    appointment_id: notif.aptId,
    read: notif.read || false,
  });
  // supabase-js NEVER settles on Android (measured: .from() hangs forever,
  // not slowly). dataSync AWAITS this on every appointment edit that raises a
  // notification, so the booking committed in under a second and then the
  // whole sync chain sat here until the 15s watchdog, retried — re-sending
  // the appointment write — and finally surfaced a 25s timeout on a booking
  // that had saved three times over. Same shape as the logAudit hang.
  if (_isNative()) return mapNotification(await rawUpsert("notifications", row));
  const { data, error } = await supabase.from("notifications").insert(row).select("*").maybeSingle();
  if (error) throw error;
  return mapNotification(data);
}

// Customer-initiated settlement of a booking deposit. Runs server-side
// via SECURITY DEFINER RPC because the customer can no longer PATCH
// customer_memberships financial columns directly (security hardening
// 2026-05-23). The RPC caps each amount at the actual balance, prevents
// voucher replay, and atomically updates the membership + apt row.
export async function settleBookingDeposit({ aptId, useWallet = 0, usePoints = 0, voucherId = null }) {
  const { data, error } = await callRpc("customer_settle_booking", {
    p_apt_id: aptId,
    p_use_wallet: Math.max(0, Math.floor(Number(useWallet) || 0)),
    p_use_points: Math.max(0, Math.floor(Number(usePoints) || 0)),
    p_voucher_id: voucherId || null,
  });
  if (error) throw error;
  // RPC returns a single-row table; Supabase serializes as [{ ... }].
  const row = Array.isArray(data) ? data[0] : data;
  if (!row || typeof row.new_wallet !== "number" || typeof row.new_points !== "number") {
    // The RPC contract changed or the row was nullified. Treat as failure
    // so the caller's retry path can surface the issue instead of mirroring
    // a fabricated zero balance over the customer's real wallet.
    throw new Error("customer_settle_booking: malformed response");
  }
  return {
    newWallet: row.new_wallet,
    newPoints: row.new_points,
    depositPaid: typeof row.deposit_paid === "number" ? row.deposit_paid : 0,
  };
}

// Cancellation refund — applies the policy fee, restores wallet/points
// in the same proportions the deposit was originally paid (from the new
// deposit_paid_wallet / deposit_paid_points columns), and unmarks any
// voucher used at booking. Server-side because RLS no longer allows the
// customer to PATCH their own membership balance.
export async function refundCancellation({ aptId, lateFee = 0, reason = "" }) {
  const { data, error } = await callRpc("customer_refund_cancellation", {
    p_apt_id: aptId,
    p_late_fee: Math.max(0, Math.round(Number(lateFee) || 0)),
    p_reason: reason || "",
  });
  if (error) throw error;
  return data || {};
}

// Shop owner provisions a full auth account for a pet owner. The Edge
// Function (create-customer-account) requires the service_role key and
// performs an authorization check that the caller owns/admins the
// target tenant. Returns { ok, userId, email, presetPassword,
// mustChangePassword }. Throws on failure with a translated message
// so the calling UI can toast it directly.
const _ACCT_ERR_MAP = {
  email_already_exists: "此電郵已被使用，請改用其他電郵或請客戶自行登入",
  create_user_failed: "建立帳戶失敗：請檢查電郵格式或聯絡客服",
  invalid_phone: "電話號碼格式不正確",
  not_authorized: "權限不足：只有店主可以建立客戶帳戶",
  missing_fields: "請填寫所有必填欄位",
  missing_authorization: "登入逾時，請重新登入再試",
  invalid_token: "登入逾時，請重新登入再試",
  invalid_json: "資料格式錯誤，請重試",
  no_profile: "找不到您的店主資料，請重新登入",
  profile_lookup_failed: "讀取資料失敗，請稍候再試",
  profile_upsert_failed: "儲存客戶資料失敗：此電話可能已被另一客戶使用",
  phone_exists: "此電話已有客戶帳戶 — 可請客戶直接用電話 / 電郵登入",
  no_user_id: "建立帳戶失敗：伺服器回應異常",
};
export async function createCustomerAccount({ tenantId, name, email, phone, countryCode = "+852", password = null, petInfo = null }) {
  const body = { tenantId, name, email, phone, countryCode, password, petInfo };
  // NATIVE: hardened raw invoke — functions.invoke() wedges on aged WebView
  // sessions (see createWalkinCustomer above). rawInvoke throws Error(<code>)
  // with the fn's error code as the message, so we reuse the same translation
  // map the web path uses below.
  if (_isNative()) {
    let data;
    try {
      data = await rawInvoke("create-customer-account", body);
    } catch (e) {
      const code = e?.message || "unknown_error";
      throw new Error(_ACCT_ERR_MAP[code] || ("建立帳戶失敗：" + code));
    }
    if (data?.error || !data?.ok) throw new Error(_ACCT_ERR_MAP[data?.error] || "建立帳戶失敗");
    return data;
  }
  const { data, error } = await supabase.functions.invoke("create-customer-account", {
    body,
  });
  if (error) {
    // supabase-js v2 sets `error.context` to a Response object. To read
    // the function's JSON body we need to await context.json() — the
    // previous code did `JSON.parse(error.context.body)` which never
    // worked because `.body` is a ReadableStream, not a string. Every
    // function error therefore fell through to the generic
    // "non-2xx status code" message, which is exactly what the owner
    // saw in the toast.
    let detail = null;
    try {
      if (error.context && typeof error.context.clone === "function") {
        const cloned = error.context.clone();
        try { detail = await cloned.json(); }
        catch {
          try { const txt = await cloned.text(); detail = txt ? JSON.parse(txt) : null; }
          catch { detail = null; }
        }
      }
    } catch { /* response already consumed or non-JSON */ }
    if (detail) {
      // eslint-disable-next-line no-console
      console.warn("[PettoBook] create-customer-account error:", detail);
    }
    const code = detail?.error || error.message || "unknown_error";
    throw new Error(_ACCT_ERR_MAP[code] || ("建立帳戶失敗：" + (detail?.detail || code)));
  }
  if (!data?.ok) throw new Error("建立帳戶失敗");
  return data;
}

// Owner-side password reset for a customer of their shop. Returns
// { ok, userId, presetPassword, mustChangePassword }. The customer
// is forced to set their own password on next login.
export async function resetCustomerPassword({ tenantId, userId, password = null }) {
  const { data, error } = await supabase.functions.invoke("reset-customer-password", {
    body: { tenantId, userId, password },
  });
  if (error) {
    let detail = null;
    try {
      if (error.context && typeof error.context.clone === "function") {
        const cloned = error.context.clone();
        try { detail = await cloned.json(); }
        catch {
          try { const txt = await cloned.text(); detail = txt ? JSON.parse(txt) : null; }
          catch { detail = null; }
        }
      }
    } catch { /* ignore */ }
    if (detail) console.warn("[PettoBook] reset-customer-password error:", detail);
    const code = detail?.error || error.message || "unknown_error";
    const map = {
      missing_authorization: "登入逾時，請重新登入再試",
      invalid_token: "登入逾時，請重新登入再試",
      invalid_json: "資料格式錯誤，請重試",
      missing_fields: "缺少必填欄位（tenantId / userId）",
      no_profile: "找不到您的店主資料",
      profile_lookup_failed: "讀取店主資料失敗",
      not_authorized: "權限不足：只有店主可以重設客戶密碼",
      target_lookup_failed: "查詢客戶資料失敗",
      target_not_found: "找不到此客戶",
      target_not_customer: "此帳戶不是客戶（員工 / 店主請用標準重設流程）",
      target_wrong_tenant: "此客戶不屬於你的店鋪",
      no_password_available: "客戶沒有電話號碼，請輸入自訂密碼（至少 6 位）",
      update_password_failed: "重設密碼失敗，請稍候再試",
    };
    throw new Error(map[code] || ("重設密碼失敗：" + (detail?.detail || code)));
  }
  if (!data?.ok) throw new Error("重設密碼失敗");
  return data;
}

// Owner-side permanent delete of a customer. Wraps the
// delete-customer-account Edge Function which removes the auth.users row
// (releasing the email) on top of the profiles row. The previous
// in-app `delete from profiles` left the auth row orphaned so the email
// couldn't re-register or Google-login.
export async function deleteCustomerAccount(customerId) {
  const { data, error } = await supabase.functions.invoke("delete-customer-account", {
    body: { customerId },
  });
  if (error) {
    let detail = null;
    try {
      if (error.context && typeof error.context.clone === "function") {
        const cloned = error.context.clone();
        try { detail = await cloned.json(); }
        catch {
          try { const txt = await cloned.text(); detail = txt ? JSON.parse(txt) : null; }
          catch { detail = null; }
        }
      }
    } catch { /* ignore */ }
    if (detail) console.warn("[PettoBook] delete-customer-account error:", detail);
    const code = detail?.error || error.message || "unknown_error";
    const map = {
      missing_authorization: "登入逾時，請重新登入再試",
      invalid_token: "登入逾時，請重新登入再試",
      invalid_json: "資料格式錯誤，請重試",
      missing_fields: "缺少必填欄位（customerId）",
      no_profile: "找不到您的店主資料",
      not_authorized: "權限不足：只有店主可以刪除客戶",
      not_a_customer: "此帳戶不是客戶（請用員工編輯器移除員工）",
      target_lookup_failed: "查詢客戶資料失敗",
      caller_lookup_failed: "讀取店主資料失敗",
      delete_user_failed: "刪除帳戶失敗，請稍候再試",
    };
    throw new Error(map[code] || ("刪除失敗：" + (detail?.detail || code)));
  }
  if (!data?.ok) throw new Error("刪除失敗");
  return data;
}

// Self-heal: when fetchProfile returns null for an authenticated user
// (their auth.users row exists but the public.profiles row was deleted),
// call this SECURITY DEFINER RPC to recreate a customer-default profile.
// Returns the freshly-mapped profile so the caller can continue the
// login flow without bouncing back to the login screen.
export async function ensureProfileForCaller() {
  const { data, error } = await supabase.rpc("ensure_profile_for_caller");
  if (error) throw error;
  return mapProfile(data);
}

// Customer asks the shop to provision a package. RLS prevents customers from
// directly inserting notifications targeted at owners/admins, so we route
// through a SECURITY DEFINER RPC that fans out one notification per shop
// owner+admin. Returns the count of notifications created. Throws if the
// caller isn't authenticated or the package no longer exists.
export async function requestPackagePurchase({ tenantId, packageId }) {
  const { data, error } = await callRpc("request_package_purchase", {
    p_tenant_id: tenantId,
    p_package_id: packageId,
  });
  if (error) throw error;
  return Number(data) || 0;
}

export async function markNotificationRead(id) {
  // Native: raw-fetch PATCH — supabase-js .update() wedges on aged iOS
  // WebView sessions, so 全部已讀 never persisted and notifications came
  // back unread after a realtime refresh. rawUpdate is the proven path.
  if (_isNative()) {
    await rawUpdate("notifications", "id", id, { read: true });
    return;
  }
  const { error } = await supabase.from("notifications").update({ read: true }).eq("id", id);
  if (error) throw error;
}

// ── Public tenant bundle (used by PublicBooking before login) ──────────────
// Returns everything PublicBooking needs to render: tenant info + services +
// addons + minimal staff list. Each table has either public-read RLS or a
// SECURITY DEFINER function fronting it.
// Anon-safe fetch for a before/after photo album by its public token.
// Returns shop name / logo / pet name / service / photos. No customer
// id, no payment, no status. Token is generated client-side when the
// owner first uploads photos (see PhotoShareSheet).
// Anonymous shop directory — flows through the public_directory_list
// SECURITY DEFINER RPC so RLS on tenants stays restrictive. Returns
// only opt-in shops (directory_listed = true), boost-active sorted to
// the top. Caller supplies optional region filter + free-text query.
// Unified directory feed: member shops (tenants on PetOS) + non-member
// external listings (AI-researched / scraped), via the public_directory_v2 RPC.
// `country` scopes the feed (HK / TW / US). Each row carries `isMember` so the
// UI can render a verified badge + booking for members and a "claim" CTA for
// non-members.
export async function fetchPublicDirectory({ country = "HK", region = null, q = null, limit = 80 } = {}) {
  const { data, error } = await supabase.rpc("public_directory_v2", {
    p_country: country || "HK",
    p_region: region || null,
    p_q: q || null,
    p_limit: limit,
  });
  if (error) throw error;
  return (Array.isArray(data) ? data : []).map(r => ({
    id: r.id,
    kind: r.kind,                       // 'member' | 'listing'
    isMember: !!r.is_member,
    tenantId: r.is_member ? r.id : null,
    slug: r.slug,
    name: r.name,
    tagline: r.tagline || "",
    aboutPreview: r.about_preview || "",
    logoUrl: r.logo_url || null,
    bannerUrl: r.banner_url || null,
    photoUrl: r.photo_url || null,
    region: r.region || null,
    country: r.country || null,
    latitude: r.latitude != null ? Number(r.latitude) : null,
    longitude: r.longitude != null ? Number(r.longitude) : null,
    rating: r.rating != null ? Number(r.rating) : null,
    ratingCount: r.rating_count != null ? Number(r.rating_count) : null,
    boostUntil: r.boost_until || null,
    servicesHead: Array.isArray(r.services_head) ? r.services_head : [],
    galleryCount: Number(r.gallery_count) || 0,
    website: r.website || null,
    address: r.address || null,
    phone: r.phone || null,
    tags: Array.isArray(r.tags) ? r.tags.filter(Boolean) : [],
    bookingEnabled: r.booking_enabled !== false,
    serviceMode: r.service_mode || "in_shop",
  }));
}

// Single non-member (external) listing by slug — for the read-only basic
// profile page at #/listing/{slug}.
export async function fetchListingBySlug(slug) {
  const { data, error } = await supabase.rpc("public_listing_detail", { p_slug: slug });
  if (error) throw error;
  const r = Array.isArray(data) ? data[0] : data;
  if (!r) return null;
  return {
    id: r.id,
    slug: r.slug,
    name: r.name,
    region: r.region || null,
    country: r.country || null,
    address: r.address || "",
    phone: r.phone || "",
    website: r.website || "",
    latitude: r.latitude != null ? Number(r.latitude) : null,
    longitude: r.longitude != null ? Number(r.longitude) : null,
    rating: r.rating != null ? Number(r.rating) : null,
    ratingCount: r.rating_count != null ? Number(r.rating_count) : null,
    photoUrl: r.photo_url || null,
    hours: r.hours || null,
    servicesText: r.services_text || "",
    photos: Array.isArray(r.photos) ? r.photos : [],
    reviews: Array.isArray(r.reviews) ? r.reviews : [],
    editorialSummary: r.editorial_summary || "",
    priceLevel: r.price_level != null ? Number(r.price_level) : null,
    businessStatus: r.business_status || null,
    serviceMode: r.service_mode || "in_shop",
  };
}

// Submit a "claim this shop" request for a non-member listing. Routed through
// the public submit-shop-claim edge function (no JWT required) which logs a
// shop_claims row and emails cs@pettobook.com.
export async function submitShopClaim(payload) {
  const { data, error } = await supabase.functions.invoke("submit-shop-claim", { body: payload });
  if (error) throw error;
  if (data && data.error) throw new Error(data.error);
  return data;
}

// ── Sales / technical chat (public widget + platform inbox) ───────────────
// Visitor side goes through the public sales-chat edge function: a prospect
// has no account, so the request is scoped by a token their browser minted
// rather than by a JWT.

const VISITOR_TOKEN_KEY = "petos:sales-visitor";

export function salesVisitorToken() {
  try {
    let t = localStorage.getItem(VISITOR_TOKEN_KEY);
    if (!t) {
      t = "vt-" + (crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now());
      localStorage.setItem(VISITOR_TOKEN_KEY, t);
    }
    return t;
  } catch {
    // Private mode: a per-session token still works for this page view.
    return "vt-" + Math.random().toString(36).slice(2) + Date.now();
  }
}

export async function sendSalesMessage(payload) {
  const { data, error } = await supabase.functions.invoke("sales-chat", {
    body: { action: "send", visitorToken: salesVisitorToken(), ...payload },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

export async function pollSalesMessages(sinceIso) {
  const { data, error } = await supabase.functions.invoke("sales-chat", {
    body: { action: "poll", visitorToken: salesVisitorToken(), sinceIso },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

// ── Admin side (superadmin RLS) ───────────────────────────────────────────

export async function fetchSalesChats({ status } = {}) {
  let q = supabase.from("sales_chats")
    .select("id, name, email, phone, topic, page_url, locale, status, last_msg_at, last_msg_by, unread_admin, created_at")
    .order("last_msg_at", { ascending: false })
    .limit(100);
  if (status && status !== "all") q = q.eq("status", status);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map(r => ({
    id: r.id, name: r.name || "", email: r.email || "", phone: r.phone || "",
    topic: r.topic || "", pageUrl: r.page_url || "", locale: r.locale || "",
    status: r.status, lastMsgAt: r.last_msg_at, lastMsgBy: r.last_msg_by,
    unread: r.unread_admin || 0, createdAt: r.created_at,
  }));
}

export async function fetchSalesMessages(chatId) {
  const { data, error } = await supabase.from("sales_messages")
    .select("id, sender_role, body, created_at")
    .eq("chat_id", chatId).order("created_at", { ascending: true }).limit(300);
  if (error) throw error;
  return data || [];
}

export async function replySalesChat(chatId, body) {
  const u = { user: { id: await _currentUserId() } };
  const { error } = await supabase.from("sales_messages")
    .insert({ chat_id: chatId, sender_role: "platform", sender_id: u?.user?.id || null, body });
  if (error) throw error;
}

// Clearing the unread counter is a separate call from replying, so opening a
// thread to read it counts as handled even when there is nothing to say yet.
export async function markSalesChatRead(chatId) {
  const { error } = await supabase.from("sales_chats").update({ unread_admin: 0 }).eq("id", chatId);
  if (error) throw error;
}

export async function setSalesChatStatus(chatId, status) {
  const { error } = await supabase.from("sales_chats").update({ status }).eq("id", chatId);
  if (error) throw error;
}

// ── Shop claims (platform admin) ──────────────────────────────────────────
// Reads are superadmin-only by RLS. Before 2026-09 the table had RLS enabled
// with NO policies, so these queries would have returned an empty list even
// for the admin — which is why four real claims sat unnoticed for six weeks.

export async function fetchShopClaims({ status } = {}) {
  let q = supabase
    .from("shop_claims")
    .select("id, listing_id, shop_name, claimant_name, claimant_email, claimant_phone, claimant_role, message, status, admin_note, reviewed_at, created_at, br_doc_path, br_doc_mime, br_doc_size, br_doc_deleted_at")
    .order("created_at", { ascending: false })
    .limit(200);
  if (status && status !== "all") q = q.eq("status", status);
  const { data, error } = await q;
  if (error) throw error;
  const rows = data || [];

  // Pull the linked directory listings in one go so the admin can see which
  // listing is being claimed (and whether it is already claimed by a tenant).
  const ids = [...new Set(rows.map(r => r.listing_id).filter(Boolean))];
  let listings = {};
  if (ids.length) {
    const { data: ls } = await supabase
      .from("directory_listings")
      .select("id, name, slug, address, phone, region, claimed_tenant_id")
      .in("id", ids);
    for (const l of ls || []) listings[l.id] = l;
  }

  return rows.map(r => ({
    id: r.id,
    listingId: r.listing_id,
    shopName: r.shop_name || "",
    claimantName: r.claimant_name || "",
    claimantEmail: r.claimant_email || "",
    claimantPhone: r.claimant_phone || "",
    claimantRole: r.claimant_role || "",
    message: r.message || "",
    brDocPath: r.br_doc_path || null,
    brDocMime: r.br_doc_mime || null,
    brDocSize: r.br_doc_size || 0,
    brDocDeletedAt: r.br_doc_deleted_at || null,
    status: r.status || "pending",
    adminNote: r.admin_note || "",
    reviewedAt: r.reviewed_at || null,
    createdAt: r.created_at,
    listing: listings[r.listing_id] || null,
  }));
}

// status: "approved" | "rejected" | "pending" (reopen). Routed through the
// shop-claim-decision edge function rather than a direct UPDATE, because the
// decision and the email to the claimant must not drift apart — a status flip
// with no message is exactly the silence this feature exists to end.
//
// Returns { emailed, emailError } so the UI can tell the admin whether the
// shop was actually reached, instead of implying it when the mail bounced.
export async function setShopClaimStatus(id, status, adminNote) {
  const { data, error } = await supabase.functions.invoke("shop-claim-decision", {
    body: { claimId: id, status, adminNote },
  });
  if (error) {
    // A refused approval comes back 409 with the REASON in the body (e.g. the
    // listing already belongs to another shop). supabase-js turns any non-2xx
    // into a generic "non-2xx status code", which would hide exactly the thing
    // the admin needs to read, so dig the real message out.
    let detail = "";
    try { detail = await error.context?.json?.().then(j => j?.error || j?.provisionError || "") || ""; } catch { /* ignore */ }
    throw new Error(detail || error.message || "shop-claim-decision failed");
  }
  if (data && data.error) throw new Error(data.error);
  return data || { ok: true };
}

// Public "Contact us" form on the marketing home page → emails the platform
// contact address (resolved server-side from platform_config). Email only,
// no DB row. Payload: { name, email, phone?, subject?, message, company? (honeypot) }.
export async function submitContactMessage(payload) {
  const { data, error } = await supabase.functions.invoke("contact-message", { body: payload });
  if (error) throw error;
  if (data && data.error) throw new Error(data.error);
  return data;
}

// Public "Request a demo" — emails the requester the shared demo logins and
// notifies the platform inbox. Payload: { email, name?, company? (honeypot) }.
export async function requestDemo(payload) {
  const { data, error } = await supabase.functions.invoke("request-demo", { body: payload });
  if (error) throw error;
  if (data && data.error) throw new Error(data.error);
  return data;
}

// ── RFQ (Request For Quote) — grooming request marketplace ─────────────
// Customer posts a grooming demand → shops in their region see it in an
// inbox and submit price+slot quotes → customer accepts one → atomic
// conversion to an appointment.

export const mapGroomingRequest = (r) => r && ({
  id: r.id,
  customerId: r.customer_id,
  petId: r.pet_id,
  petType: r.pet_type,
  petWeight: r.pet_weight != null ? Number(r.pet_weight) : null,
  petCoat: r.pet_coat || null,
  petName: r.pet_name || "",
  serviceKind: r.service_kind,
  description: r.description || "",
  dateStart: r.date_start,
  dateEnd: r.date_end,
  timeWindow: r.time_window || "any",
  region: r.region || null,
  budgetMin: r.budget_min != null ? Number(r.budget_min) : null,
  budgetMax: r.budget_max != null ? Number(r.budget_max) : null,
  photos: Array.isArray(r.photos) ? r.photos : [],
  status: r.status,
  mobile: !!r.mobile,
  acceptedQuoteId: r.accepted_quote_id || null,
  declinedBy: Array.isArray(r.declined_by) ? r.declined_by : [],
  expiresAt: r.expires_at,
  createdAt: r.created_at,
});

export const mapGroomingQuote = (r) => r && ({
  id: r.id,
  requestId: r.request_id,
  tenantId: r.tenant_id,
  price: Number(r.price) || 0,
  slotDate: r.slot_date,
  slotTime: r.slot_time,
  serviceId: r.service_id || null,
  message: r.message || "",
  status: r.status,
  expiresAt: r.expires_at,
  createdAt: r.created_at,
  respondedAt: r.responded_at || null,
});

export async function postGroomingRequest(input) {
  const { data, error } = await supabase
    .from("grooming_requests")
    .insert({
      customer_id: input.customerId,
      pet_id: input.petId || null,
      pet_type: input.petType,
      pet_weight: input.petWeight || null,
      pet_coat: input.petCoat || null,
      pet_name: input.petName || "",
      service_kind: input.serviceKind,
      description: input.description || "",
      date_start: input.dateStart,
      date_end: input.dateEnd,
      time_window: input.timeWindow || "any",
      region: input.region || null,
      mobile: !!input.mobile,
      location_lat: input.lat || null,
      location_lng: input.lng || null,
      budget_min: input.budgetMin || null,
      budget_max: input.budgetMax || null,
      photos: input.photos || [],
    })
    .select("*")
    .maybeSingle();
  if (error) throw error;
  return mapGroomingRequest(data);
}

export async function fetchMyGroomingRequests() {
  const { data, error } = await supabase
    .from("grooming_requests")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(50);
  if (error) throw error;
  return (data || []).map(mapGroomingRequest);
}

export async function fetchQuotesForRequest(requestId) {
  const { data, error } = await supabase
    .from("grooming_quotes")
    .select("*")
    .eq("request_id", requestId)
    .order("price", { ascending: true });
  if (error) throw error;
  return (data || []).map(mapGroomingQuote);
}

export async function fetchShopInbox(tenantId) {
  const { data, error } = await supabase.rpc("shop_inbox_requests", { p_tenant_id: tenantId });
  if (error) throw error;
  return (data || []).map(r => ({
    id: r.request_id,
    customerName: r.customer_name,
    petName: r.pet_name,
    petType: r.pet_type,
    petWeight: r.pet_weight != null ? Number(r.pet_weight) : null,
    petCoat: r.pet_coat,
    serviceKind: r.service_kind,
    description: r.description,
    dateStart: r.date_start,
    dateEnd: r.date_end,
    timeWindow: r.time_window,
    region: r.region,
    budgetMin: r.budget_min,
    budgetMax: r.budget_max,
    photos: Array.isArray(r.photos) ? r.photos : [],
    createdAt: r.created_at,
    expiresAt: r.expires_at,
    alreadyQuoted: !!r.already_quoted,
    regionMatch: !!r.region_match,
    mobile: !!r.mobile,
  }));
}

export async function submitQuote({ requestId, tenantId, price, slotDate, slotTime, serviceId, message }) {
  const { data, error } = await supabase
    .from("grooming_quotes")
    .insert({
      request_id: requestId,
      tenant_id: tenantId,
      price: Number(price) || 0,
      slot_date: slotDate,
      slot_time: slotTime,
      service_id: serviceId || null,
      message: message || "",
    })
    .select("*")
    .maybeSingle();
  if (error) throw error;
  return mapGroomingQuote(data);
}

export async function acceptGroomingQuote(quoteId) {
  const { data, error } = await supabase.rpc("accept_grooming_quote", { p_quote_id: quoteId });
  if (error) {
    const code = (error.message || "").toLowerCase();
    if (code.includes("slot_taken")) throw new Error("此時段已被預約，請揀另一個報價");
    if (code.includes("quote_not_open")) throw new Error("此報價已失效");
    if (code.includes("request_not_open")) throw new Error("此詢價已關閉");
    throw error;
  }
  return data; // new appointment id
}

export async function cancelGroomingRequest(requestId) {
  const { error } = await supabase
    .from("grooming_requests")
    .update({ status: "cancelled" })
    .eq("id", requestId);
  if (error) throw error;
}

export async function declineGroomingRequest(requestId, tenantId) {
  // Append tenantId to declined_by[]; cheap RPC keeps it atomic. The
  // previous version silently swallowed all failures via
  // .catch(() => ({ error: null })) — Phase 1.1 of the stability plan
  // requires every failure to surface. Throw to the caller so the UI
  // can toast + the user knows the decline didn't stick.
  try {
    const { error } = await supabase.rpc("declined_by_append", { p_request_id: requestId, p_tenant_id: tenantId });
    if (error) throw error;
  } catch (e) {
    // Wrap with a friendlier message; caller decides whether to surface.
    const wrapped = new Error("拒絕詢價失敗：" + (e?.message || e));
    wrapped.cause = e;
    throw wrapped;
  }
}

export async function fetchPublicAlbum(token) {
  if (!token) return null;
  const { data, error } = await supabase.rpc("fetch_public_album", { p_token: token });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  if (!row) return null;
  return {
    aptDate: row.apt_date,
    aptTime: row.apt_time,
    shopName: row.shop_name,
    shopLogoUrl: row.shop_logo_url,
    shopTagline: row.shop_tagline,
    shopTheme: row.shop_theme,
    petName: row.pet_name,
    petType: row.pet_type,
    service: row.service,
    photos: Array.isArray(row.photos) ? row.photos : [],
  };
}

// Returns anonymised busy windows for a tenant's date range. Used by the
// public/customer booking time picker — RLS on appointments hides other
// customers' bookings from the calling session, so generateTimeSlots
// alone marks every slot free. The RPC returns just staff_id + time +
// duration (no PII) and is granted to anon + authenticated.
export async function fetchPublicBusySlots(tenantId, fromDate, toDate) {
  if (!tenantId || !fromDate) return [];
  const to = toDate || fromDate;
  const { data, error } = await supabase.rpc("public_busy_slots", {
    p_tenant_id: tenantId,
    p_from: fromDate,
    p_to: to,
  });
  if (error) throw error;
  return (data || []).map(r => ({
    staffId: r.staff_id,
    date: r.apt_date,
    time: r.apt_time,
    durationMin: Number(r.duration_min) || 60,
  }));
}

export async function fetchPublicTenantBundle(slug) {
  const tenant = await fetchTenantBySlug(slug);
  if (!tenant) return null;
  const [services, addons, staffRows] = await Promise.all([
    fetchTenantServices(tenant.id),
    fetchTenantAddons(tenant.id),
    callRpc("list_tenant_staff_public", { p_tenant_id: tenant.id }).then(r => r.data || []),
  ]);
  // Shape into the same in-memory layout used elsewhere (attachTenantChildren).
  // Hide services flagged customer_visible=false — these are owner/staff-only
  // (e.g. internal walk-in SKUs, comp services) and must not surface on the
  // public booking page.
  tenant.services = services.filter(s => s.customerVisible !== false);
  tenant.addons = addons;
  tenant.inventory = [];
  // Owners ARE bookable groomers in small one-person shops (no separate
  // hired staff yet). The list_tenant_staff_public RPC already returns
  // both roles; we include both in the staff list shown on the booking page.
  tenant.staffIds = staffRows.filter(p => p.role === "staff" || p.role === "owner").map(p => p.id);
  tenant.customerIds = [];
  tenant.cancelPolicy = {
    deadlineHours: tenant.cancellationPolicy?.deadlineHours || 24,
    lateCancelFee: (tenant.cancellationPolicy?.lateCancelFeePercent || 50) / 100,
    noShowFee: (tenant.cancellationPolicy?.noShowFeePercent || 100) / 100,
  };
  const staffUsers = staffRows.map(r => ({
    id: r.id, role: r.role, name: r.name, enabled: r.enabled !== false,
    tenantId: tenant.id, pets: [], avatar: r.avatar || null,
    joinDate: r.join_date || null, leaveDate: r.leave_date || null,
    jobType: r.job_type || null,
    staffHours: (r.staff_hours && typeof r.staff_hours === "object") ? r.staff_hours : {},
    timeOff: Array.isArray(r.time_off) ? r.time_off : [],
  }));
  return { tenant, staffUsers };
}

// ── Public phone lookup (used by PublicBooking before signup) ──────────────

export async function lookupCustomerByPhone(tenantId, phone) {
  const { data, error } = await supabase.rpc("lookup_customer_by_phone", {
    p_tenant_id: tenantId,
    p_phone: phone,
  });
  if (error) throw error;
  return data || { found: false };
}

// ── WhatsApp / messaging ───────────────────────────────────────────────────

export async function fetchMessagingSecrets(tenantId) {
  // Native: raw GET, same reason as fetchWaConversations — supabase-js
  // .select() wedges on an aged iOS session after resume and never settles,
  // which left 設定 → 通訊 stuck on 「載入中…」 until the app was force-killed.
  // The WRITE path below already used rawWrite; the read was the one gap.
  let data;
  if (_isNative()) {
    const rows = await rawRead(
      `tenant_messaging_secrets?tenant_id=eq.${encodeURIComponent(tenantId)}&select=*&limit=1`
    );
    data = (rows || [])[0] || null;
  } else {
    const res = await supabase
      .from("tenant_messaging_secrets")
      .select("*")
      .eq("tenant_id", tenantId)
      .maybeSingle();
    if (res.error) throw res.error;
    data = res.data;
  }
  if (!data) return null;
  return {
    tenantId: data.tenant_id,
    whatsappEnabled: data.whatsapp_enabled,
    whatsappPhoneId: data.whatsapp_phone_id || "",
    whatsappAccessToken: data.whatsapp_access_token || "",
    whatsappFromName: data.whatsapp_from_name || "",
    whatsappDefaultTemplate: data.whatsapp_default_template || "",
    smsEnabled: !!data.sms_enabled,
    smsProvider: data.sms_provider || "twilio",
    smsAccountSid: data.sms_account_sid || "",
    smsAuthToken: data.sms_auth_token || "",
    smsFromNumber: data.sms_from_number || "",
    emailEnabled: !!data.email_enabled,
    emailProvider: data.email_provider || "resend",
    emailApiKey: data.email_api_key || "",
    emailFrom: data.email_from || "",
    waAiEnabled: !!data.wa_ai_enabled,
    // WhatsApp booking confirmation (per shop, opt-in) — sent server-side by
    // the booking-confirmation edge function when a customer books online.
    waConfirmEnabled: !!data.wa_confirm_enabled,
    waConfirmTemplate: data.wa_confirm_template || "",
    waConfirmLang: data.wa_confirm_lang || "zh_HK",
    waAiBookingEnabled: data.wa_ai_booking_enabled !== false,
    waAiKnowledge: data.wa_ai_knowledge || "",
    waAiInstructions: data.wa_ai_instructions || "",
    waAiGreeting: data.wa_ai_greeting || "",
    waAiName: data.wa_ai_name || "",
    waAiHours: data.wa_ai_hours || null,   // { enabled, start:"HH:MM", end:"HH:MM" } — null/enabled:false = 24h always-on
    waAiWeatherPolicy: data.wa_ai_weather_policy || "",
    waProvider: data.wa_provider || "meta",
    waTwilioAccountSid: data.wa_twilio_account_sid || "",
    waTwilioAuthToken: data.wa_twilio_auth_token || "",
    waTwilioFrom: data.wa_twilio_from || "",
    waWatiEndpoint: data.wa_wati_endpoint || "",
    waWatiToken: data.wa_wati_token || "",
    waWatiNumber: data.wa_wati_number || "",
    waChatwootBaseUrl: data.wa_chatwoot_base_url || "",
    waChatwootApiToken: data.wa_chatwoot_api_token || "",
    waChatwootAccountId: data.wa_chatwoot_account_id || null,
    waChatwootInboxId: data.wa_chatwoot_inbox_id || null,
    waChatwootHmacSecret: data.wa_chatwoot_hmac_secret || "",
    updatedAt: data.updated_at,
  };
}

export async function upsertMessagingSecrets(tenantId, s) {
  const userRes = { user: { id: await _currentUserId() } };
  const row = stripUndef({
    tenant_id: tenantId,
    whatsapp_enabled: s.whatsappEnabled,
    whatsapp_phone_id: s.whatsappPhoneId,
    whatsapp_access_token: s.whatsappAccessToken,
    whatsapp_from_name: s.whatsappFromName,
    whatsapp_default_template: s.whatsappDefaultTemplate,
    sms_enabled: s.smsEnabled,
    sms_provider: s.smsProvider,
    sms_account_sid: s.smsAccountSid,
    sms_auth_token: s.smsAuthToken,
    sms_from_number: s.smsFromNumber,
    email_enabled: s.emailEnabled,
    email_provider: s.emailProvider,
    email_api_key: s.emailApiKey,
    email_from: s.emailFrom,
    wa_ai_enabled: s.waAiEnabled,
    wa_confirm_enabled: s.waConfirmEnabled,
    wa_confirm_template: s.waConfirmTemplate,
    wa_confirm_lang: s.waConfirmLang,
    wa_ai_booking_enabled: s.waAiBookingEnabled,
    wa_ai_knowledge: s.waAiKnowledge,
    wa_ai_instructions: s.waAiInstructions,
    wa_ai_greeting: s.waAiGreeting,
    wa_ai_name: s.waAiName,
    wa_ai_hours: s.waAiHours,
    wa_ai_weather_policy: s.waAiWeatherPolicy,
    wa_provider: s.waProvider,
    wa_twilio_account_sid: s.waTwilioAccountSid,
    wa_twilio_auth_token: s.waTwilioAuthToken,
    wa_twilio_from: s.waTwilioFrom,
    wa_wati_endpoint: s.waWatiEndpoint,
    wa_wati_token: s.waWatiToken,
    wa_wati_number: s.waWatiNumber,
    wa_chatwoot_base_url: s.waChatwootBaseUrl,
    wa_chatwoot_api_token: s.waChatwootApiToken,
    wa_chatwoot_account_id: s.waChatwootAccountId,
    wa_chatwoot_inbox_id: s.waChatwootInboxId,
    wa_chatwoot_hmac_secret: s.waChatwootHmacSecret,
    updated_at: new Date().toISOString(),
    updated_by: userRes?.user?.id,
  });
  if (_isNative()) {
    const arr = await rawWrite(
      "POST",
      "tenant_messaging_secrets?on_conflict=tenant_id",
      [row],
      "resolution=merge-duplicates,return=representation",
    );
    return (arr && arr[0]) || null;
  }
  const { data, error } = await supabase
    .from("tenant_messaging_secrets")
    .upsert(row, { onConflict: "tenant_id" })
    .select("*")
    .maybeSingle();
  if (error) throw error;
  return data;
}

// Calls the send-whatsapp Edge Function. Caller must be authenticated.
export async function sendWhatsApp({ tenantId, to, template, templateParams, text, languageCode, components, appointmentId, userId, mediaUrl, mediaType, caption }) {
  const body = { tenantId, to, template, templateParams, text, languageCode, components, appointmentId, userId, mediaUrl, mediaType, caption };
  // Native: hardened raw invoke (functions.invoke wedges on aged iOS sessions,
  // which made the 訊息 send disappear + require a force-quit).
  if (_isNative()) return await rawInvoke("send-whatsapp", body);
  const { data, error } = await supabase.functions.invoke("send-whatsapp", { body });
  if (error) throw error;
  return data;
}

// ── WhatsApp inbox (inbound conversations stored by whatsapp-webhook) ──────

const mapWaConversation = (r) => r && ({
  id: r.id,
  tenantId: r.tenant_id,
  waId: r.wa_id,
  customerName: r.customer_name,
  profileId: r.profile_id,
  mode: r.mode,
  lastInboundAt: r.last_inbound_at,
  lastMessageAt: r.last_message_at,
  lastMessagePreview: r.last_message_preview,
  unreadCount: r.unread_count || 0,
});

export async function fetchWaConversations(tenantId) {
  // Native: raw GET — supabase-js .select() wedges on aged iOS sessions after
  // resume, so the 訊息 tab spun forever until the app was force-killed.
  if (_isNative()) {
    const rows = await rawRead(
      `wa_conversations?tenant_id=eq.${encodeURIComponent(tenantId)}&select=*&order=last_message_at.desc.nullslast&limit=100`
    );
    return (rows || []).map(mapWaConversation);
  }
  const { data, error } = await supabase
    .from("wa_conversations")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("last_message_at", { ascending: false, nullsFirst: false })
    .limit(100);
  if (error) throw error;
  return (data || []).map(mapWaConversation);
}

// Total unread WhatsApp messages across a tenant's conversations — for the
// 訊息 tab badge. Sums unread_count. Native-safe (raw GET) like the list fetch.
export async function fetchWaUnread(tenantId) {
  let rows;
  if (_isNative()) {
    rows = await rawRead(`wa_conversations?tenant_id=eq.${encodeURIComponent(tenantId)}&select=unread_count&unread_count=gt.0&limit=500`);
  } else {
    const { data, error } = await supabase
      .from("wa_conversations").select("unread_count")
      .eq("tenant_id", tenantId).gt("unread_count", 0).limit(500);
    if (error) throw error;
    rows = data;
  }
  return (rows || []).reduce((n, r) => n + (Number(r.unread_count) || 0), 0);
}

// ── Customer ↔ shop chat (web portal + Android customer app) ─────────────
// Same conversation the iOS customer app uses: one wa_conversations row per
// customer per shop, wa_id "app:<profile id>", created on first open.
export async function openShopConversation(tenantId) {
  const { data, error } = await callRpc("customer_open_conversation", { p_tenant_id: tenantId });
  if (error) throw error;
  return typeof data === "string" ? data : String(data || "");
}
export async function sendShopMessage(conversationId, body) {
  const { data, error } = await callRpc("customer_send_message", { p_conversation_id: conversationId, p_body: body });
  if (error) throw error;
  return data;
}
// Ask the shop's AI to answer the latest message. The server applies every
// gate (platform switch, shop AI switch + hours, plan, monthly cap, human
// takeover) and simply skips when the AI shouldn't reply — staff answer then.
// WooCommerce bridge for the customer portal's 網店 tab — same proxy the iOS
// customer app uses (woocommerce edge fn; the shop's WC keys never leave it).
// GET when `body` is null (query ops: products / product / variations /
// categories), POST otherwise (op: "order"). Raw path on every platform so
// Android doesn't hit the supabase-js hang.
export async function wooCall(query, body = null) {
  const base = (import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "");
  await _ensureBearer();
  const qs = new URLSearchParams(Object.entries(query || {}).filter(([, v]) => v != null && v !== "")).toString();
  const resp = await appFetch(`${base}/functions/v1/woocommerce${qs ? "?" + qs : ""}`, {
    method: body ? "POST" : "GET",
    headers: { apikey: _apikey(), Authorization: `Bearer ${_bearer()}`, ...(body ? { "Content-Type": "application/json" } : {}) },
    body: body ? JSON.stringify(body) : undefined,
    timeoutMs: 25000,
  });
  const text = await resp.text().catch(() => "");
  let j = null; try { j = text ? JSON.parse(text) : null; } catch { /* not json */ }
  if (!resp.ok) {
    _maybeFireSessionExpired(resp.status, text);
    const err = new Error((j && (j.error + (j.detail ? "：" + String(j.detail).slice(0, 160) : ""))) || `woocommerce ${resp.status}`);
    err.status = resp.status;
    throw err;
  }
  return j;
}

// Platform announcement email to shop owners (superadmin). mode: "count"
// (recipient count), "test" (send only to me) or "send" (all owners).
export async function announceEmail(mode, email = null, announcementId = null) {
  const body = { mode, email, announcementId };
  if (_isNative()) return await rawInvoke("announce-email", body);
  const { data, error } = await supabase.functions.invoke("announce-email", { body });
  if (error) {
    let detail = null;
    try { detail = await error.context?.clone?.().json(); } catch { /* not json */ }
    const map = { not_authorized: "只有超級管理員可以發送", email_not_configured: "平台未設定 Email（PETOS_EMAIL_KEY）", no_recipients: "沒有收件人", send_failed: "寄送失敗" };
    throw new Error((map[detail?.error] || detail?.error || error.message) + (detail?.detail ? "：" + detail.detail : ""));
  }
  return data;
}

export async function askShopAi(conversationId) {
  const body = { conversationId };
  if (_isNative()) return await rawInvoke("customer-chat-ai", body);
  const { data, error } = await supabase.functions.invoke("customer-chat-ai", { body });
  if (error) throw error;
  return data;
}

export async function fetchWaMessages(conversationId) {
  const mapMsg = (r) => ({
    id: r.id,
    direction: r.direction,
    sender: r.sender,
    body: r.body,
    status: r.status,
    mediaType: r.media_type || null,
    mediaUrl: r.media_url || null,
    createdAt: r.created_at,
  });
  if (_isNative()) {
    const rows = await rawRead(
      `wa_messages?conversation_id=eq.${encodeURIComponent(conversationId)}&select=*&order=created_at.asc&limit=200`
    );
    return (rows || []).map(mapMsg);
  }
  const { data, error } = await supabase
    .from("wa_messages")
    .select("*")
    .eq("conversation_id", conversationId)
    .order("created_at", { ascending: true })
    .limit(200);
  if (error) throw error;
  return (data || []).map(mapMsg);
}

export async function setWaConversationMode(conversationId, mode) {
  if (_isNative()) { await rawUpdate("wa_conversations", "id", conversationId, { mode }); return; }
  const { error } = await supabase
    .from("wa_conversations")
    .update({ mode })
    .eq("id", conversationId);
  if (error) throw error;
}

export async function markWaConversationRead(conversationId) {
  if (_isNative()) { await rawUpdate("wa_conversations", "id", conversationId, { unread_count: 0 }); return; }
  const { error } = await supabase
    .from("wa_conversations")
    .update({ unread_count: 0 })
    .eq("id", conversationId);
  if (error) throw error;
}

// This calendar month's AI-concierge reply count for a tenant. The
// whatsapp-webhook logs every AI reply to message_log with
// payload.source = "ai_concierge"; we count sent ones since month start.
export async function fetchAiConciergeUsage(tenantId) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const { count, error } = await supabase
    .from("message_log")
    .select("id", { count: "exact", head: true })
    .eq("tenant_id", tenantId)
    .eq("channel", "whatsapp")
    .eq("status", "sent")
    .eq("payload->>source", "ai_concierge")
    .gte("created_at", monthStart);
  if (error) throw error;
  return count || 0;
}

// Record a staff manual reply in the thread AFTER send-whatsapp succeeded.
export async function insertWaStaffMessage({ conversationId, tenantId, body, waMessageId, mediaUrl, mediaType }) {
  const row = {
    conversation_id: conversationId,
    tenant_id: tenantId,
    direction: "out",
    sender: "staff",
    body,
    wa_message_id: waMessageId || null,
    status: "sent",
    media_url: mediaUrl || null,
    media_type: mediaType || null,
  };
  if (_isNative()) { await rawUpsert("wa_messages", row); return; }
  const { error } = await supabase.from("wa_messages").insert(row);
  if (error) throw error;
}

// List the shop's APPROVED WhatsApp templates (for the inbox reopen picker).
export async function listWaTemplates(tenantId) {
  const body = { tenantId };
  if (_isNative()) return await rawInvoke("list-wa-templates", body);
  const { data, error } = await supabase.functions.invoke("list-wa-templates", { body });
  if (error) throw error;
  return data;
}

// Owner → customer: give a shop-created customer (no real login) a phone +
// 6-digit password they can sign in with on the shop page. The edge fn
// refuses customers who already have their own email/Google account, and
// re-syncs the synthetic login email to the current phone + slug. Returns
// { phone, password, shopName, loginUrl }.
const _SEND_LOGIN_ERR = {
  has_real_account: "此客戶已有自己嘅帳戶（電郵／Google），可以直接登入。",
  no_phone: "此客戶未有有效電話號碼，請先補回電話。",
  phone_login_taken: "另一個客戶紀錄用緊同一個電話，請先合併兩個紀錄。",
  not_authorized: "只有店主可以發送登入資料。",
  target_wrong_tenant: "此客戶不屬於你嘅店舖。",
  missing_authorization: "登入逾時，請重新登入再試",
  invalid_token: "登入逾時，請重新登入再試",
};
export async function sendCustomerLogin({ tenantId, userId }) {
  const body = { tenantId, userId };
  let data;
  if (_isNative()) {
    try { data = await rawInvoke("send-customer-login", body); }
    catch (e) { throw new Error(_SEND_LOGIN_ERR[e?.message] || e?.message || "發送失敗"); }
  } else {
    const res = await supabase.functions.invoke("send-customer-login", { body });
    if (res.error) {
      let code = null;
      try { code = (await res.error.context?.clone?.().json())?.error; } catch { /* not json */ }
      throw new Error(_SEND_LOGIN_ERR[code] || code || res.error.message || "發送失敗");
    }
    data = res.data;
  }
  if (!data?.ok) throw new Error(_SEND_LOGIN_ERR[data?.error] || data?.error || "發送失敗");
  return data;
}

// ── Multi-shop customer auth (synthetic email scoped by shop slug) ─────────

// Same phone can register at multiple shops; we encode (phone + slug) into a
// synthetic email so Supabase auth's global email-unique constraint doesn't
// collide. Customer never sees this email.
export function customerSyntheticEmail(phone, slug) {
  const digits = (phone || "").replace(/\D+/g, "");
  return `${digits}.${slug}@petos.local`;
}

// Customer login at a shop. Resolves phone+slug to synthetic email and
// calls Supabase auth from the browser so the session is set in this tab.
export async function customerSignIn({ phone, password, slug }) {
  const email = customerSyntheticEmail(phone, slug);
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

// Customer claims a walk-in account created by the shop. Sets their password
// and clears the pending_claim flag, then signs them in.
export async function claimCustomerAccount({ tenantId, tenantSlug, phone, password }) {
  const { data, error } = await supabase.functions.invoke("claim-customer-account", {
    body: { tenantId, tenantSlug, phone, password },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  // Auto sign-in now that the password is set
  await customerSignIn({ phone, password, slug: tenantSlug });
  return data;
}

// Customer phone OTP — the only customer-side login method going forward.
// Supabase delivers the SMS via the project's configured Twilio provider
// (platform-owned creds in Supabase Auth → Phone). Shops don't configure
// anything per-tenant.
export async function sendCustomerOtp(phone) {
  const { data, error } = await supabase.auth.signInWithOtp({ phone });
  if (error) throw error;
  return data;
}

export async function verifyCustomerOtp({ phone, code }) {
  const { data, error } = await supabase.auth.verifyOtp({ phone, token: code, type: "sms" });
  if (error) throw error;
  return data;
}

// Called once after verifyCustomerOtp succeeds. Atomically merges any walk-in
// profiles in this tenant sharing the same phone into the verified user,
// creates a customer_memberships row, and emails the shop owner.
// Idempotent: re-running finds no walk-ins and returns zero counts.
export async function linkPetsOnLogin({ slug, phone }) {
  const { data, error } = await supabase.functions.invoke("link-pets-on-login", {
    body: { slug, phone },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error + (data.detail ? ": " + data.detail : ""));
  return data;
}

// Idempotent membership upsert. Insert if missing, leave existing rows alone.
// Used by the PublicBooking flow so a Google customer of Shop B can book at
// Shop A without losing their Shop B membership / wallet / points.
export async function upsertCustomerMembership({ tenantId, displayName, phone }) {
  // Routes through SECURITY DEFINER RPC `self_upsert_customer_membership` which
  // forces user_id = auth.uid(). Direct table INSERT is no longer permitted by
  // RLS (would allow customers to self-join any tenant and harvest profiles).
  const { data, error } = await supabase.rpc("self_upsert_customer_membership", {
    p_tenant_id: tenantId,
    p_display_name: displayName || "",
    p_phone: phone || "",
  });
  if (error) throw error;
  return data;
}

// Customer leaves a shop (deletes their own customer_memberships row).
// Routes through SECURITY DEFINER RPC `self_leave_customer_membership` which
// forces user_id = auth.uid(); a direct client DELETE is RLS-blocked (same
// reason self_upsert is an RPC — stops customers touching other users' rows).
export async function leaveCustomerMembership({ tenantId }) {
  const { data, error } = await supabase.rpc("self_leave_customer_membership", {
    p_tenant_id: tenantId,
  });
  if (error) throw error;
  return data;
}

// Patch a customer's own membership row (wallet / points / wallet_tx /
// vouchers). RLS lets the user update only their own row (cm_update with
// user_id = auth.uid()). Used by the booking flow when the customer pays
// deposit / redeems points / consumes a voucher.
// Atomic wallet debit — calls the SECURITY DEFINER RPC so concurrent
// decrements stack correctly (the older patchCustomerMembership({wallet})
// pattern was last-write-wins and would silently restore a stale balance
// over a fresher server value). Use this for any path that subtracts
// from the customer's wallet, especially walk-in / settle / refund.
// `tx` is the wallet_tx entry to append: { id, type:"deduct", amount, note, date, aptId }.
export async function debitCustomerWallet({ userId, tenantId, amount, tx }) {
  const amt = Math.max(0, Math.floor(Number(amount) || 0));
  if (amt === 0) return { newWallet: null, newTxCount: 0 };
  const { data, error } = await callRpc("debit_customer_wallet", {
    p_user_id: userId,
    p_tenant_id: tenantId,
    p_amount: amt,
    p_tx: tx ? [tx] : [],
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return { newWallet: row?.new_wallet ?? null, newTxCount: row?.new_tx_count ?? 0 };
}

// Atomic wallet credit — mirror of debitCustomerWallet for owner-side
// top-ups (儲值). Old doTopup used patchCustomerMembership({ wallet:
// newAbsolute }) computed from a stale local snapshot — same last-write-
// wins clobber. credit_customer_wallet does `wallet = wallet + amount`
// + append-tx atomically.
export async function creditCustomerWallet({ userId, tenantId, amount, tx }) {
  const amt = Math.max(0, Math.floor(Number(amount) || 0));
  if (amt === 0) return { newWallet: null, newTxCount: 0 };
  const { data, error } = await callRpc("credit_customer_wallet", {
    p_user_id: userId,
    p_tenant_id: tenantId,
    p_amount: amt,
    p_tx: Array.isArray(tx) ? tx : (tx ? [tx] : []),
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return { newWallet: row?.new_wallet ?? null, newTxCount: row?.new_tx_count ?? 0 };
}

export async function patchCustomerMembership({ userId, tenantId, wallet, points, walletTx, tags, displayName, phone, vouchers, address, stamps }) {
  // Wallet is now ledger-backed: the balance + wallet_tx are maintained ONLY by
  // the wallet RPCs (credit/debit/settle/refund) and the shadow trigger. Refuse
  // to write them from this generic PATCH so it can't cause a last-write-wins
  // desync (that was a real bug source). Use credit/debitCustomerWallet instead.
  if (wallet !== undefined || walletTx !== undefined) {
    // eslint-disable-next-line no-console
    console.warn("[PettoBook] patchCustomerMembership: ignoring wallet/walletTx — route wallet changes through credit/debitCustomerWallet");
  }
  const row = stripUndef({
    points,
    tags,
    display_name: displayName,
    phone,
    vouchers,
    address,
    stamps,
  });
  if (Object.keys(row).length === 0) return null;
  // Native: raw PATCH. supabase-js .update() never settles on Android, and
  // dataSync AWAITS this, so a points/stamps change would hang the whole sync
  // chain behind it (see pushNotification). Two eq filters, same as the
  // supabase-js path below.
  if (_isNative()) {
    const arr = await rawWrite(
      "PATCH",
      `customer_memberships?user_id=eq.${encodeURIComponent(userId)}&tenant_id=eq.${encodeURIComponent(tenantId)}`,
      row,
      "return=representation",
    );
    return (arr && arr[0]) || null;
  }
  const { data, error } = await supabase
    .from("customer_memberships")
    .update(row)
    .eq("user_id", userId)
    .eq("tenant_id", tenantId)
    .select("*")
    .maybeSingle();
  if (error) throw error;
  return data;
}

// Owner/staff manual stamp-card adjustment. Upsert (not update) so a customer
// who has no membership row yet — e.g. a walk-in mirror — still gets one
// created instead of the write silently no-op'ing. RLS cm_insert/cm_update
// both allow is_staff_of(tenant_id). Returns the resulting membership row.
export async function setCustomerStamps({ userId, tenantId, stamps }) {
  const { data, error } = await supabase
    .from("customer_memberships")
    .upsert({ user_id: userId, tenant_id: tenantId, stamps: Math.max(0, Math.round(Number(stamps) || 0)) }, { onConflict: "user_id,tenant_id" })
    .select("*")
    .maybeSingle();
  if (error) throw error;
  return data ? mapMembership(data) : null;
}

// Multi-shop customer membership listing (Google-authenticated customer can
// be a customer of multiple shops). RLS allows the user to read their own
// rows.
export async function fetchCustomerMemberships(userId) {
  const { data, error } = await supabase
    .from("customer_memberships")
    .select("*")
    .eq("user_id", userId);
  if (error) throw error;
  return (data || []).map(mapMembership);
}

// Owner/staff-facing: every customer membership row for a shop. Used to
// hydrate state.memberships on the owner side so the CRM stamp-card / wallet /
// points views read real per-customer counts. RLS scopes this to the tenant.
export async function fetchTenantMemberships(tenantId) {
  const { data, error } = await supabase
    .from("customer_memberships")
    .select("*")
    .eq("tenant_id", tenantId);
  if (error) throw error;
  return (data || []).map(mapMembership);
}

// Shared row→state mapper for customer_memberships. Exported so the native
// raw-fetch (pgRest) path in dataLoader can reuse it.
export function mapMembership(r) {
  return {
    userId: r.user_id,
    tenantId: r.tenant_id,
    displayName: r.display_name || "",
    phone: r.phone || "",
    address: r.address || "", // saved 上門 address — prefilled on next booking
    wallet: Number(r.wallet) || 0,
    points: Number(r.points) || 0,
    // Stamp-card count is now a separate column. Customers' stamp UI
    // reads this; cash-back points live on `points`.
    stamps: Number(r.stamps) || 0,
    walletTx: Array.isArray(r.wallet_tx) ? r.wallet_tx : [],
    pointsTx: Array.isArray(r.points_tx) ? r.points_tx : [],
    tags: Array.isArray(r.tags) ? r.tags : [],
    referredBy: r.referred_by || null,
    pendingClaim: !!r.pending_claim,
    joinedAt: r.joined_at,
    vouchers: Array.isArray(r.vouchers) ? r.vouchers : [],
    blockedAt: r.blocked_at || null,
    blockedReason: r.blocked_reason || "",
  };
}

// Idempotent: provisions a Google-authenticated customer's profile (if first
// time) and joins them to a shop. Returns { profile, membership, tenant }.
export async function claimGoogleCustomer({ slug }) {
  const { data, error } = await supabase.functions.invoke("claim-google-customer", {
    body: { slug },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error + (data.detail ? ": " + data.detail : ""));
  return data;
}

// Shop staff adds a walk-in customer. Caller must be authenticated staff/owner.
// Creates auth user + profile (pending_claim=true).
export async function createWalkinCustomer({ tenantId, tenantSlug, name, phone }) {
  const body = { tenantId, tenantSlug, name, phone };
  // NATIVE: hardened raw invoke. supabase-js functions.invoke() wedges/hangs on
  // an aged iOS/Android WebView session (the same failure that pushed reads to
  // rawRead, writes to rawWrite and the WhatsApp send to rawInvoke) — which
  // broke "walk-in appointment with a NEW customer" on both native apps while
  // old-customer bookings (rawWrite path) kept working: the invoke hung
  // client-side, so the request never even reached the edge function.
  let data;
  if (_isNative()) {
    data = await rawInvoke("create-walkin-customer", body);
  } else {
    const res = await supabase.functions.invoke("create-walkin-customer", { body });
    if (res.error) throw res.error;
    data = res.data;
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

export async function insertReview(review) {
  const row = stripUndef({
    id: review.id,
    tenant_id: review.tenantId,
    appointment_id: review.aptId || null,
    customer_id: review.customerId,
    // "" on a uuid column is a hard 22P02 — a review of an appointment with no
    // assigned groomer used to 400 the same way bookings did.
    staff_id: review.staffId || null,
    rating: review.rating,
    comment: review.comment,
  });
  // Awaited by dataSync — see pushNotification: supabase-js hangs on Android.
  if (_isNative()) return mapReview(await rawUpsert("reviews", row));
  const { data, error } = await supabase.from("reviews").insert(row).select("*").maybeSingle();
  if (error) throw error;
  return mapReview(data);
}

export async function insertPayment(payment) {
  const row = stripUndef({
    tenant_id: payment.tenantId,
    appointment_id: payment.appointmentId,
    customer_id: payment.customerId,
    amount: payment.amount,
    method: payment.method,
    type: payment.type,
    reference: payment.reference,
    by_user: payment.byUser,
    meta: payment.meta,
  });
  const { data, error } = await supabase.from("payments").insert(row).select("*").maybeSingle();
  if (error) throw error;
  return data;
}

export async function logAudit(entry) {
  const row = stripUndef({
    tenant_id: entry.tenantId,
    user_id: entry.userId,
    action: entry.action,
    entity_type: entry.entityType,
    entity_id: entry.entityId,
    before: entry.before,
    after: entry.after,
    meta: entry.meta,
  });
  // Native uses the raw path. This one line was the whole of the owner's
  // "the record saves but 正在儲存… spins for 10s": dataSync AWAITS the audit
  // write on every appointment edit, and supabase-js does not resolve on
  // Android — so the booking committed in under a second and the sync chain
  // then sat here until the 15s watchdog gave up. The row was already on
  // other devices while the chip still said saving.
  if (_isNative()) { await rawWrite("POST", "audit_log", row, "return=minimal"); return; }
  const { error } = await supabase.from("audit_log").insert(row);
  if (error) throw error;
}

// ── Activity Center (Activity Logs) ───────────────────────────────────────

export async function logActivity({
  tenantId, userId, actionType, entityType, entityId, entityName,
  oldValue = null, newValue = null, description = "", severity = "info", details = null
}) {
  const row = stripUndef({
    tenant_id: tenantId,
    user_id: userId,
    action_type: actionType,
    entity_type: entityType,
    entity_id: entityId,
    entity_name: entityName,
    old_value: oldValue,
    new_value: newValue,
    description,
    severity,
    details,
  });
  const { error } = await supabase.from("activity_logs").insert(row);
  if (error) throw error;
}

export async function fetchActivityLogs(tenantId, { limit = 50, offset = 0 } = {}) {
  const { data, error } = await supabase
    .from("activity_logs")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1);
  if (error) throw error;
  return (data || []).map(r => ({
    id: r.id,
    tenantId: r.tenant_id,
    userId: r.user_id,
    actionType: r.action_type,
    entityType: r.entity_type,
    entityId: r.entity_id,
    entityName: r.entity_name,
    oldValue: r.old_value,
    newValue: r.new_value,
    description: r.description,
    severity: r.severity,
    details: r.details,
    createdAt: r.created_at,
  }));
}

export async function fetchActivityLogCount(tenantId) {
  const { count, error } = await supabase
    .from("activity_logs")
    .select("*", { count: "exact", head: true })
    .eq("tenant_id", tenantId);
  if (error) throw error;
  return count;
}

// ── Tenant media uploads (logos, banners) ──────────────────────────────────

// MIME → extension. Used in place of `file.name.split(".").pop()` which
// produced absurd extensions (e.g. a file literally named "logo" became
// "logo-1234.logo"). The bucket only stores images so this small table
// covers every realistic input.
const MIME_TO_EXT = {
  "image/jpeg": "jpg",
  "image/jpg":  "jpg",
  "image/png":  "png",
  "image/gif":  "gif",
  "image/webp": "webp",
  "image/heic": "heic",
  "image/heif": "heif",
  "image/svg+xml": "svg",
};
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024; // 5 MB — generous for logos/QR/photos.

export async function uploadTenantMedia(tenantId, file, type) {
  if (!file || !tenantId || !type) throw new Error("Missing required parameters");
  // Reject non-image uploads at the boundary so the bucket doesn't fill
  // with random PDFs. The browser sets `file.type` from sniffing, which
  // is reliable enough for this client-side gate; server-side validation
  // is layered in via the bucket's RLS in Supabase Storage.
  if (!file.type || !file.type.startsWith("image/")) {
    throw new Error("只接受圖片檔案 (JPG / PNG / WebP / HEIC)");
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error("檔案太大（上限 5 MB）");
  }
  const fileExt = MIME_TO_EXT[file.type] || (file.name.includes(".") ? file.name.split(".").pop().toLowerCase().replace(/[^a-z0-9]/g, "") : "jpg") || "jpg";
  const fileName = `${type}-${Date.now()}.${fileExt}`;
  const filePath = `${tenantId}/${fileName}`;

  if (_isNative()) {
    // tenant-media uses upsert:true, so we override the upsert header.
    const url = (import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "");
    const apikey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";
    let bearer; try { bearer = window.__petosAccessToken || apikey; } catch { bearer = apikey; }
    const buf = await file.arrayBuffer();
    const controller = new AbortController();
    const tid = setTimeout(() => controller.abort(), 90_000);
    try {
      const resp = await fetch(`${url}/storage/v1/object/tenant-media/${filePath}`, {
        method: "POST",
        signal: controller.signal,
        headers: {
          apikey,
          Authorization: `Bearer ${bearer}`,
          "Content-Type": file.type || "application/octet-stream",
          "x-upsert": "true",
        },
        body: buf,
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => "");
        throw new Error(`storage upload ${resp.status}: ${text.slice(0, 200)}`);
      }
    } finally {
      clearTimeout(tid);
    }
    return `${url}/storage/v1/object/public/tenant-media/${filePath}`;
  }

  const { error: uploadError } = await supabase.storage
    .from("tenant-media")
    .upload(filePath, file, { upsert: true, contentType: file.type });

  if (uploadError) throw uploadError;

  // Get public URL
  const { data } = supabase.storage.from("tenant-media").getPublicUrl(filePath);
  return data.publicUrl;
}

// Upload a grooming-report or pet-album photo to the public `grooming-media`
// bucket. `scope` is either `appointment-<id>` or `pet-<id>` so files are
// browsable in storage by what they belong to. Returns the public URL.
//
// Caller is expected to pre-resize the image (PhotoUpload does an 800px
// resize + JPEG re-encode client-side) so we don't waste bandwidth.
// Client-side image compression. iPhone shots are typically 12MP / 3-5 MB
// → resize to 1600px longest edge + JPEG q0.82 cuts that to ~150-300 KB,
// which saves 90%+ on Supabase storage egress and is more than enough
// resolution for avatars / before-after photos / health reports.
// Skips non-images (PDFs, videos, etc.) and bails out if the image is
// already smaller than the target on the longest axis.
async function compressImage(blob, { maxDim = 1600, quality = 0.82, type = "image/jpeg" } = {}) {
  if (!blob || !blob.type || !blob.type.startsWith("image/")) return blob;
  if (blob.type === "image/gif" || blob.type === "image/svg+xml") return blob;
  try {
    const bitmap = typeof createImageBitmap === "function"
      ? await createImageBitmap(blob)
      : await new Promise((resolve, reject) => {
          const url = URL.createObjectURL(blob);
          const img = new Image();
          img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
          img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("image decode failed")); };
          img.src = url;
        });
    const w = bitmap.width || bitmap.naturalWidth;
    const h = bitmap.height || bitmap.naturalHeight;
    if (!w || !h) return blob;
    if (w <= maxDim && h <= maxDim && blob.size < 400 * 1024) return blob;
    const scale = Math.min(1, maxDim / Math.max(w, h));
    const targetW = Math.round(w * scale);
    const targetH = Math.round(h * scale);
    const canvas = typeof OffscreenCanvas !== "undefined"
      ? new OffscreenCanvas(targetW, targetH)
      : Object.assign(document.createElement("canvas"), { width: targetW, height: targetH });
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    if (typeof bitmap.close === "function") bitmap.close();
    const out = canvas.convertToBlob
      ? await canvas.convertToBlob({ type, quality })
      : await new Promise(resolve => canvas.toBlob(resolve, type, quality));
    return out && out.size < blob.size ? out : blob;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[PettoBook] image compression failed, uploading raw:", e);
    return blob;
  }
}

// Native-only raw storage upload — bypasses supabase-js's Storage client
// because WKWebView / older Android WebView stall on streaming a large
// Blob as a POST body. Read the blob into an ArrayBuffer ourselves and
// fetch it directly with an `application/octet-stream`-ish content type
// (Supabase storage accepts any binary as long as Content-Type is set).
async function rawStorageUpload(bucket, path, blob) {
  const url = (import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "");
  const apikey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";
  let bearer;
  try { bearer = window.__petosAccessToken || apikey; } catch { bearer = apikey; }
  const buf = await blob.arrayBuffer();
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), 90_000);
  try {
    const resp = await fetch(`${url}/storage/v1/object/${bucket}/${path}`, {
      method: "POST",
      signal: controller.signal,
      headers: {
        apikey,
        Authorization: `Bearer ${bearer}`,
        "Content-Type": blob.type || "image/jpeg",
        "x-upsert": "false",
      },
      body: buf,
    });
    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new Error(`storage upload ${resp.status}: ${text.slice(0, 200)}`);
    }
  } finally {
    clearTimeout(tid);
  }
}

export async function uploadGroomingMedia({ tenantId, blob, scope, skipCompress }) {
  if (!tenantId || !blob) throw new Error("Missing required parameters");
  // Grooming photos: 1600px longest edge, JPEG quality 0.85 (owner request —
  // sharper before/after shots; ~larger files but still well within limits).
  const compressed = skipCompress ? blob : await compressImage(blob, { maxDim: 1600, quality: 0.85 });
  const folder = scope ? `${tenantId}/${scope}` : `${tenantId}/misc`;
  const fileName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
  const filePath = `${folder}/${fileName}`;
  if (_isNative()) {
    await rawStorageUpload("grooming-media", filePath, compressed);
  } else {
    const { error } = await supabase.storage
      .from("grooming-media")
      .upload(filePath, compressed, { upsert: false, contentType: compressed.type || "image/jpeg" });
    if (error) throw error;
  }
  const publicUrl = `${(import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "")}/storage/v1/object/public/grooming-media/${filePath}`;
  return { url: publicUrl, path: filePath, bytesIn: blob.size, bytesOut: compressed.size };
}

// ──────────────────────────────────────────────────────────────────────────
// TAX HELPER
// Computes the tax owed on a subtotal given a tenant's tax config. Honors
// inclusive vs exclusive modes:
//   - "exclusive": subtotal × rate added on top
//   - "inclusive": prices already include tax; the breakout = subtotal × r/(1+r)
// Returns { taxAmount, totalIncTax, subtotalExclTax } in tenant currency units.
// Used by both the booking confirmation UI and the receipt printer.
// ──────────────────────────────────────────────────────────────────────────
export function calcTax(subtotal, tenant) {
  const tc = tenant?.taxConfig || { enabled: false, rate: 0, mode: "exclusive" };
  const amount = Math.max(0, Number(subtotal) || 0);
  if (!tc.enabled || !tc.rate) return { taxAmount: 0, totalIncTax: amount, subtotalExclTax: amount };
  const rate = Number(tc.rate) / 100;
  if (tc.mode === "inclusive") {
    const taxAmount = Math.round((amount * rate) / (1 + rate));
    return { taxAmount, totalIncTax: amount, subtotalExclTax: amount - taxAmount };
  }
  // exclusive
  const taxAmount = Math.round(amount * rate);
  return { taxAmount, totalIncTax: amount + taxAmount, subtotalExclTax: amount };
}

// ──────────────────────────────────────────────────────────────────────────
// PROMO CODES
// ──────────────────────────────────────────────────────────────────────────
const mapPromoCode = (r) => r && ({
  id: r.id,
  tenantId: r.tenant_id,
  code: r.code,
  description: r.description || "",
  discountType: r.discount_type || "percent",
  discountValue: Number(r.discount_value) || 0,
  maxDiscount: r.max_discount,
  maxUses: r.max_uses,
  usedCount: Number(r.used_count) || 0,
  perCustomerLimit: r.per_customer_limit,
  validFrom: r.valid_from,
  validTo: r.valid_to,
  newCustomersOnly: !!r.new_customers_only,
  minSubtotal: Number(r.min_subtotal) || 0,
  enabled: r.enabled !== false,
  createdAt: r.created_at,
});

export async function fetchPromoCodes(tenantId) {
  const { data, error } = await supabase.from("promo_codes").select("*").eq("tenant_id", tenantId).order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(mapPromoCode);
}

export async function upsertPromoCode(p) {
  const row = stripUndef({
    id: p.id,
    tenant_id: p.tenantId,
    code: (p.code || "").toUpperCase().trim(),
    description: p.description,
    discount_type: p.discountType,
    discount_value: p.discountValue,
    max_discount: p.maxDiscount,
    max_uses: p.maxUses,
    per_customer_limit: p.perCustomerLimit,
    valid_from: p.validFrom,
    valid_to: p.validTo,
    new_customers_only: p.newCustomersOnly,
    min_subtotal: p.minSubtotal,
    enabled: p.enabled,
  });
  const { data, error } = await supabase.from("promo_codes").upsert(row).select("*").maybeSingle();
  if (error) throw error;
  return mapPromoCode(data);
}

export async function deletePromoCode(id) {
  const { error } = await supabase.from("promo_codes").delete().eq("id", id);
  if (error) throw error;
}

// Validate a promo code at the booking step. Returns:
//   { valid: true, promo, discount, message } — applies
//   { valid: false, reason }                  — does not apply
// `subtotal` is the order's pre-tax/pre-discount value. `isReturning` is
// true when the customer has prior appointments at this tenant.
export async function validatePromoCode({ tenantId, code, subtotal = 0, isReturning = false, customerId = null }) {
  const cleaned = (code || "").trim().toUpperCase();
  if (!cleaned) return { valid: false, reason: "empty" };
  const { data, error } = await supabase
    .from("promo_codes")
    .select("*")
    .eq("tenant_id", tenantId)
    .eq("code", cleaned)
    .eq("enabled", true)
    .maybeSingle();
  if (error) throw error;
  if (!data) return { valid: false, reason: "not_found" };
  const promo = mapPromoCode(data);
  // Local-date (not toISOString — UTC slips a day for HKT 00:00-08:00).
  const today = (() => {
    const d = new Date();
    return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
  })();
  if (promo.validFrom && today < promo.validFrom) return { valid: false, reason: "not_yet_active" };
  if (promo.validTo && today > promo.validTo) return { valid: false, reason: "expired" };
  if (promo.maxUses != null && promo.usedCount >= promo.maxUses) return { valid: false, reason: "exhausted" };
  if (promo.newCustomersOnly && isReturning) return { valid: false, reason: "new_customers_only" };
  if (promo.minSubtotal && subtotal < promo.minSubtotal) return { valid: false, reason: "min_subtotal", minSubtotal: promo.minSubtotal };
  // Per-customer limit
  if (customerId && promo.perCustomerLimit) {
    const { count } = await supabase
      .from("promo_redemptions").select("id", { count: "exact", head: true })
      .eq("promo_code_id", promo.id).eq("customer_id", customerId);
    if ((count || 0) >= promo.perCustomerLimit) return { valid: false, reason: "per_customer_limit" };
  }
  // Compute discount
  let discount = 0;
  if (promo.discountType === "percent") {
    discount = Math.round(subtotal * (promo.discountValue / 100));
    if (promo.maxDiscount) discount = Math.min(discount, promo.maxDiscount);
  } else {
    discount = Math.min(promo.discountValue, subtotal);
  }
  return { valid: true, promo, discount };
}

export async function redeemPromoCode({ tenantId, promoCodeId, customerId, appointmentId, amount }) {
  const { error } = await supabase.from("promo_redemptions").insert({
    tenant_id: tenantId,
    promo_code_id: promoCodeId,
    customer_id: customerId,
    appointment_id: appointmentId,
    amount,
  });
  if (error) throw error;
  // Increment used_count atomically — use an RPC for strict atomicity.
  // Previously this was .catch(() => {}) which silently swallowed all
  // failures; Phase 1.1 of the stability plan removes those swallowers.
  // If the RPC genuinely isn't deployed (PGRST202 "function not found"),
  // skip silently — otherwise log + console.warn so the divergence is
  // visible. The redemption row itself was already inserted above, so
  // the user-visible effect (promo applied to apt) succeeded; only the
  // counter is at risk of drift.
  try {
    const { error } = await supabase.rpc("increment_promo_used", { p_id: promoCodeId });
    if (error && !/function .* does not exist|PGRST202/i.test(error.message || "")) {
      console.warn("[PettoBook] increment_promo_used failed:", error.message);
    }
  } catch (e) {
    console.warn("[PettoBook] increment_promo_used threw:", e?.message || e);
  }
}

// ──────────────────────────────────────────────────────────────────────────
// SERVICE PACKAGES (multi-visit passes)
// ──────────────────────────────────────────────────────────────────────────
const mapServicePackage = (r) => r && ({
  id: r.id,
  tenantId: r.tenant_id,
  name: r.name,
  description: r.description || "",
  serviceIds: Array.isArray(r.service_ids) ? r.service_ids : [],
  // visits remains for backward compat — equals paidVisits + bonusVisits.
  visits: Number(r.visits) || 1,
  paidVisits: Number(r.paid_visits ?? r.visits) || 1,
  bonusVisits: Number(r.bonus_visits) || 0,
  // pricing: { mode: "flat" | "perSize", perSize?: { [sizeLabel]: { shortHair, longHair } } }
  pricing: r.pricing && typeof r.pricing === "object" ? r.pricing : { mode: "flat" },
  price: Number(r.price) || 0,
  validityDays: Number(r.validity_days) || 365,
  enabled: r.enabled !== false,
  coversSpas: !!r.covers_spas,
  coversAddons: !!r.covers_addons,
  lowBalanceThreshold: Number(r.low_balance_threshold) || 2,
});

export async function fetchServicePackages(tenantId) {
  const { data, error } = await supabase.from("service_packages").select("*").eq("tenant_id", tenantId).order("created_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(mapServicePackage);
}

export async function upsertServicePackage(p) {
  const paid = Number(p.paidVisits ?? p.visits) || 1;
  const bonus = Number(p.bonusVisits) || 0;
  const row = stripUndef({
    id: p.id,
    tenant_id: p.tenantId,
    name: p.name,
    description: p.description,
    service_ids: p.serviceIds,
    visits: paid + bonus,           // legacy column = total visits granted
    paid_visits: paid,
    bonus_visits: bonus,
    pricing: p.pricing || { mode: "flat" },
    price: p.price,
    validity_days: p.validityDays,
    enabled: p.enabled,
    covers_spas: !!p.coversSpas,
    covers_addons: !!p.coversAddons,
    low_balance_threshold: Number(p.lowBalanceThreshold) || 2,
  });
  const { data, error } = await supabase.from("service_packages").upsert(row).select("*").maybeSingle();
  if (error) throw error;
  return mapServicePackage(data);
}

export async function deleteServicePackage(id) {
  const { error } = await supabase.from("service_packages").delete().eq("id", id);
  if (error) throw error;
}

// Customer's purchased packages (for redemption at booking time)
const mapCustomerPackage = (r) => r && ({
  id: r.id,
  tenantId: r.tenant_id,
  customerId: r.customer_id,
  packageId: r.package_id,
  // Pet binding — NULL means "any pet" (legacy). New sells from the
  // UI always set petId. Snapshot fields are display-only.
  petId: r.pet_id || null,
  petBreedAtPurchase: r.pet_breed_at_purchase || null,
  petWeightAtPurchase: Number(r.pet_weight_at_purchase) || null,
  petCoatAtPurchase: r.pet_coat_at_purchase || null,
  remainingVisits: Number(r.remaining_visits) || 0,
  totalVisits: Number(r.total_visits ?? r.remaining_visits) || 0,
  expiresAt: r.expires_at,
  purchasedAt: r.purchased_at,
  enabled: r.enabled !== false,
  amountPaid: Number(r.amount_paid) || 0,
  paymentMethod: r.payment_method || null,
  soldByUserId: r.sold_by_user_id || null,
  isGift: !!r.is_gift,
  usageLog: Array.isArray(r.usage_log) ? r.usage_log : [],
  refundedAt: r.refunded_at,
  refundAmount: r.refund_amount,
  refundReason: r.refund_reason,
});

export async function fetchCustomerPackages(customerId, tenantId) {
  let q = supabase.from("customer_packages").select("*").eq("customer_id", customerId).eq("enabled", true);
  if (tenantId) q = q.eq("tenant_id", tenantId);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map(mapCustomerPackage);
}

// Owner-side: every customer_packages row for the tenant (including refunded
// / spent rows) so analytics can total sales + visits used.
export async function fetchTenantCustomerPackages(tenantId) {
  const { data, error } = await supabase
    .from("customer_packages")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("purchased_at", { ascending: false });
  if (error) throw error;
  return (data || []).map(mapCustomerPackage);
}

export async function purchaseCustomerPackage({ tenantId, customerId, packageId, validityDays, visits, appointmentId, amountPaid = 0, paymentMethod = null, soldByUserId = null, isGift = false, petId = null, petBreed = null, petWeight = null, petCoat = null }) {
  // setDate handles DST transitions correctly (ms math drops/adds an
  // hour at the spring/autumn switch in TZs that use DST).
  const expires = validityDays > 0
    ? (() => { const d = new Date(); d.setDate(d.getDate() + Number(validityDays)); return d.toISOString(); })()
    : null;
  const { data, error } = await supabase.from("customer_packages").insert({
    tenant_id: tenantId,
    customer_id: customerId,
    package_id: packageId,
    pet_id: petId || null,
    pet_breed_at_purchase: petBreed || null,
    pet_weight_at_purchase: petWeight || null,
    pet_coat_at_purchase: petCoat || null,
    remaining_visits: visits,
    total_visits: visits,
    expires_at: expires,
    purchase_appointment_id: appointmentId || null,
    amount_paid: amountPaid,
    payment_method: paymentMethod,
    sold_by_user_id: soldByUserId,
    is_gift: !!isGift,
  }).select("*").maybeSingle();
  if (error) throw error;
  return mapCustomerPackage(data);
}

// Decrement a customer package by 1 visit via SECURITY DEFINER RPC.
// SELECT … FOR UPDATE + dedupe-by-aptId happen in a single TX so two
// concurrent done-status flips can't both decrement.
export async function redeemCustomerPackage(customerPackageId, { appointmentId = null, sizeLabel = "", coatLength = "", serviceId = null } = {}) {
  if (!customerPackageId || !appointmentId) throw new Error("redeemCustomerPackage requires customerPackageId + appointmentId");
  const { data, error } = await callRpc("redeem_package_atomic", {
    p_customer_package_id: customerPackageId,
    p_apt_id: appointmentId,
    p_size_label: sizeLabel || "",
    p_coat_length: coatLength || "",
    p_service_id: serviceId || null,
  });
  if (error) throw error;
  return data || {};
}

// Owner reverses a deduction (e.g. apt completed in error then re-opened).
export async function restoreCustomerPackageVisit(customerPackageId, { appointmentId = null } = {}) {
  if (!customerPackageId || !appointmentId) throw new Error("restoreCustomerPackageVisit requires customerPackageId + appointmentId");
  const { data, error } = await callRpc("restore_package_visit_atomic", {
    p_customer_package_id: customerPackageId,
    p_apt_id: appointmentId,
  });
  if (error) throw error;
  return data || {};
}

// Idempotent loyalty awarder — adds the 1% cash points + 1 stamp + any
// triggered milestone vouchers for a completed appointment. The server
// stamps `appointments.points_awarded_at / stamp_awarded_at` so a second
// call is a no-op. Returns the new membership balances.
export async function awardLoyaltyForApt(appointmentId) {
  if (!appointmentId) return null;
  const { data, error } = await callRpc("award_loyalty_for_apt", { p_apt_id: appointmentId });
  if (error) throw error;
  return data || null;
}

// Reverse the stamp + points + uncirculated vouchers awarded for an
// appointment. Used when owner reverts a `done` status (e.g. mis-click).
export async function revokeLoyaltyForApt(appointmentId) {
  if (!appointmentId) return null;
  const { data, error } = await callRpc("revoke_loyalty_for_apt", { p_apt_id: appointmentId });
  if (error) throw error;
  return data || null;
}

// ── Google Calendar integration ────────────────────────────────────────
// Per-staff OAuth: each member who wants real-time GCal sync clicks
// "Connect Google Calendar" → goes through Google's consent screen →
// refresh_token saved on profiles. Subsequent apt changes call
// syncAppointmentToGoogle (fire-and-forget) which pushes the change to
// every affected staff's primary calendar.
const SB_FUNCTIONS_BASE = (() => {
  try {
    // Same project URL the supabase client uses, swap to /functions/v1.
    const u = new URL(supabase.supabaseUrl);
    return u.protocol + "//" + u.host + "/functions/v1";
  } catch { return ""; }
})();

export async function getGoogleAuthorizeUrl(returnTo) {
  const { data: { session } } = await supabase.auth.getSession();
  const jwt = session?.access_token;
  if (!jwt) throw new Error("not signed in");
  const url = SB_FUNCTIONS_BASE + "/google-calendar-oauth?action=authorize"
    + (returnTo ? "&return_to=" + encodeURIComponent(returnTo) : "");
  const res = await fetch(url, { headers: { Authorization: "Bearer " + jwt } });
  if (!res.ok) throw new Error("authorize_url request failed: " + res.status);
  const j = await res.json();
  return j.authorize_url || null;
}

export async function disconnectGoogleCalendar(staffId) {
  const { data, error } = await supabase.rpc("disconnect_google_calendar", { p_staff_id: staffId });
  if (error) throw error;
  return data;
}

// Fire-and-forget push of an apt to Google for every staff that has
// sync enabled. Called by the client after every successful apt
// create / update / cancel. We do NOT await this — sync failures are
// recoverable on the next mutation and shouldn't block the user.
export function syncAppointmentToGoogle(appointmentId, staffId) {
  if (!appointmentId) return;
  (async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const jwt = session?.access_token;
      if (!jwt) return;
      await fetch(SB_FUNCTIONS_BASE + "/google-calendar-sync", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + jwt },
        body: JSON.stringify({ appointment_id: appointmentId, staff_id: staffId || null }),
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[PettoBook] gcal sync skipped:", e?.message || e);
    }
  })();
}

// Append a payment_log entry atomically. Solves the multi-device race
// where two clients each compute from their own prevState snapshot and
// the debounced dataSync upsert overwrites the other's entry. The RPC
// is idempotent on entry.id so a double-tap can't double-charge.
// Returns { log, collected, total, paid } on success or
// { skipped: 'duplicate', ... } if the entry was already recorded.
export async function recordPaymentAtomic(appointmentId, entry) {
  if (!appointmentId || !entry) throw new Error("recordPaymentAtomic requires appointmentId + entry");
  const { data, error } = await callRpc("record_payment_atomic", {
    p_apt_id: appointmentId,
    p_entry: entry,
  });
  if (error) throw error;
  return data;
}

// Owner-side manual adjustment. delta can be positive (refund / gift)
// or negative (extra service consumed in the same visit). Appends a
// usage_log entry so audit trail survives.
export async function adjustCustomerPackageVisit(customerPackageId, delta, reason = "") {
  if (!customerPackageId) throw new Error("adjustCustomerPackageVisit requires customerPackageId");
  if (!Number.isFinite(delta) || delta === 0) return null;
  const { data, error } = await callRpc("adjust_package_visit_atomic", {
    p_cp_id: customerPackageId, p_delta: Math.trunc(delta), p_reason: reason || null,
  });
  if (error) throw error;
  return data || null;
}

export async function extendCustomerPackage(customerPackageId, { addDays = 0, newExpiresAt = null } = {}) {
  let expires = newExpiresAt;
  if (!expires && addDays) {
    const { data: cur } = await supabase.from("customer_packages").select("expires_at").eq("id", customerPackageId).maybeSingle();
    // setDate handles month + DST boundaries; using Date math (`+ ms`)
    // produces a one-hour-shifted timestamp across the spring/autumn
    // DST switch for any locale that uses it.
    const baseDate = cur?.expires_at ? new Date(cur.expires_at) : new Date();
    baseDate.setDate(baseDate.getDate() + Number(addDays || 0));
    expires = baseDate.toISOString();
  }
  if (!expires) return;
  const { error } = await supabase
    .from("customer_packages")
    .update({ expires_at: expires, enabled: true })
    .eq("id", customerPackageId);
  if (error) throw error;
}

export async function refundCustomerPackage(customerPackageId, { amount = 0, reason = "" } = {}) {
  const { error } = await supabase
    .from("customer_packages")
    .update({ refunded_at: new Date().toISOString(), refund_amount: amount, refund_reason: reason, enabled: false })
    .eq("id", customerPackageId);
  if (error) throw error;
}

// ──────────────────────────────────────────────────────────────────────────
// SMS + EMAIL (server-side, via Edge Functions)
// These are thin wrappers; the actual sending happens in Supabase Edge
// Functions so secret API keys never reach the browser.
// ──────────────────────────────────────────────────────────────────────────
export async function sendSms({ tenantId, to, text, appointmentId, userId }) {
  const { data, error } = await supabase.functions.invoke("send-sms", {
    body: { tenantId, to, text, appointmentId, userId },
  });
  if (error) throw error;
  return data;
}

export async function sendEmail({ tenantId, to, subject, html, text, appointmentId, userId }) {
  const { data, error } = await supabase.functions.invoke("send-email", {
    body: { tenantId, to, subject, html, text, appointmentId, userId },
  });
  if (error) throw error;
  return data;
}

// ──────────────────────────────────────────────────────────────────────────
// STRIPE (deposit collection at booking time)
// `createStripeCheckout` builds a Checkout Session on the server (so the
// secret key never hits the browser) and returns a URL the client redirects
// to. After payment, Stripe redirects back to the booking URL with
// `?stripe=success&session_id=...` which the booking page recognizes.
// ──────────────────────────────────────────────────────────────────────────
export async function createStripeCheckout({ tenantId, amount, currency, appointmentId, customerEmail, description, returnUrl }) {
  const { data, error } = await supabase.functions.invoke("create-stripe-checkout", {
    body: { tenantId, amount, currency, appointmentId, customerEmail, description, returnUrl },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data; // { url, sessionId }
}

// Platform subscription billing — opens a Stripe Checkout in `subscription`
// mode for the given tenant + plan ("basic" | "pro"). The webhook
// (stripe-webhook) flips tenants.plan + tenants.subscription on success.
export async function createSubscriptionCheckout({ tenantId, plan, returnUrl, customerEmail, expectedAmount }) {
  // `expectedAmount` is the price the shop was actually SHOWN. The edge
  // function refuses the session if its own table disagrees, so a drift
  // between the two fails the checkout instead of quietly charging a live
  // card the wrong amount — which is what nearly shipped: 基本版 advertised
  // at $68 was wired to bill $299.
  const { data, error } = await supabase.functions.invoke("create-subscription-checkout", {
    body: { tenantId, plan, returnUrl, customerEmail, expectedAmount },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data; // { url, sessionId }
}

// Stripe Customer Portal — owner self-services their subscription:
// switch plans, update card, view invoices, cancel. Returns a short-
// lived URL we open in a new tab / browser window.
export async function createStripePortalSession({ tenantId, returnUrl }) {
  const { data, error } = await supabase.functions.invoke("create-stripe-portal-session", {
    body: { tenantId, returnUrl },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data; // { url }
}

// ──────────────────────────────────────────────────────────────────────────
// PRIVACY COMPLIANCE — HK PDPO + TW PDPA helpers
// ──────────────────────────────────────────────────────────────────────────

// Record explicit consent for a specific purpose (booking_terms, marketing_*,
// photo_sharing, etc.). Called from the registration/booking flows.
export async function recordConsent({ tenantId, purpose, granted = true, version = null, context = null }) {
  const userRes = { user: { id: await _currentUserId() } };
  const userId = userRes?.user?.id;
  if (!userId) throw new Error("must be authenticated to record consent");
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : null;
  const { error } = await supabase.from("consent_logs").insert({
    tenant_id: tenantId || null,
    user_id: userId,
    purpose,
    granted,
    version,
    user_agent: ua,
    context: context || {},
  });
  if (error) throw error;
}

// Download a JSON dump of all PII the platform holds about the caller.
// Triggers a browser download — returns the parsed payload too if needed.
export async function exportMyData() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("must be authenticated");
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-customer-data`;
  const resp = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (!resp.ok) throw new Error("Export failed: " + resp.status);
  const text = await resp.text();
  const blob = new Blob([text], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `petos-data-${Date.now()}.json`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  return JSON.parse(text);
}

// Request right-to-erasure. Returns the request id; deletion is immediate.
export async function requestDataDeletion({ reason } = {}) {
  const { data, error } = await supabase.functions.invoke("delete-customer-data", {
    body: { reason: reason || null },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

// Shop owner / staff account deletion. Records a pending request (processed
// by the admin within 30 days) — shops can't be erased instantly because
// staff, customers and tax records depend on them. `check: true` only reads
// the current pending request.
export async function requestAccountDeletion({ reason, check } = {}) {
  const { data, error } = await supabase.functions.invoke("request-account-deletion", {
    body: { reason: reason || null, check: !!check },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

// Shop-owner AI assistant (read-only answer + find). Posts the chat history to
// the shop-assistant edge function and returns { reply }. Native-safe bearer
// (window.__petosAccessToken) so it works on aged iOS sessions too.
export async function askShopAssistant({ tenantId, messages }) {
  const url = `${(import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "")}/functions/v1/shop-assistant`;
  let token = null;
  try { token = (typeof window !== "undefined" && window.__petosAccessToken) || null; } catch { /* ignore */ }
  if (!token) { const { data: { session } } = await supabase.auth.getSession(); token = session?.access_token; }
  if (!token) throw new Error("must be authenticated");
  const resp = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ tenantId, messages }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) { const e = new Error(data?.error || ("assistant " + resp.status)); e.locked = !!data?.locked; throw e; }
  return data;
}

// Short-lived signed URL for a claim's business-registration document.
// The bucket is private and superadmin-only; the URL is minted per view and
// expires, so the document never becomes a shareable link.
export async function claimDocUrl(path, seconds = 300) {
  if (!path) return null;
  const { data, error } = await supabase.storage
    .from("shop-claim-docs").createSignedUrl(path, seconds);
  if (error) throw error;
  return data?.signedUrl || null;
}

// ── Shop onboarding funnel + follow-up (superadmin) ───────────────────────
// Why this exists: shops were signing up, stalling at a specific step and
// leaving without ever opening a support ticket. `shop_funnel()` is the
// SECURITY DEFINER RPC that returns where every shop actually got stuck;
// `shop_followups` records what we sent them so the same shop isn't chased
// twice. Both are gated on is_superadmin() in the database, not just here.
export async function fetchShopFunnel() {
  const { data, error } = await supabase.rpc("shop_funnel");
  if (error) throw error;
  return (data || []).map(r => ({
    tenantId: r.tenant_id,
    shopName: r.shop_name,
    bookingSlug: r.booking_slug,
    plan: r.plan,
    createdAt: r.created_at,
    ownerId: r.owner_id,
    ownerEmail: r.owner_email,
    ownerName: r.owner_name,
    phone: r.phone,
    servicesCount: Number(r.services_count) || 0,
    pricedCount: Number(r.priced_count) || 0,
    customersCount: Number(r.customers_count) || 0,
    petsCount: Number(r.pets_count) || 0,
    aptsCount: Number(r.apts_count) || 0,
    lastAptCreated: r.last_apt_created,
    lastActivity: r.last_activity,
    followupsCount: Number(r.followups_count) || 0,
    lastFollowupAt: r.last_followup_at,
  }));
}

export async function fetchShopFollowups(tenantId) {
  const { data, error } = await supabase
    .from("shop_followups").select("*")
    .eq("tenant_id", tenantId).order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function logShopFollowup(row) {
  const { data, error } = await supabase.from("shop_followups").insert(stripUndef({
    tenant_id: row.tenantId,
    stage: row.stage,
    channel: row.channel,
    subject: row.subject,
    body: row.body,
    sent_to: row.sentTo,
    sent_by: row.sentBy,
    email_ok: row.emailOk,
    inapp_ok: row.inappOk,
    error: row.error,
  })).select("*").maybeSingle();
  if (error) throw error;
  return data;
}

// ── Staff deductions ───────────────────────────────────────────────────────
const mapDeduction = (r) => r && ({
  id: r.id,
  tenantId: r.tenant_id,
  staffId: r.staff_id,
  date: r.date,
  kind: r.kind,
  amount: Number(r.amount) || 0,
  note: r.note || "",
  createdBy: r.created_by,
  createdAt: r.created_at,
});

export async function fetchStaffDeductions(tenantId, { from, to, staffId } = {}) {
  // Raw path on web AND native — supabase-js .from() never settles on Android.
  let q = `staff_deductions?tenant_id=eq.${encodeURIComponent(tenantId)}&select=*&order=date.desc`;
  if (from) q += `&date=gte.${encodeURIComponent(from)}`;
  if (to) q += `&date=lte.${encodeURIComponent(to)}`;
  if (staffId) q += `&staff_id=eq.${encodeURIComponent(staffId)}`;
  return (await rawRead(q)).map(mapDeduction);
}

export async function addStaffDeduction({ tenantId, staffId, date, kind, amount, note, createdBy }) {
  const arr = await rawWrite("POST", "staff_deductions",
    [{ tenant_id: tenantId, staff_id: staffId, date, kind, amount, note, created_by: createdBy }], "return=representation");
  return mapDeduction((arr && arr[0]) || {});
}

export async function deleteStaffDeduction(id) {
  await rawWrite("DELETE", `staff_deductions?id=eq.${encodeURIComponent(id)}`, null);
}

// ── Staff pay (owner-only tables, 2026-09-28) ─────────────────────────────
// Pay moved off `profiles` so colleagues can't read it: staff_pay_settings,
// staff_wage_entries (one row per work day) and staff_payouts (add-only,
// void-with-reason, DB blocks overlapping salary payouts). The mapped shapes
// keep the field names the screens already used (baseSalary…, wageLog rows
// {date,hours,amount,note,at}, payoutLog rows {from,to,paidAt,amount,…}).
const mapPaySettings = (r) => ({
  baseSalary: Number(r.base_salary) || 0,
  hourlyRate: Number(r.hourly_rate) || 0,
  commissionEnabled: r.commission_enabled !== false,
  commissionMode: r.commission_mode || "percent",
  commissionRate: Number(r.commission_rate) || 0,
  commissionTiers: Array.isArray(r.commission_tiers) ? r.commission_tiers : [],
  serviceCommissionRates: (r.service_commission_rates && typeof r.service_commission_rates === "object") ? r.service_commission_rates : {},
  packageCommissionRate: Number(r.package_commission_rate) || 0,
  packageRedemptionCommissionRate: Number(r.package_redemption_commission_rate) || 0,
  settleMode: r.settle_mode || "daily",
  mpfEnabled: !!r.mpf_enabled,
  rateChangeLog: Array.isArray(r.change_log) ? r.change_log : [],
});
const mapWageEntry = (r) => ({
  id: r.id, staffId: r.staff_id, date: r.work_date,
  hours: Number(r.hours) || 0, amount: Number(r.amount) || 0, note: r.note || "", at: r.updated_at || r.created_at,
  verified: !!r.verified, paidMethod: r.paid_method || "",
});
const mapPayout = (r) => ({
  id: r.id, staffId: r.staff_id, kind: r.kind || "salary",
  from: r.period_from, to: r.period_to, paidAt: r.paid_on,
  amount: Number(r.amount) || 0, method: r.method, note: r.note || "",
  breakdown: r.breakdown || null, at: r.created_at,
  voidedAt: r.voided_at || null, voidReason: r.void_reason || "",
});

/** Everything the payroll screens need for one shop: { settings: {staffId: …}, wages: [], payouts: [] }. */
export async function fetchStaffPay(tenantId) {
  const t = encodeURIComponent(tenantId);
  const [settings, wages, payouts] = await Promise.all([
    rawRead(`staff_pay_settings?tenant_id=eq.${t}&select=*`),
    rawRead(`staff_wage_entries?tenant_id=eq.${t}&select=*&order=work_date.desc`),
    rawRead(`staff_payouts?tenant_id=eq.${t}&select=*&order=period_from.desc`),
  ]);
  const byStaff = {};
  for (const r of settings) byStaff[r.staff_id] = mapPaySettings(r);
  return { settings: byStaff, wages: wages.map(mapWageEntry), payouts: payouts.map(mapPayout) };
}

export async function upsertStaffPaySettings({ tenantId, staffId, settings }) {
  const s = settings || {};
  const row = stripUndef({
    staff_id: staffId, tenant_id: tenantId,
    base_salary: s.baseSalary, hourly_rate: s.hourlyRate,
    commission_enabled: s.commissionEnabled, commission_mode: s.commissionMode,
    commission_rate: s.commissionRate, commission_tiers: s.commissionTiers,
    service_commission_rates: s.serviceCommissionRates,
    package_commission_rate: s.packageCommissionRate,
    package_redemption_commission_rate: s.packageRedemptionCommissionRate,
    settle_mode: s.settleMode, mpf_enabled: s.mpfEnabled,
    change_log: Array.isArray(s.rateChangeLog) ? s.rateChangeLog : undefined,
  });
  const arr = await rawWrite("POST", "staff_pay_settings?on_conflict=staff_id", [row], "resolution=merge-duplicates,return=representation");
  return mapPaySettings((arr && arr[0]) || row);
}

/** One wage entry per staff per day — saving the same day again replaces it. */
export async function upsertWageEntry({ tenantId, staffId, date, hours, amount, note, verified, paidMethod }) {
  const row = stripUndef({ tenant_id: tenantId, staff_id: staffId, work_date: date, hours: Number(hours) || 0, amount: Math.round(Number(amount) || 0), note: note || "",
    verified: verified === undefined ? undefined : !!verified, paid_method: paidMethod === undefined ? undefined : (paidMethod || "") });
  const arr = await rawWrite("POST", "staff_wage_entries?on_conflict=staff_id,work_date", [row], "resolution=merge-duplicates,return=representation");
  return mapWageEntry((arr && arr[0]) || row);
}

export async function deleteWageEntry({ staffId, date }) {
  await rawWrite("DELETE", `staff_wage_entries?staff_id=eq.${encodeURIComponent(staffId)}&work_date=eq.${encodeURIComponent(date)}`, null);
}

const _PAYOUT_ERR = (e) => {
  const m = String(e?.message || e || "");
  if (/staff_payouts_no_overlap|exclusion/i.test(m)) return new Error("呢段期間已經出過薪。如要補發，請用「補發 / 調整」並寫低原因。");
  if (/check constraint/i.test(m) && /note|kind/i.test(m)) return new Error("補發 / 調整需要填寫原因。");
  return e instanceof Error ? e : new Error(m || "出薪失敗");
};

/** Record a payment. `breakdown` = the computePay() result it paid (frozen). */
export async function recordPayout({ tenantId, staffId, kind = "salary", from, to, paidOn, amount, method, note, breakdown }) {
  try {
    const arr = await rawWrite("POST", "staff_payouts", [{
      tenant_id: tenantId, staff_id: staffId, kind, period_from: from, period_to: to, paid_on: paidOn,
      amount: Math.round(Number(amount) || 0), method, note: note || "", breakdown: breakdown || null,
    }], "return=representation");
    return mapPayout((arr && arr[0]) || {});
  } catch (e) { throw _PAYOUT_ERR(e); }
}

/** Payouts are never deleted — a void keeps the row, with who / when / why. */
export async function voidPayout(id, reason) {
  if (!String(reason || "").trim()) throw new Error("請填寫作廢原因");
  const arr = await rawWrite("PATCH", `staff_payouts?id=eq.${encodeURIComponent(id)}`,
    { voided_at: new Date().toISOString(), void_reason: String(reason).trim() }, "return=representation");
  return mapPayout((arr && arr[0]) || {});
}

// ── Per-shop customer block ───────────────────────────────────────────────
// Owner can block a customer from making bookings at THEIR tenant. Block is
// scoped to the customer_memberships row; other tenants are unaffected.
export async function setCustomerBlock({ userId, tenantId, blocked, reason = "" }) {
  const row = blocked
    ? { blocked_at: new Date().toISOString(), blocked_reason: reason || null }
    : { blocked_at: null, blocked_reason: null };
  if (_isNative()) {
    const arr = await rawWrite(
      "PATCH",
      `customer_memberships?user_id=eq.${encodeURIComponent(userId)}&tenant_id=eq.${encodeURIComponent(tenantId)}`,
      row,
      "return=representation",
    );
    return (arr && arr[0]) || null;
  }
  const { data, error } = await supabase
    .from("customer_memberships")
    .update(row)
    .eq("user_id", userId)
    .eq("tenant_id", tenantId)
    .select("*")
    .maybeSingle();
  if (error) throw error;
  return data;
}

// ── Support tickets (shop owner ↔ PettoBook platform admin) ──────────────
const mapTicket = (r) => r && ({
  id: r.id,
  tenantId: r.tenant_id,
  createdBy: r.created_by,
  subject: r.subject,
  status: r.status,
  priority: r.priority,
  category: r.category,
  createdAt: r.created_at,
  updatedAt: r.updated_at,
  closedAt: r.closed_at,
  lastMsgAt: r.last_msg_at,
  lastMsgBy: r.last_msg_by,
});
const mapTicketMsg = (r) => r && ({
  id: r.id,
  ticketId: r.ticket_id,
  senderId: r.sender_id,
  senderRole: r.sender_role,
  body: r.body,
  attachments: Array.isArray(r.attachments) ? r.attachments : [],
  readAt: r.read_at,
  createdAt: r.created_at,
});

export async function fetchSupportTickets({ tenantId = null, all = false } = {}) {
  let q = supabase.from("support_tickets").select("*").order("updated_at", { ascending: false }).limit(200);
  if (!all && tenantId) q = q.eq("tenant_id", tenantId);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map(mapTicket);
}

export async function createSupportTicket({ tenantId, subject, body, category = null, priority = "normal" }) {
  const userRes = { user: { id: await _currentUserId() } };
  const createdBy = userRes?.user?.id || null;
  const { data: t, error } = await supabase
    .from("support_tickets")
    .insert({ tenant_id: tenantId, subject, category, priority, created_by: createdBy })
    .select("*").maybeSingle();
  if (error) throw error;
  if (body && body.trim()) {
    await supabase.from("ticket_messages").insert({
      ticket_id: t.id,
      sender_id: createdBy,
      sender_role: "shop",
      body: body.trim(),
    });
  }
  return mapTicket(t);
}

export async function fetchTicketMessages(ticketId) {
  const { data, error } = await supabase
    .from("ticket_messages")
    .select("*")
    .eq("ticket_id", ticketId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data || []).map(mapTicketMsg);
}

export async function postTicketMessage({ ticketId, body, senderRole }) {
  const userRes = { user: { id: await _currentUserId() } };
  const { data, error } = await supabase
    .from("ticket_messages")
    .insert({ ticket_id: ticketId, sender_id: userRes?.user?.id || null, sender_role: senderRole, body })
    .select("*").maybeSingle();
  if (error) throw error;
  return mapTicketMsg(data);
}

export async function updateTicketStatus(ticketId, status) {
  const row = { status };
  if (status === "closed" || status === "resolved") row.closed_at = new Date().toISOString();
  const { data, error } = await supabase
    .from("support_tickets")
    .update(row)
    .eq("id", ticketId)
    .select("*").maybeSingle();
  if (error) throw error;
  return mapTicket(data);
}

// Tiered commission. Bands like [{fromRev:0,rate:8},{fromRev:10000,rate:12}]:
// revenue between two thresholds gets multiplied by the LOWER tier's rate.
export function computeTieredCommission(tiers, rev) {
  if (!Array.isArray(tiers) || tiers.length === 0 || rev <= 0) return 0;
  const sorted = [...tiers].sort((a, b) => (Number(a.fromRev) || 0) - (Number(b.fromRev) || 0));
  let comm = 0;
  for (let i = 0; i < sorted.length; i++) {
    const cur = sorted[i];
    const next = sorted[i + 1];
    const bandStart = Number(cur.fromRev) || 0;
    const bandEnd = next ? (Number(next.fromRev) || 0) : Infinity;
    if (rev <= bandStart) break;
    const bandRev = Math.min(rev, bandEnd) - bandStart;
    comm += bandRev * (Number(cur.rate) || 0) / 100;
    if (rev <= bandEnd) break;
  }
  return Math.round(comm);
}

// ── Web Push subscriptions ────────────────────────────────────────────────
// PettoBook sends OS-level notifications (works when browser is closed) via the
// Web Push API. Public VAPID key is exposed via platform_config so the same
// secret pair works for all tenants.

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Register the SW + ask permission + persist subscription. Idempotent.
export async function ensurePushSubscription({ userId, vapidPublicKey }) {
  if (typeof window === "undefined" || !("serviceWorker" in navigator) || !("PushManager" in window)) {
    return { skipped: "unsupported" };
  }
  if (!vapidPublicKey) return { skipped: "no_vapid_key" };
  if (!userId) return { skipped: "no_user" };

  const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  await navigator.serviceWorker.ready;

  // Don't auto-prompt — caller decides when to ask (e.g. after a user gesture)
  if (Notification.permission !== "granted") {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return { skipped: "permission_denied" };
  }

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  }

  const json = sub.toJSON();
  const row = {
    user_id: userId,
    endpoint: sub.endpoint,
    p256dh: json.keys?.p256dh || "",
    auth: json.keys?.auth || "",
    user_agent: navigator.userAgent || null,
    last_seen_at: new Date().toISOString(),
  };
  const { error } = await supabase
    .from("push_subscriptions")
    .upsert(row, { onConflict: "user_id,endpoint" });
  if (error) throw error;
  return { ok: true, endpoint: sub.endpoint };
}

// Register a NATIVE device push token (APNs/FCM) so the send-push fan-out can
// reach the Capacitor app (WKWebView can't do Web Push). Called after login on
// native with the token from @capacitor/push-notifications. Upsert by token.
export async function registerDeviceToken({ userId, token, platform = "ios", tenantId = null }) {
  if (!userId || !token) return;
  const row = { token, user_id: userId, platform, tenant_id: tenantId, updated_at: new Date().toISOString() };
  if (_isNative()) { await rawUpsert("device_tokens", row); return; }
  const { error } = await supabase.from("device_tokens").upsert(row, { onConflict: "token" });
  if (error) throw error;
}

// Persist a tiny config blob to native Preferences (UserDefaults) so the iOS
// Siri App Intent (SiriBookingIntent.swift) can act as this shop: it reads
// supabaseUrl + anonKey + tenantId here, and the session (refresh token) from
// the "petos-auth" storage key, to call the siri-book edge function. No-op on
// web. Written on every owner boot so tenantId stays current.
export async function persistSiriConfig(tenantId) {
  if (!_isNative() || !tenantId) return;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.set({
      key: "petos-siri",
      value: JSON.stringify({
        supabaseUrl: (import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, ""),
        anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || "",
        tenantId,
      }),
    });
  } catch { /* ignore */ }
}

// Send a Web Push to one or more user IDs (server-side fan-out).
// Each notification creates an OS-level alert via FCM/APNs.
export async function sendPush({ userIds, title, body, url, aptId, tag }) {
  if (!userIds || userIds.length === 0) return;
  const { data, error } = await supabase.functions.invoke("send-push", {
    body: { userIds: Array.isArray(userIds) ? userIds : [userIds], title, body, url, aptId, tag },
  });
  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data;
}

// ── POS ───────────────────────────────────────────────────────────────────
// Every money / stock change goes through a SECURITY DEFINER RPC
// (checkout_sale, refund_sale, adjust_stock) so a sale is all-or-nothing.
// Reads and plain product edits use the raw REST path on every platform:
// supabase-js hangs on Android and on aged iOS sessions.
export const mapProduct = (r) => r && ({
  id: r.id,
  tenantId: r.tenant_id,
  parentId: r.parent_id || null,
  optionValues: r.option_values || {},
  name: r.name || "",
  sku: r.sku || "",
  barcode: r.barcode || "",
  category: r.category || "",
  categories: Array.isArray(r.categories) ? r.categories : [],
  brand: r.brand || "",
  season: r.season || "",
  tags: Array.isArray(r.tags) ? r.tags : [],
  status: r.status || "active",
  sellable: r.sellable !== false,
  price: Number(r.price) || 0,
  salePrice: r.sale_price == null ? null : Number(r.sale_price),
  saleFrom: r.sale_from || null,
  saleTo: r.sale_to || null,
  cost: Number(r.cost) || 0,
  trackStock: r.track_stock !== false,
  stock: Number(r.stock) || 0,
  lowStock: Number(r.low_stock) || 0,
  reorderQty: Number(r.reorder_qty) || 0,
  allowBackorder: !!r.allow_backorder,
  images: Array.isArray(r.images) ? r.images : [],
  description: r.description || "",
  web: (r.web && typeof r.web === "object") ? r.web : {},
  categoryId: r.category_id || null,
  brandId: r.brand_id || null,
  supplierId: r.supplier_id || null,
  supplierCodes: (r.supplier_codes && typeof r.supplier_codes === "object") ? r.supplier_codes : {},
  sortOrder: Number(r.sort_order) || 0,
  createdAt: r.created_at,
  updatedAt: r.updated_at,
});

// idWins: when a category/brand id is set, leave the text out so a stale
// label from an old copy can't override it (the DB trigger fills the label).
// Bulk upserts need identical keys on every row, so they pass idWins=false.
const productRow = (p, idWins = true) => stripUndef({
  id: p.id,
  tenant_id: p.tenantId,
  parent_id: p.parentId === undefined ? undefined : (p.parentId || null),
  option_values: p.optionValues,
  name: p.name,
  sku: p.sku,
  barcode: p.barcode === undefined ? undefined : String(p.barcode || "").trim(),
  category: idWins && p.categoryId ? undefined : p.category,
  categories: p.categories,
  brand: idWins && p.brandId ? undefined : p.brand,
  season: p.season,
  tags: p.tags,
  status: p.status,
  sellable: p.sellable,
  price: p.price === undefined ? undefined : Number(p.price) || 0,
  sale_price: p.salePrice === undefined ? undefined : (p.salePrice === null || p.salePrice === "" ? null : Number(p.salePrice)),
  sale_from: p.saleFrom === undefined ? undefined : (p.saleFrom || null),
  sale_to: p.saleTo === undefined ? undefined : (p.saleTo || null),
  cost: p.cost === undefined ? undefined : Number(p.cost) || 0,
  track_stock: p.trackStock,
  low_stock: p.lowStock === undefined ? undefined : Number(p.lowStock) || 0,
  reorder_qty: p.reorderQty === undefined ? undefined : Number(p.reorderQty) || 0,
  allow_backorder: p.allowBackorder,
  images: p.images,
  description: p.description,
  web: p.web,
  // A DB trigger keeps category/brand text and ids in step (set either one).
  category_id: p.categoryId === undefined ? undefined : (p.categoryId || null),
  brand_id: p.brandId === undefined ? undefined : (p.brandId || null),
  supplier_id: p.supplierId === undefined ? undefined : (p.supplierId || null),
  supplier_codes: p.supplierCodes,
  sort_order: p.sortOrder,
  updated_at: new Date().toISOString(),
});

// RPC errors come back as { message } from PostgREST or supabase-js; surface
// the Postgres exception text (e.g. "insufficient_wallet") as the message.
const rpcOrThrow = async (fn, args) => {
  const { data, error } = await callRpc(fn, args);
  if (error) {
    let msg = error.message || String(error);
    const m = /"message"\s*:\s*"([^"]+)"/.exec(msg);
    if (m) msg = m[1];
    throw Object.assign(new Error(msg), { cause: error });
  }
  return data;
};

// The API returns at most 1000 rows per request (Supabase max-rows) and cuts
// the rest silently, so big catalogues are read page by page. id is the
// tie-breaker so pages never overlap or skip.
export async function fetchPosProducts(tenantId) {
  const PAGE = 1000, all = [];
  for (let offset = 0; ; offset += PAGE) {
    const rows = await rawRead(`products?tenant_id=eq.${encodeURIComponent(tenantId)}&select=*&order=sort_order.asc,name.asc,id.asc&limit=${PAGE}&offset=${offset}`);
    all.push(...(rows || []));
    if (!rows || rows.length < PAGE) break;
  }
  return all.map(mapProduct);
}

// Products changed since `iso` (stock moves bump updated_at), for keeping a
// till in step with other devices without re-reading the whole catalogue.
export async function fetchPosProductsSince(tenantId, iso) {
  const rows = await rawRead(`products?tenant_id=eq.${encodeURIComponent(tenantId)}&updated_at=gt.${encodeURIComponent(iso)}&select=*&order=updated_at.asc&limit=1000`);
  return (rows || []).map(mapProduct);
}

// Stock is never written here — only adjust_stock / checkout_sale move it, so
// every change lands in stock_movements. A NEW product may carry an opening
// stock: it is inserted at 0 and then received through adjust_stock.
export async function savePosProduct(p) {
  const row = productRow(p);
  const saved = await rawUpsert("products", row);
  return mapProduct(saved);
}

// Import: full rows (every row carries the same keys — PostgREST bulk upsert
// needs that), in chunks. Matching to existing products is done by the caller.
export async function bulkUpsertPosProducts(products) {
  const out = [];
  for (let i = 0; i < products.length; i += 200) {
    const rows = products.slice(i, i + 200).map(p => productRow(p, false));
    const saved = await rawWrite("POST", "products", rows, "resolution=merge-duplicates,return=representation");
    out.push(...(saved || []).map(mapProduct));
  }
  return out;
}

export async function bulkPatchPosProducts(ids, patch) {
  if (!ids?.length) return [];
  const row = productRow(patch);
  delete row.id; delete row.tenant_id;
  const rows = await rawWrite("PATCH", `products?id=in.(${ids.map(encodeURIComponent).join(",")})`, row, "return=representation");
  return (rows || []).map(mapProduct);
}

export async function deletePosProduct(id) {
  await rawWrite("DELETE", `products?id=eq.${encodeURIComponent(id)}`, null, "return=minimal");
}

// p: { tenantId, kind: receive|adjust|count|writeoff|opening, ref?, note?, items:[{productId, qty?, count?, cost?}] }
export const adjustStock = (p) => rpcOrThrow("adjust_stock", { p });

export async function fetchStockMovements(productId, limit = 50) {
  return await rawRead(`stock_movements?product_id=eq.${encodeURIComponent(productId)}&select=*&order=created_at.desc&limit=${limit}`) || [];
}

export const checkoutSale = (sale) => rpcOrThrow("checkout_sale", { p_sale: sale });
export const refundSale = (p) => rpcOrThrow("refund_sale", { p });

// Sales with their lines + payments, newest first. `from` / `to` are ISO
// timestamps (inclusive / exclusive).
export async function fetchSales(tenantId, { from, to, customerId, limit = 300 } = {}) {
  const parts = [`tenant_id=eq.${encodeURIComponent(tenantId)}`, "select=*,sale_lines(*),sale_payments(*)", "order=created_at.desc", `limit=${limit}`];
  if (from) parts.push(`created_at=gte.${encodeURIComponent(from)}`);
  if (to) parts.push(`created_at=lt.${encodeURIComponent(to)}`);
  if (customerId) parts.push(`customer_id=eq.${encodeURIComponent(customerId)}`);
  return await rawRead("sales?" + parts.join("&")) || [];
}

export async function fetchHeldTickets(tenantId) {
  return await rawRead(`pos_held_tickets?tenant_id=eq.${encodeURIComponent(tenantId)}&select=*&order=created_at.desc`) || [];
}
export async function saveHeldTicket(t) {
  return await rawUpsert("pos_held_tickets", {
    id: t.id, tenant_id: t.tenantId, customer_id: t.customerId || null, label: t.label || "",
    ticket: t.ticket, product_qty: t.productQty || {}, updated_at: new Date().toISOString(),
  });
}
export async function deleteHeldTicket(id) {
  await rawWrite("DELETE", `pos_held_tickets?id=eq.${encodeURIComponent(id)}`, null, "return=minimal");
}

// Fresh balances after a sale (fetchAppointmentsByIds above refreshes the
// appointments): the RPC changed the customer's wallet + points server-side.
export async function fetchMembership(userId, tenantId) {
  const rows = await rawRead(`customer_memberships?user_id=eq.${encodeURIComponent(userId)}&tenant_id=eq.${encodeURIComponent(tenantId)}&select=user_id,tenant_id,wallet,points,stamps&limit=1`);
  return (rows && rows[0]) || null;
}

// POS shifts (交更 / cash drawer). One open shift per shop.
export async function fetchOpenShift(tenantId) {
  const rows = await rawRead(`pos_shifts?tenant_id=eq.${encodeURIComponent(tenantId)}&status=eq.open&select=*&limit=1`);
  return (rows && rows[0]) || null;
}
export async function fetchShifts(tenantId, limit = 30) {
  return await rawRead(`pos_shifts?tenant_id=eq.${encodeURIComponent(tenantId)}&select=*&order=opened_at.desc&limit=${limit}`) || [];
}
export const openShift = (p) => rpcOrThrow("pos_open_shift", { p });
export const shiftCash = (p) => rpcOrThrow("pos_shift_cash", { p });
export const closeShift = (p) => rpcOrThrow("pos_close_shift", { p });
export const shiftFigures = (shiftId) => rpcOrThrow("pos_shift_figures", { p_shift: shiftId });
// Platform switch (super admin only — a DB trigger ignores anyone else).
export async function setTenantPosEnabled(tenantId, on) {
  return await rawUpdate("tenants", "id", tenantId, { pos_enabled: !!on });
}

// ── POS: categories / brands / suppliers / purchase orders / promotions ──
const tq = (tenantId) => `tenant_id=eq.${encodeURIComponent(tenantId)}`;
export const fetchCategories = async (tenantId) => await rawRead(`product_categories?${tq(tenantId)}&select=*&order=sort_order.asc,name.asc`) || [];
export const saveCategory = (row) => rawUpsert("product_categories", stripUndef(row));
export const patchCategory = (id, patch) => rawWrite("PATCH", `product_categories?id=eq.${encodeURIComponent(id)}`, patch, "return=minimal");
export const patchBrand = (id, patch) => rawWrite("PATCH", `product_brands?id=eq.${encodeURIComponent(id)}`, patch, "return=minimal");
export const deleteCategory = (id) => rawWrite("DELETE", `product_categories?id=eq.${encodeURIComponent(id)}`, null, "return=minimal");
export const fetchBrands = async (tenantId) => await rawRead(`product_brands?${tq(tenantId)}&select=*&order=sort_order.asc,name.asc`) || [];
export const saveBrand = (row) => rawUpsert("product_brands", stripUndef(row));
export const deleteBrand = (id) => rawWrite("DELETE", `product_brands?id=eq.${encodeURIComponent(id)}`, null, "return=minimal");
// p: {tenantId, kind: category|brand, fromId, intoId} → products moved
export const mergeTaxonomy = (p) => rpcOrThrow("pos_merge_taxonomy", { p });

export const fetchSuppliers = async (tenantId) => await rawRead(`suppliers?${tq(tenantId)}&select=*&order=name.asc`) || [];
export const saveSupplier = (row) => rawUpsert("suppliers", stripUndef(row));
export const deleteSupplier = (id) => rawWrite("DELETE", `suppliers?id=eq.${encodeURIComponent(id)}`, null, "return=minimal");

// Purchase orders come with their lines (newest first).
export const fetchPurchaseOrders = async (tenantId, limit = 200) =>
  await rawRead(`purchase_orders?${tq(tenantId)}&select=*,purchase_order_lines(*)&order=created_at.desc&limit=${limit}`) || [];
export const savePurchaseOrder = (p) => rpcOrThrow("pos_save_po", { p });
export const setPurchaseOrderStatus = (p) => rpcOrThrow("pos_set_po_status", { p });
export const receivePurchaseOrder = (p) => rpcOrThrow("pos_receive_po", { p });
export const returnPurchaseOrderLine = (p) => rpcOrThrow("pos_return_po", { p });
// {productId: qty sold (net of refunds)} over the last `days` days, summed in SQL.
export async function fetchSalesVelocity(tenantId, days = 30) {
  const rows = await rpcOrThrow("pos_sales_velocity", { p: { tenantId, days } });
  return Object.fromEntries((rows || []).map(r => [r.product_id, Number(r.qty) || 0]));
}

export const fetchPromotions = async (tenantId) => await rawRead(`promotions?${tq(tenantId)}&select=*&order=sort_order.asc,created_at.desc`) || [];
// Merge one supplier item code into products.supplier_codes (server-side jsonb merge).
export const setSupplierCode = (p) => rpcOrThrow("pos_set_supplier_code", { p });
export const savePromotion = (row) => rawUpsert("promotions", stripUndef(row));
export const deletePromotion = (id) => rawWrite("DELETE", `promotions?id=eq.${encodeURIComponent(id)}`, null, "return=minimal");

// Product photo: shrink on the device (phone photos are often > 5 MB / HEIC),
// then the normal tenant-media upload. Returns the public URL.
export async function uploadProductImage(tenantId, file) {
  let blob = file;
  try { blob = await compressImage(file, { maxDim: 1000, quality: 0.8 }); } catch { /* upload the original */ }
  const out = blob instanceof File ? blob : new File([blob], "product.jpg", { type: blob.type || "image/jpeg" });
  // Random part: several photos uploading at once must never share a name
  // (the storage upload upserts, so a clash would silently overwrite).
  const rand = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID().slice(0, 8) : Math.random().toString(36).slice(2, 10);
  return await uploadTenantMedia(tenantId, out, "product-" + rand);
}

// Category icon / brand logo. Small (512px) and PNG stays PNG so a
// transparent logo doesn't end up on a black box.
export async function uploadTaxonomyImage(tenantId, file, kind) {
  let blob = file;
  const type = file?.type === "image/png" || file?.type === "image/webp" ? "image/png" : "image/jpeg";
  try { blob = await compressImage(file, { maxDim: 512, quality: 0.88, type }); } catch { /* upload the original */ }
  const out = blob instanceof File ? blob : new File([blob], kind + (type === "image/png" ? ".png" : ".jpg"), { type: blob.type || type });
  const rand = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID().slice(0, 8) : Math.random().toString(36).slice(2, 10);
  return await uploadTenantMedia(tenantId, out, kind + "-" + rand);
}

// POS AI (edge fn pos-ai). AI calls take 10–25 s, longer than the normal raw
// timeout, so this has its own 75 s budget. task: product_from_image |
// product_from_url | po_from_document.
export async function posAi(body, _retried) {
  await _ensureBearer();
  const base = (import.meta.env.VITE_SUPABASE_URL || "").replace(/\/$/, "");
  const resp = await Promise.race([
    appFetch(`${base}/functions/v1/pos-ai`, {
      method: "POST",
      headers: { apikey: _apikey(), Authorization: `Bearer ${_bearer()}`, "Content-Type": "application/json" },
      body: JSON.stringify(body || {}),
      timeoutMs: 75000,
    }),
    new Promise((_, rej) => setTimeout(() => rej(new Error("AI 處理時間太長，請再試一次")), 76000)),
  ]);
  const text = await resp.text().catch(() => "");
  let j = null; try { j = JSON.parse(text); } catch { /* not json */ }
  if (resp.status === 401 && !_retried) {
    // Token aged out while the app sat idle — refresh once and retry.
    try { const mod = await import("./auth.js"); const r = await mod.ensureFreshSession(); if (r && r.ok) return await posAi(body, true); } catch { /* fall through */ }
  }
  if (!resp.ok) throw new Error((j && j.error) || "AI 服務出錯（" + resp.status + "）");
  return j;
}

// File → {base64, mimeType} for pos-ai. Photos are shrunk first (faster upload,
// enough detail to read a label or an invoice); PDFs go as they are.
export async function fileForAi(file, maxDim = 1800) {
  let blob = file;
  if (file.type && file.type.startsWith("image/")) {
    try { blob = await compressImage(file, { maxDim, quality: 0.85 }); } catch { /* original */ }
  }
  const buf = new Uint8Array(await blob.arrayBuffer());
  let bin = "";
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode.apply(null, buf.subarray(i, i + 0x8000));
  return { base64: btoa(bin), mimeType: blob.type || file.type || "image/jpeg" };
}

// Sales report summed in SQL (pos_sales_report). bucket: 'hour' | 'day'.
export const fetchSalesReport = (p) => rpcOrThrow("pos_sales_report", { p });

// Free shops take a limited number of customer bookings a month. Callable by
// visitors too. -> { unlimited, limit, used, open }
export const fetchOnlineBookingQuota = (tenantId) => rpcOrThrow("online_booking_quota", { p_tenant: tenantId });
// { max, used, active:[ids], locked:[ids] } — staff over the plan seat limit are "locked".
export const fetchStaffSeats = (tenantId) => rpcOrThrow("staff_seats", { p_tenant: tenantId });
// { tier: "off"|"lite"|"standard"|"full", maxProducts: n|null, products: n } — what the plan includes.
export const fetchPosPlanInfo = (tenantId) => rpcOrThrow("pos_plan_info", { p_tenant: tenantId });
